import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Building2,
  MessageSquare,
  CreditCard,
  LogOut,
  Menu,
  X,
  Plus,
  Trash2,
  Eye,
  Home,
  BarChart3,
  Mail,
  Send,
  FileText,
  Tag,
  Link,
  Search,
  ClipboardList,
  Download,
  Activity,
  Image as ImageIcon,
  Ban,
  CheckCircle,
  Edit3,
  Globe,
  TrendingUp,
  Phone,
  Calendar,
  Star,
  Compass,
  Handshake,
  AlertTriangle,
  RefreshCw,
  Sparkles,
  MessageCircle,
  AlertCircle,
} from 'lucide-react';
import CommandCenter from './CommandCenter';
import AdminCommunitySection from './AdminCommunitySection';
import AdminNewsEditor from './AdminNewsEditor';
import AdminSpotlightPanel from './AdminSpotlightPanel';
import AdminDiscountsPanel from './AdminDiscountsPanel';
import { BRAND_LOGO } from '../../constants/brand';
import AdminUpgradeInsightsPanel from './AdminUpgradeInsightsPanel';
import AdminCityGuidesPanel from './AdminCityGuidesPanel';
import AdminConversionPanel from './AdminConversionPanel';
import FunnelSnapshotCard from './FunnelSnapshotCard';
import AdminAlertsPanel from './AdminAlertsPanel';
import CanonicalCountTile from './CanonicalCountTile';
import DeployBundleTile from './DeployBundleTile';
import { notifyError, notifySuccess } from '../../lib/notify';
import { formatCentralTime } from '../../utils/formatCentral';

// iter 142d - admin emails that must NEVER be deletable through the admin
// Clients UI. Compared case-insensitively against client.email. Used to hide
// the delete button on both the row list and the client detail view.
const PROTECTED_ADMIN_EMAILS = new Set([
  'francisco@mundalo.com',
  'rodrigo@mundalo.com',
]);
const isProtectedAdmin = (client) =>
  PROTECTED_ADMIN_EMAILS.has((client?.email || '').toLowerCase());

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [advisoryIntakes, setAdvisoryIntakes] = useState([]);
  const [intakeStats, setIntakeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  // iter 175 - pagination state per tab. Server-side, 25/page.
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsMeta, setClientsMeta] = useState({ total: 0, total_pages: 0, per_page: 25 });
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsMeta, setLeadsMeta] = useState({ total: 0, total_pages: 0, per_page: 25 });

  useEffect(() => {
    verifyAdmin();
  }, []);

  // iter 179 (Fix 3) - cross-component bridge to let the Overview "New
  // Leads" card jump directly into the unified Leads tab and open the
  // drawer for a specific email. Uses a custom event so we do not need
  // to prop-drill through the deep component tree.
  useEffect(() => {
    const onOpen = (e) => {
      const em = e.detail?.email;
      if (!em) return;
      setActiveTab('leads');
      try { sessionStorage.setItem('mundalo_pending_lead_drawer', em); } catch { /* private mode */ }
    };
    window.addEventListener('mundalo:open-lead-drawer', onOpen);
    return () => window.removeEventListener('mundalo:open-lead-drawer', onOpen);
  }, []);

  useEffect(() => {
    if (activeTab === 'overview') fetchStats();
    else if (activeTab === 'clients') fetchClients(clientsPage);
    else if (activeTab === 'listings') fetchListings();
    else if (activeTab === 'leads') fetchLeads(leadsPage);
    else if (activeTab === 'campaigns') fetchCampaigns();
    else if (activeTab === 'templates') fetchTemplates();
    else if (activeTab === 'advisory') fetchAdvisoryIntakes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, clientsPage, leadsPage]);

  const verifyAdmin = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`, {
        credentials: 'include'
      });
      if (!response.ok) navigate('/admin');
    } catch {
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, { credentials: 'include' });
      if (response.ok) setStats(await response.json());
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchClients = async (page = 1) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients?page=${page}&per_page=25`,
        { credentials: 'include' },
      );
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients);
        setClientsMeta({
          total: data.total ?? data.clients.length,
          total_pages: data.total_pages ?? 1,
          per_page: data.per_page ?? 25,
        });
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchListings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings);
      }
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    }
  };

  const fetchLeads = async (page = 1) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads?page=${page}&per_page=25`,
        { credentials: 'include' },
      );
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads);
        setLeadsMeta({
          total: data.total ?? data.leads.length,
          total_pages: data.total_pages ?? 1,
          per_page: data.per_page ?? 25,
        });
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/campaigns`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns);
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/templates`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchAdvisoryIntakes = async (excludeTest = false) => {
    try {
      const params = excludeTest ? '?exclude_test=true' : '';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/advisory/intakes${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAdvisoryIntakes(data.intakes || []);
        setIntakeStats({ total: data.total, byTier: data.by_tier });
      }
    } catch (error) {
      console.error('Failed to fetch advisory intakes:', error);
    }
  };

  const exportAdvisoryIntakes = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/advisory/export`, '_blank');
  };

  const handleLogout = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    navigate('/admin');
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings/${listingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) setListings(listings.filter(l => l.listing_id !== listingId));
    } catch (error) {
      console.error('Failed to delete listing:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2CA8A0]"></div>
      </div>
    );
  }

  const sidebarItems = [
    { id: 'overview', icon: BarChart3, label: 'Overview' },
    { id: 'command_center', icon: Activity, label: 'Command Center' },
    { id: 'clients', icon: Users, label: 'Clients' },
    { id: 'listings', icon: Building2, label: 'Listings' },
    { id: 'city_photos', icon: ImageIcon, label: 'City Photos', href: '/admin/cities' },
    { id: 'city_guides', icon: Globe, label: 'City Guides' },
    { id: 'conversion', icon: TrendingUp, label: 'Conversion' },
    { id: 'leads', icon: MessageSquare, label: 'Leads' },
    // iter 179 (Fix 3) - Tool Leads tab folded into unified Leads with source chips.
    { id: 'advisory', icon: ClipboardList, label: 'Advisory Intakes' },
    { id: 'coaching', icon: Phone, label: 'Coaching Calls', href: '/admin/coaching' },
    { id: 'events_mod', icon: Calendar, label: 'Events Moderation', href: '/admin/events' },
    { id: 'reviews_mod', icon: Star, label: 'Reviews Moderation', href: '/admin/reviews' },
    { id: 'housing_mod', icon: Home, label: 'Housing Listings', href: '/admin/housing-listings' },
    { id: 'deal_subs', icon: Tag, label: 'Deal Submissions', href: '/admin/local-deal-submissions' },
    { id: 'local_deals', icon: Tag, label: 'Local Deals', href: '/admin/local-deals' },
    { id: 'adventures', icon: Compass, label: 'Adventures', href: '/admin/adventures' },
    { id: 'divider_partners', type: 'divider', label: 'Partners' },
    { id: 'partners', icon: Handshake, label: 'Partners', href: '/admin/partners' },
    { id: 'partner_apps', icon: ClipboardList, label: 'Partner Applications', href: '/admin/partner-applications' },
    { id: 'partner_convs', icon: MessageCircle, label: 'Partner Conversations', href: '/admin/partner-conversations' },
    { id: 'divider1', type: 'divider', label: 'Marketing Studio' },
    { id: 'campaigns', icon: Send, label: 'Campaigns' },
    { id: 'templates', icon: FileText, label: 'Templates' },
    { id: 'divider2', type: 'divider', label: 'Community' },
    { id: 'community', icon: MessageSquare, label: 'Community' },
    { id: 'ambassadors', icon: Users, label: 'Ambassadors', href: '/admin/ambassadors' },
    { id: 'discounts', icon: BarChart3, label: 'Discounts' },
    { id: 'divider_analytics', type: 'divider', label: 'Analytics' },
    { id: 'affiliate_clicks', icon: BarChart3, label: 'Affiliate Clicks', href: '/admin/analytics/affiliate-clicks' },
    { id: 'upgrade_insights', icon: BarChart3, label: 'Upgrade Insights' },
    { id: 'divider_diagnostics', type: 'divider', label: 'Diagnostics' },
    { id: 'client_errors', icon: AlertTriangle, label: 'Client Errors', href: '/admin/client-errors' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F6EF] flex">
      {/* Sidebar
          iter 90: on MOBILE this also serves as the hamburger dropdown menu.
          The cream / Deep Ocean / teal-active palette below is overridden via
          `lg:` utilities so the existing desktop sidebar styling is preserved
          unchanged at >= 1024px. */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 lg:w-64 bg-[#F9F6EF] lg:bg-[#FFFFFF] border-r border-[#EFE6CF] lg:border-[#F4ECD8] transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static`} data-testid="admin-sidebar">
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#EFE6CF] lg:border-[#F4ECD8] flex items-center justify-between">
            {/* iter 144o - Brand green Mundalo wordmark in the Admin sidebar. */}
            <div className="flex items-center gap-3 min-w-0">
              <img src={BRAND_LOGO.green} alt="Mundalo" className="block h-8 w-[160px] object-contain object-left flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-[#0A1F2E] text-sm leading-tight">Admin Panel</p>
                <p className="text-xs text-[#5A6B73] leading-tight">Mundalo</p>
              </div>
            </div>
            {/* Mobile-only close button */}
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 -mr-2 text-[#0A1F2E]/70 hover:text-[#0A1F2E]"
              aria-label="Close menu"
              data-testid="admin-sidebar-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {sidebarItems.map((item) => (
              item.type === 'divider' ? (
                <div key={item.id} className="pt-4 pb-2">
                  <p className="px-4 text-xs font-semibold text-[#1A5C6E] lg:text-[#5A6B73] uppercase tracking-wider">{item.label}</p>
                </div>
              ) : item.href ? (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm rounded transition text-[#0A1F2E] lg:text-[#1A5C6E] hover:bg-[#F4ECD8] hover:text-[#0A1F2E]"
                  data-testid={`admin-nav-${item.id}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setSelectedClient(null); setSidebarOpen(false); }}
                  data-testid={`admin-nav-${item.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded transition text-left ${
                    activeTab === item.id
                      ? 'bg-[#2CA8A0] text-[#F9F6EF] font-semibold'
                      : 'text-[#0A1F2E] lg:text-[#1A5C6E] hover:bg-[#F4ECD8] hover:text-[#0A1F2E]'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              )
            ))}
          </nav>

          <div className="p-4 border-t border-[#EFE6CF] lg:border-[#F4ECD8] space-y-2">
            <a href="/" className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#0A1F2E] lg:text-[#1A5C6E] hover:bg-[#F4ECD8] rounded transition">
              <Home className="w-4 h-4" />
              View Website
            </a>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 lg:text-red-400 hover:bg-red-50 lg:hover:bg-red-900/20 rounded transition">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-[#0A1F2E]/45 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="admin-sidebar-overlay" />}

      <main className="flex-1 min-h-screen overflow-x-hidden">
        <div className="sticky top-0 z-30 bg-[#F9F6EF] border-b border-[#F4ECD8] px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-[#0A1F2E]">
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-[#0A1F2E]">
              {selectedClient ? `Client: ${selectedClient.name}` : sidebarItems.find(item => item.id === activeTab)?.label || 'Admin'}
            </h1>
            <AdminHealthBadge />
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {selectedClient ? (
            <ClientDetailView 
              client={selectedClient} 
              onBack={() => setSelectedClient(null)}
              onListingAdded={fetchListings}
              onDeleted={() => { setSelectedClient(null); fetchClients(); }}
            />
          ) : (
            <>
              {activeTab === 'overview' && <OverviewTab stats={stats} />}
              {activeTab === 'command_center' && <CommandCenter />}
              {activeTab === 'clients' && <ClientsTab clients={clients} onSelectClient={setSelectedClient} onRefresh={() => fetchClients(clientsPage)} onOptimisticRemove={(id) => setClients((prev) => prev.filter((c) => c.id !== id))} page={clientsPage} setPage={setClientsPage} meta={clientsMeta} />}
              {activeTab === 'listings' && <ListingsTab listings={listings} onDelete={deleteListing} clients={clients} onRefresh={fetchListings} />}
              {activeTab === 'leads' && <LeadsTab leads={leads} page={leadsPage} setPage={setLeadsPage} meta={leadsMeta} />}
              {activeTab === 'tool_leads' && <ToolLeadsTab />}
              {activeTab === 'advisory' && <AdvisoryIntakesTab intakes={advisoryIntakes} stats={intakeStats} onExport={exportAdvisoryIntakes} onRefetch={fetchAdvisoryIntakes} />}
              {activeTab === 'campaigns' && <CampaignsTab campaigns={campaigns} clients={clients} onRefresh={fetchCampaigns} templates={templates} />}
              {activeTab === 'templates' && <TemplatesTab templates={templates} onRefresh={fetchTemplates} />}
              {activeTab === 'community' && <AdminCommunitySection />}
              {activeTab === 'discounts' && <AdminDiscountsPanel />}
              {activeTab === 'upgrade_insights' && <AdminUpgradeInsightsPanel />}
              {activeTab === 'city_guides' && <AdminCityGuidesPanel />}
              {activeTab === 'conversion' && <AdminConversionPanel />}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

// ============== ADMIN HEALTH BADGE ==============
// Pings /api/admin/diagnostic on mount + every 60s. Green dot if every
// configured admin has env loaded + hash matches stored bcrypt. Red dot
// otherwise. Tooltip surfaces the failing admin row for instant debugging
// of env-misconfig issues.
const AdminHealthBadge = () => {
  const [diag, setDiag] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reseeding, setReseeding] = React.useState(false);
  const [reseedToast, setReseedToast] = React.useState(null);

  const fetchDiag = React.useCallback(async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/diagnostic`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDiag(d);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (cancelled) return;
      await fetchDiag();
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [fetchDiag]);

  const handleReseed = async () => {
    if (reseeding) return;
    if (!window.confirm('Re-seed admins now? This rewrites the stored bcrypt hash for every configured admin to match the current ADMIN_PASSWORD env values.')) {
      return;
    }
    setReseeding(true);
    setReseedToast(null);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reseed`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setReseedToast({
        kind: 'success',
        msg: `Reseeded: ${data.result.seeded} new, ${data.result.updated} refreshed, ${data.result.skipped} skipped`,
      });
      await fetchDiag();
    } catch (e) {
      setReseedToast({ kind: 'error', msg: e.message });
    } finally {
      setReseeding(false);
      setTimeout(() => setReseedToast(null), 6000);
    }
  };

  const healthy = diag?.healthy === true;
  const dotColor = error ? 'bg-amber-500' : healthy ? 'bg-emerald-500' : diag ? 'bg-red-500' : 'bg-gray-300';
  const label = error ? 'Unknown' : healthy ? 'Healthy' : diag ? 'Degraded' : 'Loading';

  const tooltip = React.useMemo(() => {
    if (error) return `Diagnostic fetch failed: ${error}`;
    if (!diag) return 'Loading admin health…';
    const lines = diag.admins.map((a) => {
      const env = a.env_password_loaded ? 'env✓' : 'env✗';
      const row = a.exists_in_db ? 'row✓' : 'row✗';
      const match = a.hash_matches_env ? 'match✓' : 'match✗';
      return `${a.email}: ${env} ${row} ${match}`;
    });
    lines.push(
      `sessions: ${diag.active_admin_sessions}`,
      `last seed: ${diag.last_seed_at?.slice(0, 19) ?? '-'}`,
    );
    return lines.join('\n');
  }, [diag, error]);

  return (
    <div className="flex items-center gap-2 relative">
      <div
        data-testid="admin-health-badge"
        title={tooltip}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F4ECD8] border border-[#0A1F2E]/8 cursor-help"
      >
        <span
          className={`w-2 h-2 rounded-full ${dotColor} ${healthy ? '' : 'animate-pulse'}`}
          aria-label={`admin auth ${label}`}
        />
        <span className="font-mono uppercase text-[10px] tracking-[0.12em] text-[#0A1F2E]/75 hidden sm:inline">
          Admin Auth · {label}
        </span>
      </div>
      <button
        type="button"
        onClick={handleReseed}
        disabled={reseeding}
        data-testid="admin-reseed-btn"
        title="Re-run seed_admins() - rewrites stored bcrypt hashes to match current ADMIN_PASSWORD env values"
        className="px-3 py-1.5 rounded-full bg-[#0A1F2E] text-white text-[10px] font-mono uppercase tracking-[0.12em] hover:bg-[#1A5C6E] disabled:opacity-50 disabled:cursor-wait transition"
      >
        {reseeding ? 'Reseeding…' : 'Re-seed'}
      </button>
      {reseedToast && (
        <div
          data-testid="admin-reseed-toast"
          className={`absolute top-full right-0 mt-2 px-3 py-2 rounded shadow-lg font-mono text-[11px] whitespace-nowrap z-50 ${
            reseedToast.kind === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {reseedToast.msg}
        </div>
      )}
    </div>
  );
};


// ============== RECENT UPLOADS (Cloudinary) ==============
const RecentUploads = () => {
  const [uploads, setUploads] = useState(undefined); // undefined = loading
  const [error, setError] = useState(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/recent-uploads?limit=12`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      })
      .then((d) => setUploads(d.uploads || []))
      .catch((e) => { setError(String(e.message || e)); setUploads([]); });
  }, []);

  return (
    <div
      className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
      data-testid="admin-recent-uploads"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-[#0A1F2E]">Recent Uploads</h3>
        <span className="text-xs text-[#5A6B73] font-mono uppercase tracking-wider">
          Cloudinary · live
        </span>
      </div>

      {uploads === undefined ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-square bg-[#F4ECD8] animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-red-500" data-testid="admin-recent-uploads-error">
          Failed to load uploads: {error}
        </p>
      ) : uploads.length === 0 ? (
        <p className="text-sm text-[#5A6B73]">No uploads yet.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {uploads.map((u) => (
            <a
              key={u.public_id}
              href={u.secure_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block relative aspect-square overflow-hidden bg-[#F4ECD8] group hover:opacity-90 transition"
              title={`${u.folder || ''}${u.folder ? ' · ' : ''}${u.public_id.split('/').pop()}`}
              data-testid={`admin-upload-${u.public_id}`}
            >
              <img
                src={u.secure_url.replace('/upload/', '/upload/c_fill,w_240,h_240,q_auto/')}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              {u.folder && (
                <span className="absolute bottom-0 left-0 right-0 bg-[#0A1F2E]/75 text-[#F9F6EF] text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 truncate">
                  {u.folder.split('/').pop()}
                </span>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== iter 126 - CONVERSION PATH COMPARISON ==============
// iter 144h - UTM Traffic Source breakdown table embedded in this card.
const TrafficSourceBreakdown = () => {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/utm-breakdown?days=30`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const err = await r.json().catch(() => ({}));
        const detail = err?.detail;
        throw new Error(typeof detail === 'string' ? detail : `HTTP ${r.status}`);
      })
      .then(setData)
      .catch((e) => { setError(String(e.message || e)); setData(null); });
  }, []);

  const fmtMoney = (n) => {
    const v = Number(n || 0);
    return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
  };

  return (
    <div
      className="mt-6 pt-5 border-t border-[#EFE6CF]"
      data-testid="admin-utm-breakdown"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#0A1F2E] text-sm">Traffic Source Breakdown</h4>
        <span className="text-[10px] text-[#5A6B73] font-mono uppercase tracking-wider">
          Last 30 days · UTM source
        </span>
      </div>

      {data === undefined ? (
        <div className="h-24 bg-[#F4ECD8] animate-pulse" />
      ) : error ? (
        <p className="text-sm text-red-500" data-testid="admin-utm-breakdown-error">
          Failed to load: {error}
        </p>
      ) : !data || !data.rows || data.rows.length === 0 ? (
        <p
          className="text-sm text-[#5A6B73] italic py-3"
          data-testid="admin-utm-breakdown-empty"
        >
          No UTM-attributed signups in the last {data?.window_days ?? 30} days.
        </p>
      ) : (
        <div className="overflow-x-auto" data-testid="admin-utm-breakdown-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#EFE6CF]">
                <th className="py-2 pr-3 font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73]">
                  Source
                </th>
                <th className="py-2 px-3 font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73] text-right">
                  Signups
                </th>
                <th className="py-2 px-3 font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73] text-right">
                  Conversions
                </th>
                <th className="py-2 px-3 font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73] text-right">
                  CVR
                </th>
                <th className="py-2 pl-3 font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73] text-right">
                  Revenue
                </th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const cvr = r.signups > 0 ? (r.conversions / r.signups) * 100 : 0;
                return (
                  <tr
                    key={r.utm_source}
                    className="border-b border-[#F4ECD8] last:border-b-0"
                    data-testid={`utm-row-${r.utm_source}`}
                  >
                    <td className="py-2 pr-3 text-[#0A1F2E] font-mono text-xs">
                      {r.utm_source}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-[#0A1F2E]">
                      {r.signups}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-[#0A1F2E]">
                      {r.conversions}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-[#2CA8A0]">
                      {cvr.toFixed(1)}%
                    </td>
                    <td className="py-2 pl-3 text-right font-mono text-[#0A1F2E] font-semibold">
                      {fmtMoney(r.revenue)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ConversionPathCard = () => {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/conversion-paths?days=30`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const err = await r.json().catch(() => ({}));
        const detail = err?.detail;
        throw new Error(typeof detail === 'string' ? detail : `HTTP ${r.status}`);
      })
      .then(setData)
      .catch((e) => { setError(String(e.message || e)); setData(null); });
  }, []);

  const PathColumn = ({ path, accent, testId }) => (
    <div
      className="bg-[#F9F6EF] border border-[#EFE6CF] p-5"
      data-testid={testId}
    >
      <p className="font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73]">
        {path.cta_label} · {path.cta_destination}
      </p>
      <p className="mt-1 font-display italic text-xl text-[#0A1F2E]">
        {path.label}
      </p>

      <div className="mt-5 space-y-3">
        {path.stages.map((s, i) => (
          <div key={s.id} className="flex items-baseline justify-between gap-4" data-testid={`${testId}-stage-${s.id}`}>
            <span className="font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73]">
              {String(i + 1).padStart(2, '0')} · {s.label}
            </span>
            <span className="font-display text-3xl leading-none" style={{ color: accent }}>
              {s.value ?? 0}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-[#EFE6CF] flex items-baseline justify-between">
        <span className="font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73]">
          Conversion rate
        </span>
        <span
          className="font-display text-2xl"
          style={{ color: accent }}
          data-testid={`${testId}-conversion`}
        >
          {path.conversion_rate?.toFixed?.(1) ?? path.conversion_rate}%
        </span>
      </div>
    </div>
  );

  return (
    <div
      className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
      data-testid="admin-conversion-paths"
    >
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold text-[#0A1F2E]">Conversion Path Comparison</h3>
        <span className="text-xs text-[#5A6B73] font-mono uppercase tracking-wider">
          Last 30 days · A/B
        </span>
      </div>
      <p className="text-xs text-[#5A6B73] mb-5">
        Side-by-side funnel for the homepage nav-CTA split test (iter 125).
      </p>

      {data === undefined ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-48 bg-[#F4ECD8] animate-pulse" />
          <div className="h-48 bg-[#F4ECD8] animate-pulse" />
        </div>
      ) : error || !data ? (
        <p className="text-sm text-red-500" data-testid="admin-conversion-paths-error">
          Failed to load: {error || 'unknown error'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PathColumn path={data.path_a} accent="#2CA8A0" testId="cpc-path-a" />
            <PathColumn path={data.path_b} accent="#1A5C6E" testId="cpc-path-b" />
          </div>
          <p className="mt-4 font-mono uppercase text-[9px] tracking-[0.12em] text-[#5A6B73]">
            updated {formatCentralTime(data.as_of, { second: '2-digit' })} CT · window {data.window_days}d · refresh page for latest
          </p>
        </>
      )}

      <TrafficSourceBreakdown />
    </div>
  );
};


// ============== iter 123 - WELCOME SEQUENCE HEALTH ==============

/**
 * Inline-SVG sparkline. Renders a polyline of the given values inside a
 * 100×24 viewBox so it scales fluidly to the parent. A single-tone fill
 * under the line gives the chart visual weight without adding a chart
 * library. Pure CSS-friendly - works in light card backgrounds without
 * any axis chrome.
 */
const Sparkline = ({ values = [], color = '#1A5C6E', testId }) => {
  if (!Array.isArray(values) || values.length < 2) return null;
  const W = 100;
  const H = 24;
  const max = Math.max(1, ...values); // avoid divide-by-zero on all-zero series
  const step = W / (values.length - 1);
  const points = values
    .map((v, i) => {
      const x = (i * step).toFixed(2);
      const y = (H - (v / max) * H).toFixed(2);
      return `${x},${y}`;
    })
    .join(' ');
  // fill polygon - same points + bottom-right + bottom-left
  const fillPoints = `${points} ${(W).toFixed(2)},${H} 0,${H}`;
  const last = values[values.length - 1];
  const lastX = ((values.length - 1) * step).toFixed(2);
  const lastY = (H - (last / max) * H).toFixed(2);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="mt-2 w-full h-5 block"
      data-testid={testId}
      aria-hidden="true"
    >
      <polygon points={fillPoints} fill={color} opacity="0.12" />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={lastX} cy={lastY} r="1.6" fill={color} />
    </svg>
  );
};

// iter 155 - Compact collapsible Quiz Funnel card. Sits next to the
// Paid Conversion + Promo Code cards on the Overview tab. Hits
// /api/admin/quiz-funnel which returns counts + rates over a sliding
// window (7 / 30 / 90 days).
// iter 156 - Adds drill-down: clicking a row opens a side panel
// listing the leads at that step with a "Copy emails" action.
const QuizFunnelCard = () => {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillStep, setDrillStep] = useState(null);  // active row id
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    setLoading(true);
    fetch(`${apiUrl}/api/admin/quiz-funnel?days=${days}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setData(d || null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  // When the drill step or days change, fetch the lead list for the
  // side panel. Closing the panel sets drillStep to null which skips.
  useEffect(() => {
    if (!drillStep) { setDrillData(null); return undefined; }
    let cancelled = false;
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    setDrillLoading(true);
    setDrillData(null);
    setCopied(false);
    fetch(`${apiUrl}/api/admin/quiz-funnel/leads?days=${days}&step=${drillStep}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setDrillData(d || null); })
      .finally(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [drillStep, days]);

  const steps = data?.steps || {};
  const rates = data?.rates || {};

  const ROWS = [
    { id: 'leads_captured',      label: 'Leads Captured',         rateKey: null },
    { id: 'checkout_clicked',    label: 'Checkout Clicked',       rateKey: 'leads_to_checkout' },
    { id: 'paid_blueprint',      label: 'Paid Blueprint Only',    rateKey: 'checkout_to_paid' },
    { id: 'upgraded_essentials', label: 'Upgraded to Essentials', rateKey: 'paid_to_essentials' },
    { id: 'upgraded_guided',     label: 'Upgraded to Guided',     rateKey: 'paid_to_guided' },
  ];
  const LABEL_BY_STEP = Object.fromEntries(ROWS.map((r) => [r.id, r.label]));

  const totalLeads = steps.leads_captured || 0;
  const topNum = totalLeads;
  const subtitle = loading
    ? 'Loading…'
    : `${totalLeads} lead${totalLeads === 1 ? '' : 's'} · ${steps.paid_blueprint || 0} paid · last ${days}d`;

  const copyEmails = async () => {
    const emails = (drillData?.leads || []).map((l) => l.email).filter(Boolean);
    if (!emails.length) return;
    try {
      await navigator.clipboard.writeText(emails.join(', '));
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // older browsers - fallback via textarea
      const ta = document.createElement('textarea');
      ta.value = emails.join(', ');
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2200); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="bg-white border border-[#F4ECD8] rounded-lg" data-testid="quiz-funnel-card">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left"
        data-testid="quiz-funnel-toggle"
      >
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">Quiz Funnel</p>
          <p className="text-sm text-[#5A6B73] mt-0.5">{subtitle}</p>
        </div>
        <span className="text-[#5A6B73] text-lg">{open ? '–' : '+'}</span>
      </button>
      {open && (
        <div className="px-5 pb-5">
          <div className="flex gap-1.5 mb-4" data-testid="quiz-funnel-range">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                data-testid={`quiz-funnel-range-${d}`}
                className={`px-3 py-1 rounded-full font-mono uppercase text-[10px] tracking-wider transition ${
                  days === d ? 'bg-[#0A1F2E] text-white' : 'bg-[#F4ECD8] text-[#5A6B73] hover:bg-[#E8D8B0]'
                }`}
              >
                Last {d}d
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-xs text-[#5A6B73]">Loading…</p>
          ) : !data ? (
            <p className="text-xs text-[#5A6B73] italic">No data.</p>
          ) : totalLeads === 0 ? (
            <p className="text-xs text-[#5A6B73] italic">No quiz leads in the last {days} days.</p>
          ) : (
            <ul className="space-y-2" data-testid="quiz-funnel-rows">
              {ROWS.map((row) => {
                const n = steps[row.id] || 0;
                const r = row.rateKey ? rates[row.rateKey] : null;
                const widthPct = topNum > 0 ? Math.max(2, Math.round((n / topNum) * 100)) : 0;
                return (
                  <li key={row.id} data-testid={`quiz-funnel-row-${row.id}`}>
                    <button
                      type="button"
                      onClick={() => setDrillStep(row.id)}
                      disabled={n === 0}
                      data-testid={`quiz-funnel-row-btn-${row.id}`}
                      className="w-full text-left disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F9F6EF] rounded px-2 -mx-2 py-1 transition-colors"
                    >
                      <div className="flex items-baseline justify-between text-sm mb-1">
                        <span className="text-[#0A1F2E]">{row.label}</span>
                        <span className="text-[#0A1F2E] font-semibold tabular-nums">
                          {n.toLocaleString()}
                          {row.rateKey && (
                            <span className="ml-2 text-xs font-normal text-[#5A6B73] font-mono">
                              {r == null ? '-' : `${r}%`}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#F4ECD8] rounded-full overflow-hidden">
                        <div className="h-full bg-[#2CA8A0] rounded-full" style={{ width: `${widthPct}%` }} />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Drill-down side panel (iter 156). */}
      {drillStep && (
        <>
          <div
            className="fixed inset-0 z-40 bg-[#0A1F2E]/35"
            onClick={() => setDrillStep(null)}
            aria-label="Close panel"
            data-testid="quiz-funnel-drill-backdrop"
          />
          <aside
            className="fixed top-0 right-0 z-50 w-full sm:w-[440px] h-full bg-white shadow-2xl flex flex-col"
            data-testid="quiz-funnel-drill-panel"
          >
            <header className="px-5 py-4 border-b border-[#EFE6CF] flex items-center justify-between">
              <div>
                <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">
                  Quiz Funnel · Last {days}d
                </p>
                <p className="font-display italic text-xl text-[#0A1F2E] mt-0.5">
                  {LABEL_BY_STEP[drillStep] || drillStep}
                </p>
                <p className="text-xs text-[#5A6B73] mt-0.5">
                  {drillData ? `${drillData.count} lead${drillData.count === 1 ? '' : 's'}` : 'Loading…'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDrillStep(null)}
                aria-label="Close"
                data-testid="quiz-funnel-drill-close"
                className="text-[#5A6B73] hover:text-[#0A1F2E] text-2xl leading-none w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
            </header>

            <div className="px-5 py-3 border-b border-[#EFE6CF] flex items-center gap-2">
              <button
                type="button"
                onClick={copyEmails}
                disabled={!drillData?.leads?.length}
                data-testid="quiz-funnel-drill-copy"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A1F2E] text-white text-xs font-mono uppercase tracking-[0.14em] hover:bg-[#163448] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {copied ? 'Copied!' : `Copy ${drillData?.leads?.length || 0} email${(drillData?.leads?.length || 0) === 1 ? '' : 's'}`}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="quiz-funnel-drill-list">
              {drillLoading ? (
                <p className="px-5 py-6 text-sm text-[#5A6B73]">Loading…</p>
              ) : !drillData || drillData.leads.length === 0 ? (
                <p className="px-5 py-6 text-sm text-[#5A6B73] italic">No leads at this step.</p>
              ) : (
                <ul className="divide-y divide-[#EFE6CF]">
                  {drillData.leads.map((l) => (
                    <li
                      key={`${l.email}-${l.captured_at || ''}`}
                      data-testid={`quiz-funnel-drill-row-${l.email}`}
                      className="px-5 py-3"
                    >
                      <div className="flex items-baseline justify-between gap-3 mb-0.5">
                        <p className="text-[#0A1F2E] text-sm font-medium truncate">{l.email}</p>
                        <span className="font-mono uppercase text-[9px] tracking-[0.14em] text-[#1A5C6E] flex-none">
                          {LABEL_BY_STEP[l.last_step] || l.last_step}
                        </span>
                      </div>
                      <p className="text-xs text-[#5A6B73]">
                        {[l.city || '-',
                          l.captured_at ? new Date(l.captured_at).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago'}) : '']
                          .filter(Boolean).join(' · ')}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
};


// iter 144g - compact collapsible Paid Conversion card. Sits below the
// Funnel Snapshot on the Overview tab. Reads `recent-paid-clients`.
const PaidConversionCard = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/recent-paid-clients?limit=15`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { clients: [] }))
      .then((d) => setRows(d?.clients || []))
      .finally(() => setLoading(false));
  }, []);

  const tierColor = (t) => ({
    blueprint_only: 'bg-[#FFF1D6] text-[#8C5A14]',
    essentials:     'bg-[#D7F0E0] text-[#1A7F4F]',
    guided:         'bg-[#DAE6F4] text-[#1A3A6E]',
  })[t] || 'bg-[#F4ECD8] text-[#5A6B73]';

  const fmt = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago'}); }
    catch { return iso; }
  };

  const fmtAmount = (a) => {
    if (a == null) return '-';
    const n = typeof a === 'number' ? a : parseFloat(a);
    if (Number.isNaN(n)) return '-';
    return `$${n >= 100 ? Math.round(n) : n.toFixed(2)}`;
  };

  return (
    <div className="bg-white border border-[#F4ECD8] rounded-lg" data-testid="paid-conversion-card">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left"
        data-testid="paid-conversion-toggle"
      >
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">Paid Conversion</p>
          <p className="text-sm text-[#5A6B73] mt-0.5">
            {loading ? 'Loading…' : `${rows.length} paid client${rows.length === 1 ? '' : 's'} · most recent first`}
          </p>
        </div>
        <span className="text-[#5A6B73] text-lg">{open ? '–' : '+'}</span>
      </button>
      {open && (
        <div className="px-5 pb-5">
          {loading ? (
            <p className="text-xs text-[#5A6B73]">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-xs text-[#5A6B73] italic">No paid clients yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[#5A6B73]">
                <tr>
                  <th className="text-left py-2 font-mono uppercase text-[10px] tracking-wider">Name</th>
                  <th className="text-left py-2 font-mono uppercase text-[10px] tracking-wider">Tier</th>
                  <th className="text-left py-2 font-mono uppercase text-[10px] tracking-wider">Purchased</th>
                  <th className="text-right py-2 font-mono uppercase text-[10px] tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.user_id || c.email} className="border-t border-[#F4ECD8]" data-testid={`paid-row-${c.user_id || c.email}`}>
                    <td className="py-2 text-[#0A1F2E]">{c.name}</td>
                    <td className="py-2">
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded ${tierColor(c.tier)}`}>{c.tier}</span>
                    </td>
                    <td className="py-2 text-[#5A6B73] text-xs">{fmt(c.purchased_at)}</td>
                    <td className="py-2 text-right text-[#0A1F2E] font-semibold">{fmtAmount(c.amount_paid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// iter 144g - compact collapsible Promo Code Usage card. Per-code totals
// and per-user redemption details, with non-paying users visually distinct.
const PromoCodeUsageCard = () => {
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCode, setExpandedCode] = useState('');

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/promo-redemption-summary`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { codes: [] }))
      .then((d) => setCodes(d?.codes || []))
      .finally(() => setLoading(false));
  }, []);

  const totalRedemptions = codes.reduce((acc, c) => acc + (c.total_redemptions || 0), 0);

  return (
    <div className="bg-white border border-[#F4ECD8] rounded-lg" data-testid="promo-usage-card">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left"
        data-testid="promo-usage-toggle"
      >
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">Promo Code Usage</p>
          <p className="text-sm text-[#5A6B73] mt-0.5">
            {loading
              ? 'Loading…'
              : `${codes.length} active code${codes.length === 1 ? '' : 's'} · ${totalRedemptions} redemption${totalRedemptions === 1 ? '' : 's'}`}
          </p>
        </div>
        <span className="text-[#5A6B73] text-lg">{open ? '–' : '+'}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-2">
          {loading ? (
            <p className="text-xs text-[#5A6B73]">Loading…</p>
          ) : codes.length === 0 ? (
            <p className="text-xs text-[#5A6B73] italic" data-testid="promo-usage-empty">
              No promo codes have been redeemed yet.
            </p>
          ) : (
            codes.map((c) => (
              <div key={c.code} className="border border-[#F4ECD8] rounded-md" data-testid={`promo-code-row-${c.code}`}>
                <button
                  onClick={() => setExpandedCode(expandedCode === c.code ? '' : c.code)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-[#F9F6EF]"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono uppercase text-xs tracking-wider text-[#0A1F2E] font-semibold">{c.code}</span>
                    <span className="text-xs text-[#5A6B73]">{c.total_redemptions} redemption{c.total_redemptions === 1 ? '' : 's'}</span>
                    {c.paid_count > 0 && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D7F0E0] text-[#1A7F4F]">
                        {c.paid_count} PAID
                      </span>
                    )}
                    {c.unpaid_count > 0 && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F8E0D7] text-[#9F4422]">
                        {c.unpaid_count} NON-PAYING
                      </span>
                    )}
                  </div>
                  <span className="text-[#5A6B73] text-sm">{expandedCode === c.code ? '–' : '+'}</span>
                </button>
                {expandedCode === c.code && (
                  <div className="px-4 pb-3">
                    <ul className="text-xs divide-y divide-[#F4ECD8]">
                      {c.users.map((u, i) => (
                        <li
                          key={i}
                          className={`py-1.5 flex items-center justify-between ${u.paid ? 'text-[#0A1F2E]' : 'text-[#9F4422] italic'}`}
                          data-testid={`promo-${c.code}-user-${i}`}
                        >
                          <span className="truncate">{u.email || '(no email)'}</span>
                          <span className="font-mono text-[10px] uppercase tracking-wider">
                            {u.paid ? 'paid' : 'unpaid'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// iter 144d - small amber badge linking to /admin/partner-applications.
// Shows up at the top of the Overview tab whenever there is at least one
// pending partner application waiting for review. Hidden entirely when
// the queue is empty.
const PartnerApplicationsBadge = () => {
  const [pending, setPending] = useState(0);
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/partner-applications-count`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { pending: 0 }))
      .then((d) => setPending(d?.pending || 0))
      .catch(() => setPending(0));
  }, []);
  if (!pending) return null;
  return (
    <a
      href="/admin/partner-applications"
      data-testid="admin-pending-partner-apps-badge"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFF1D6] border border-[#E8A830]/30 text-[#8C5A14] font-mono uppercase text-[11px] tracking-[0.16em] hover:border-[#E8A830] transition-colors"
    >
      {pending} partner application{pending === 1 ? '' : 's'} pending review →
    </a>
  );
};

// iter 144 Phase 2 - Partner Network LIVE card. Sits between the Tool
// Leads health card and the Conversion Path card on the Overview tab.
const PartnerNetworkCard = () => {
  const [pendingApps, setPendingApps] = useState(0);
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/partner-conversations-health`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const err = await r.json().catch(() => ({}));
        throw new Error(typeof err?.detail === 'string' ? err.detail : `HTTP ${r.status}`);
      })
      .then(setData)
      .catch((e) => { setError(e.message || 'Failed'); setData(null); });
    // iter 144d - pending partner applications counter (added to the grid below).
    fetch(`${apiUrl}/api/admin/partner-applications-count`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { pending: 0 }))
      .then((d) => setPendingApps(d?.pending || 0))
      .catch(() => setPendingApps(0));
  }, []);

  const SlaPill = ({ badge }) => {
    const tones = {
      active:     { bg: '#1A7F4F', fg: '#FFFFFF', label: 'ACTIVE' },
      waiting:    { bg: '#E8A830', fg: '#0A1F2E', label: 'WAITING' },
      sla_breach: { bg: '#C0392B', fg: '#FFFFFF', label: 'SLA BREACH' },
      escalated:  { bg: '#7B1212', fg: '#FFFFFF', label: 'ESCALATED' },
      resolved:   { bg: '#5A6B73', fg: '#FFFFFF', label: 'RESOLVED' },
    };
    const t = tones[badge] || tones.active;
    return (
      <span
        className="inline-block px-2 py-0.5 font-mono uppercase text-[8px] tracking-[0.18em] font-semibold rounded"
        style={{ background: t.bg, color: t.fg }}
      >
        {t.label}
      </span>
    );
  };

  // 7-day sparkline - same dot-line treatment as WelcomeSequenceHealth.
  const Sparkline = ({ data: points }) => {
    if (!points || points.length === 0) return null;
    const w = 200, h = 32, p = 4;
    const max = Math.max(1, ...points.map((d) => d.count));
    const pts = points.map((d, i) => {
      const x = p + (i * (w - 2 * p)) / (points.length - 1 || 1);
      const y = h - p - ((d.count / max) * (h - 2 * p));
      return [x, y];
    });
    const path = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    return (
      <svg width={w} height={h} className="block mt-2" aria-hidden="true">
        <path d={path} fill="none" stroke="#2CA8A0" strokeWidth="1.5" />
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="2" fill="#2CA8A0" />
        ))}
      </svg>
    );
  };

  return (
    <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-6" data-testid="admin-partner-network-card">
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">
          Partner Network · LIVE
        </p>
        <a
          href="/admin/partner-conversations"
          className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#2CA8A0] hover:underline"
          data-testid="admin-partner-network-card-link"
        >
          View all →
        </a>
      </div>
      <h3 className="font-semibold text-[#0A1F2E] mb-4">Partner Conversations</h3>

      {error && <p className="text-sm text-[#C0392B]" data-testid="admin-partner-network-error">{error}</p>}
      {data === undefined && !error && <p className="text-sm text-[#5A6B73]">Loading…</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <div data-testid="pn-vetted-active">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73]">Vetted active</p>
              <p className="text-2xl font-display italic text-[#0A1F2E]">{data.total_vetted_active}</p>
            </div>
            <div data-testid="pn-new-today">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73]">New today</p>
              <p className="text-2xl font-display italic text-[#0A1F2E]">{data.new_conversations_today}</p>
              <Sparkline data={data.sparkline_7d} />
            </div>
            <div data-testid="pn-week-total">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73]">This week</p>
              <p className="text-2xl font-display italic text-[#0A1F2E]">{data.total_conversations_this_week}</p>
            </div>
            <div data-testid="pn-avg-response">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73]">Avg response (h)</p>
              <p className="text-2xl font-display italic text-[#0A1F2E]">{data.avg_response_hours_this_week ?? '-'}</p>
            </div>
            <div data-testid="pn-sla-breaches">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73]">SLA breaches</p>
              <p className={`text-2xl font-display italic ${data.sla_breaches_active > 0 ? 'text-[#C0392B]' : 'text-[#0A1F2E]'}`}>
                {data.sla_breaches_active}
              </p>
            </div>
            <div data-testid="pn-unbilled">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73]">Unbilled</p>
              <p className={`text-2xl font-display italic ${data.unbilled_introductions_total > 0 ? 'text-[#E8A830]' : 'text-[#0A1F2E]'}`}>
                {data.unbilled_introductions_total}
              </p>
            </div>
            <a
              href="/admin/partner-applications"
              data-testid="pn-pending-applications"
              className="block group"
            >
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73] group-hover:text-[#2CA8A0]">Pending applications</p>
              <p className={`text-2xl font-display italic ${pendingApps > 0 ? 'text-[#E8A830]' : 'text-[#0A1F2E]'}`}>
                {pendingApps}
              </p>
            </a>
          </div>

          {data.recent && data.recent.length > 0 && (
            <div className="border-t border-[#F4ECD8] pt-3 mt-2" data-testid="admin-partner-network-recent">
              <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#5A6B73] mb-2">Most recent</p>
              <ul className="space-y-1.5 text-sm">
                {data.recent.map((r) => (
                  <li key={r.conversation_id} className="flex items-center gap-3">
                    <SlaPill badge={r.sla_badge} />
                    <span className="text-[#0A1F2E] flex-1 truncate">
                      {r.partner_name} ↔ {r.member_first_name} · {r.subject}
                    </span>
                    <span className="text-[11px] text-[#5A6B73]">{r.last_message_at ? new Date(r.last_message_at).toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'America/Chicago'}) : '-'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// iter 144z - Email Performance card. Aggregates email_logs by tag
// (sequence name) so Francisco can see open / click / unsub rates per
// nurture step. Includes a city-personalization breakdown for the two
// Day-2 city-aware sequences (quiz_nurture_day2, calc_nurture_day2).
const EmailPerformanceCard = () => {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/email-performance?days=${days}`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const err = await r.json().catch(() => ({}));
        throw new Error(typeof err?.detail === 'string' ? err.detail : `HTTP ${r.status}`);
      })
      .then((d) => setData(d))
      .catch((e) => { setError(String(e.message || e)); setData(null); });
  }, [days]);

  return (
    <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-6 mb-6" data-testid="email-performance-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1">
            Email Performance · email_logs
          </p>
          <h3 className="font-semibold text-[#0A1F2E]">Email Performance</h3>
        </div>
        <select
          data-testid="email-performance-range"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="text-sm border border-[#EFE6CF] bg-white px-3 py-1.5 rounded text-[#0A1F2E]"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 12 months</option>
        </select>
      </div>

      {error && (
        <div className="bg-[#FFF3E6] border border-[#E8A830] p-3 text-sm text-[#0A1F2E] mb-3" data-testid="email-performance-error">
          Failed to load - {error}
        </div>
      )}

      {data === undefined && !error && (
        <p className="text-sm text-[#5A6B73]">Loading…</p>
      )}

      {data && (
        <>
          {/* iter 179 (Fix 2) - Surface a big warning banner when opens/clicks
              are structurally broken (0 opens across >20 sends all-time). Same
              display replaces the misleading "0%" columns with "-". */}
          {data?.tracking?.ok === false && (
            <div
              className="bg-red-50 border border-red-300 text-red-900 px-4 py-3 mb-3 text-sm"
              data-testid="email-performance-tracking-broken"
              role="alert"
            >
              <p className="font-semibold mb-1">Open/click tracking unavailable</p>
              <p className="text-xs leading-relaxed">
                {data.tracking.reason}
              </p>
              <p className="text-xs mt-1 text-red-800">
                Sends all-time: <b>{data.tracking.total_sends_all_time}</b> ·
                Opens recorded: <b>{data.tracking.total_opens_all_time}</b>
              </p>
            </div>
          )}
          {/* Per-sequence rollup */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-performance-sequences">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#5A6B73] border-b border-[#F4ECD8]">
                  <th className="py-2 pr-3">Sequence</th>
                  <th className="py-2 px-3 text-right">Sent</th>
                  <th className="py-2 px-3 text-right">Opens</th>
                  <th className="py-2 px-3 text-right">Open %</th>
                  <th className="py-2 px-3 text-right">Clicks</th>
                  <th className="py-2 px-3 text-right">Click %</th>
                  <th className="py-2 pl-3 text-right">Unsubs</th>
                </tr>
              </thead>
              <tbody>
                {(data.sequences || []).length === 0 && (
                  <tr><td colSpan={7} className="py-6 text-center text-sm text-[#5A6B73]">
                    No email events in this window yet.
                  </td></tr>
                )}
                {(data.sequences || []).map((row) => (
                  <tr key={row.tag} className="border-b border-[#F4ECD8] hover:bg-[#F9F6EF]">
                    <td className="py-2 pr-3 font-mono text-xs text-[#1A5C6E]">{row.tag}</td>
                    <td className="py-2 px-3 text-right text-[#0A1F2E]">{row.sent}</td>
                    <td className="py-2 px-3 text-right text-[#0A1F2E]">
                      {data?.tracking?.ok === false ? <span className="text-[#B94A3B]" title="Tracking unavailable">-</span> : row.opened}
                    </td>
                    <td className="py-2 px-3 text-right text-[#0A1F2E] font-semibold">
                      {data?.tracking?.ok === false ? <span className="text-[#B94A3B]">-</span> : `${row.open_rate}%`}
                    </td>
                    <td className="py-2 px-3 text-right text-[#0A1F2E]">{row.clicked}</td>
                    <td className="py-2 px-3 text-right text-[#E8A830] font-semibold">{row.click_rate}%</td>
                    <td className="py-2 pl-3 text-right text-[#5A6B73]">{row.unsubscribed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* City-personalisation breakdown */}
          {(data.city_breakdown || []).length > 0 && (
            <div className="mt-6">
              <p className="font-mono uppercase tracking-[0.16em] text-[10px] text-[#1A5C6E] mb-2">
                City personalization breakdown · Day-2 sequences
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="email-performance-cities">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[#5A6B73] border-b border-[#F4ECD8]">
                      <th className="py-2 pr-3">Sequence</th>
                      <th className="py-2 px-3">City</th>
                      <th className="py-2 px-3 text-right">Sent</th>
                      <th className="py-2 px-3 text-right">Open %</th>
                      <th className="py-2 pl-3 text-right">Click %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.city_breakdown.map((row, i) => (
                      <tr key={i} className="border-b border-[#F4ECD8]">
                        <td className="py-2 pr-3 font-mono text-xs text-[#1A5C6E]">{row.tag}</td>
                        <td className="py-2 px-3 text-[#0A1F2E]">{row.city}</td>
                        <td className="py-2 px-3 text-right text-[#0A1F2E]">{row.sent}</td>
                        <td className="py-2 px-3 text-right text-[#0A1F2E] font-semibold">{row.open_rate}%</td>
                        <td className="py-2 pl-3 text-right text-[#E8A830] font-semibold">{row.click_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// iter 143 - Tool Leads health card: 6 counters (3 today, 3 this week) + 5
// most-recent leads with their portal-access status. Placed below the
// Welcome Sequence Health card on the admin Overview tab.
const ToolLeadsCard = () => {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/tool-leads-health`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const err = await r.json().catch(() => ({}));
        throw new Error(typeof err?.detail === 'string' ? err.detail : `HTTP ${r.status}`);
      })
      .then((d) => setData(d))
      .catch((e) => { setError(String(e.message || e)); setData(null); });
  }, []);

  const counter = (label, value, testId, accent = 'text-[#0A1F2E]') => (
    <div className="bg-[#F9F6EF] border border-[#EFE6CF] p-4" data-testid={testId}>
      <p className={`font-display text-3xl ${accent} leading-none`}>{value ?? '-'}</p>
      <p className="mt-2 font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73]">
        {label}
      </p>
    </div>
  );

  const [resendingEmail, setResendingEmail] = useState(null);
  const [sendingNext, setSendingNext] = useState(null);

  const handleResend = async (row) => {
    if (!row?.email || !row?.type) return;
    setResendingEmail(row.email + row.type);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const r = await fetch(`${apiUrl}/api/admin/tool-leads/resend-last`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: row.email,
          lead_type: row.type.toLowerCase(),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.success) {
        notifySuccess(`Email resent to ${row.email}`);
      } else {
        notifyError(data, 'Resend failed');
      }
    } catch {
      notifyError('Network error - please try again.');
    } finally {
      setResendingEmail(null);
    }
  };

  // iter 143c - fire the next un-sent email in this lead's sequence
  // immediately. Backend returns 409 "Sequence complete." when all
  // emails have already been sent; we surface that distinctly.
  const handleSendNext = async (row) => {
    if (!row?.email || !row?.type || !row?.next_email_n) return;
    setSendingNext(row.email + row.type);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const r = await fetch(`${apiUrl}/api/admin/tool-leads/send-next`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: row.email,
          lead_type: row.type.toLowerCase(),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.success) {
        notifySuccess(`Next email sent to ${row.email}`);
        // Optimistically advance the row's next_email_n so the button
        // updates immediately without a refetch.
        row.next_email_n = data.sequence_complete ? null : (data.sent_email_n + 1);
      } else {
        notifyError(data, 'Send next failed');
      }
    } catch {
      notifyError('Network error - please try again.');
    } finally {
      setSendingNext(null);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago'});
    } catch { return iso; }
  };

  return (
    <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-6" data-testid="admin-tool-leads-card">
      <div className="flex items-center justify-between mb-1">
        <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">
          {/* iter 179 (Fix 3) - relabeled: this IS the per-source breakdown for
              the unified Leads tab (Tool Leads tab was removed). */}
          New Leads · LIVE
        </p>
      </div>
      <h3 className="font-semibold text-[#0A1F2E] mb-4">New Leads</h3>


      {data === undefined ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-[78px] bg-[#F4ECD8] animate-pulse" />
          ))}
        </div>
      ) : error || !data ? (
        <p className="text-sm text-red-500" data-testid="admin-tool-leads-error">
          Failed to load: {error || 'unknown error'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tl-new-leads-strip">
            {/* iter 182.e - Option A reality-mirrored counters. Today row
                first, then last-7d row so the newest activity is on top. */}
            {counter('/start Quiz · Today', data.today?.quiz, 'tl-quiz-today', 'text-[#2CA8A0]')}
            {counter('Cost Calc V2 · Today', data.today?.calculator_v2, 'tl-calc-v2-today', 'text-[#2CA8A0]')}
            {counter('Visa Finder · Today', data.today?.visa, 'tl-visa-today', 'text-[#2CA8A0]')}
            {counter('Move Score · Today', data.today?.score, 'tl-score-today', 'text-[#2CA8A0]')}
            {counter('Calc legacy v1 · Today', data.today?.calculator, 'tl-calc-today', 'text-[#2CA8A0]')}
            {counter('Blueprint intake · Today', data.today?.intake, 'tl-intake-today', 'text-[#2CA8A0]')}
            {counter('/start Quiz · 7d', data.week?.quiz, 'tl-quiz-week')}
            {counter('Cost Calc V2 · 7d', data.week?.calculator_v2, 'tl-calc-v2-week')}
            {counter('Visa Finder · 7d', data.week?.visa, 'tl-visa-week')}
            {counter('Move Score · 7d', data.week?.score, 'tl-score-week')}
            {counter('Calc legacy v1 · 7d', data.week?.calculator, 'tl-calc-week')}
            {counter('Blueprint intake · 7d', data.week?.intake, 'tl-intake-week')}
          </div>

          {Array.isArray(data.recent) && data.recent.length > 0 && (
            <div className="mt-5 border-t border-[#EFE6CF] pt-4">
              <p className="font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73] mb-3">
                Latest 5
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tl-recent-table">
                  <thead className="text-left text-[10px] font-mono uppercase tracking-wider text-[#5A6B73]">
                    <tr className="border-b border-[#EFE6CF]">
                      <th className="py-2 pr-3">Time</th>
                      <th className="py-2 pr-3">Type</th>
                      <th className="py-2 pr-3">Detail</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Portal</th>
                      <th className="py-2 pr-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((r, i) => (
                      <tr key={r.lead_id || i} className="border-b border-[#F4ECD8]">
                        <td className="py-2 pr-3 text-[#5A6B73] whitespace-nowrap">{fmtTime(r.created_at)}</td>
                        <td className="py-2 pr-3">
                          <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-[#F4ECD8] text-[#1A5C6E] rounded">{r.type}</span>
                        </td>
                        <td className="py-2 pr-3 text-[#0A1F2E]">{r.detail || '-'}</td>
                        <td className="py-2 pr-3 text-[#0A1F2E] break-all">
                          {/* iter 179 (Fix 3) - click routes into unified Leads tab drawer. */}
                          <button
                            type="button"
                            onClick={() => window.dispatchEvent(
                              new CustomEvent('mundalo:open-lead-drawer', { detail: { email: r.email } })
                            )}
                            className="underline decoration-dotted text-[#1A5C6E] hover:text-[#0A1F2E] transition"
                            data-testid={`tl-open-drawer-${i}`}
                            title="Open in Leads tab"
                          >
                            {r.email}
                          </button>
                        </td>
                        <td className="py-2 pr-3">
                          {r.portal_access ? (
                            <span className="text-[#2CA8A0] font-mono text-[10px] uppercase tracking-wider">Yes</span>
                          ) : (
                            <span className="text-[#5A6B73] font-mono text-[10px] uppercase tracking-wider">No</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleResend(r)}
                              disabled={resendingEmail === r.email + r.type}
                              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border border-[#1A5C6E]/35 text-[#1A5C6E] hover:bg-[#1A5C6E]/10 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              data-testid={`tl-resend-${i}`}
                              title="Re-fire the most recent sequence email"
                            >
                              {resendingEmail === r.email + r.type ? 'Sending…' : 'Resend last'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSendNext(r)}
                              disabled={!r.next_email_n || sendingNext === r.email + r.type}
                              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border border-[#2CA8A0]/40 text-[#2CA8A0] hover:bg-[#2CA8A0]/10 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              data-testid={`tl-send-next-${i}`}
                              title={r.next_email_n ? `Send email ${r.next_email_n} now` : 'Sequence complete.'}
                            >
                              {sendingNext === r.email + r.type ? 'Sending…' : 'Send next'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const WelcomeSequenceHealth = () => {
  const [data, setData] = useState(undefined); // undefined = loading
  const [error, setError] = useState(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/welcome-sequence-health?days=7`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const err = await r.json().catch(() => ({}));
        const detail = err?.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : detail?.message || `HTTP ${r.status}`;
        throw new Error(msg);
      })
      .then((d) => setData(d))
      .catch((e) => { setError(String(e.message || e)); setData(null); });
  }, []);

  const stat = (label, value, testId, tone = 'default', trend = null) => {
    const valueTone =
      tone === 'success' ? 'text-[#2CA8A0]' :
      tone === 'warn'    ? 'text-[#E8A830]' :
      tone === 'danger'  ? 'text-red-600' :
                           'text-[#0A1F2E]';
    const strokeTone =
      tone === 'success' ? '#2CA8A0' :
      tone === 'warn'    ? '#E8A830' :
      tone === 'danger'  ? '#DC2626' :
                           '#1A5C6E';
    return (
      <div className="bg-[#F9F6EF] border border-[#EFE6CF] p-4" data-testid={testId}>
        <p className={`font-display text-3xl ${valueTone} leading-none`}>
          {value ?? '-'}
        </p>
        <p className="mt-2 font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73]">
          {label}
        </p>
        {Array.isArray(trend) && trend.length > 1 && (
          <Sparkline
            values={trend}
            color={strokeTone}
            testId={`${testId}-sparkline`}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
      data-testid="admin-welcome-sequence-health"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-[#0A1F2E]">Welcome Sequence Health</h3>
        <span className="text-xs text-[#5A6B73] font-mono uppercase tracking-wider">
          Founder · live
        </span>
      </div>

      {data === undefined ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[78px] bg-[#F4ECD8] animate-pulse" />
          ))}
        </div>
      ) : error || !data ? (
        <p className="text-sm text-red-500" data-testid="admin-welcome-sequence-health-error">
          Failed to load: {error || 'unknown error'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {stat('Signups today',          data.signups_today,             'wsh-signups-today',
                  'default', data.trends?.signups)}
            {stat('Email 1 sent today',     data.email_1_sent_today,        'wsh-email-1-sent',
                  'success', data.trends?.email_1_sent)}
            {stat('Errors · last 24h',      data.errors_24h_count,          'wsh-errors-24h',
                  data.errors_24h_count > 0 ? 'danger' : 'default',
                  data.trends?.errors)}
            {stat('Email 2 pending',        data.email_2_pending,           'wsh-email-2-pending')}
            {stat('Email 3 pending',        data.email_3_pending,           'wsh-email-3-pending')}
            {stat('Blueprint cancels today',data.blueprint_cancellations_today,
                  'wsh-blueprint-cancels',  'warn')}
          </div>

          {data.errors_24h_sample?.length > 0 && (
            <div className="mt-5 border-t border-[#F4ECD8] pt-4">
              <p className="font-mono uppercase text-[10px] tracking-[0.14em] text-[#5A6B73] mb-2">
                Recent errors
              </p>
              <ul className="space-y-1.5">
                {data.errors_24h_sample.slice(0, 5).map((u, i) => (
                  <li
                    key={i}
                    className="text-xs text-[#0A1F2E]/85 flex flex-col sm:flex-row sm:items-baseline sm:gap-3"
                    data-testid={`wsh-error-row-${i}`}
                  >
                    <span className="font-medium">{u.email}</span>
                    <span className="text-red-600/90 truncate flex-1" title={u.welcome_email_error}>
                      {u.welcome_email_error}
                    </span>
                    <span className="text-[#5A6B73] font-mono text-[10px]">
                      {(u.welcome_email_attempted_at || '').slice(11, 16)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-4 font-mono uppercase text-[9px] tracking-[0.12em] text-[#5A6B73]">
            updated {formatCentralTime(data.as_of, { second: '2-digit' })} CT · 7-day trend shown · refresh page for latest
          </p>
        </>
      )}
    </div>
  );
};


// ============== OVERVIEW TAB ==============
// iter 144g - Pending partner profile image submissions. Partners can
// queue new thumbnail/logo/gallery uploads via their token-gated reply
// page; this card lets admins approve or reject each one inline.
const PendingPartnerImagesCard = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [acting, setActing] = useState('');

  const reload = () => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    setLoading(true);
    fetch(`${apiUrl}/api/admin/partner-pending-images?status=pending`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        setRows(d?.items || []);
        setPendingCount((d?.items || []).length);
      })
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const act = async (id, action, extra = {}) => {
    setActing(id);
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    try {
      const r = await fetch(`${apiUrl}/api/admin/partner-pending-images/${id}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(extra),
      });
      if (r.ok) reload();
    } finally {
      setActing('');
    }
  };

  return (
    <div className="bg-white border border-[#F4ECD8] rounded-lg" data-testid="pending-images-card">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left"
        data-testid="pending-images-toggle"
      >
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">
            Partner Image Approvals
          </p>
          <p className="text-sm text-[#5A6B73] mt-0.5">
            {loading
              ? 'Loading…'
              : pendingCount === 0
                ? 'All caught up - no images awaiting review.'
                : `${pendingCount} image${pendingCount === 1 ? '' : 's'} awaiting review`}
          </p>
        </div>
        {pendingCount > 0 && (
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-[#FFF1D6] text-[#8C5A14] mr-3">
            {pendingCount} PENDING
          </span>
        )}
        <span className="text-[#5A6B73] text-lg">{open ? '–' : '+'}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          {!loading && rows.length === 0 && (
            <p className="text-xs text-[#5A6B73] italic" data-testid="pending-images-empty">
              All caught up.
            </p>
          )}
          {rows.map((row) => (
            <div
              key={row.id}
              className="flex flex-col sm:flex-row gap-3 border border-[#F4ECD8] rounded-md p-3"
              data-testid={`pending-image-row-${row.id}`}
            >
              <img
                src={row.image_url}
                alt=""
                className="w-24 h-24 object-cover rounded-md bg-[#F4ECD8] flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-mono uppercase tracking-wider text-[#1A5C6E]">
                  {row.image_type}
                </p>
                <p className="text-sm text-[#0A1F2E] font-semibold truncate">{row.partner_name}</p>
                <p className="text-xs text-[#5A6B73] mt-0.5">
                  Submitted {row.submitted_at ? new Date(row.submitted_at).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : '-'}
                </p>
                <a
                  href={row.image_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#2CA8A0] underline mt-1 inline-block"
                >
                  View full size
                </a>
              </div>
              <div className="flex sm:flex-col gap-2 sm:gap-1.5 sm:items-end">
                <button
                  onClick={() => act(row.id, 'approve')}
                  disabled={acting === row.id}
                  data-testid={`approve-image-${row.id}`}
                  className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded-full bg-[#2CA8A0] text-white hover:brightness-95 disabled:opacity-50"
                >
                  {acting === row.id ? '…' : 'Approve'}
                </button>
                <button
                  onClick={() => {
                    const reason = window.prompt('Reason for rejection (optional):', '');
                    if (reason !== null) act(row.id, 'reject', { reason });
                  }}
                  disabled={acting === row.id}
                  data-testid={`reject-image-${row.id}`}
                  className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded-full border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B] hover:text-white disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== iter 165 - CALCULATOR DROPOFF CARD ==============
const CalculatorDropoffCard = () => {
  const [data, setData] = React.useState(undefined); // undefined = loading

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/calculator-dropoff`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d || null))
      .catch(() => setData(null));
  }, []);

  return (
    <section
      className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
      data-testid="calculator-dropoff-card"
    >
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-semibold text-[#0A1F2E]">Calculator Dropoff</h3>
        {data && data.total_sessions > 0 && (
          <p
            className="text-xs font-mono uppercase tracking-[0.12em] text-[#5A6B73]"
            data-testid="dropoff-total-sessions"
          >
            {data.total_sessions} sessions
          </p>
        )}
      </div>
      <p className="text-xs text-[#5A6B73] mb-4">
        Calculator V2 funnel - % of visitors who reached each screen.
      </p>

      {data === undefined ? (
        <p className="text-sm text-[#5A6B73] italic">Loading…</p>
      ) : !data || data.total_sessions === 0 ? (
        <p
          className="text-sm text-[#5A6B73] italic"
          data-testid="dropoff-empty-state"
        >
          No calculator sessions tracked yet. Data starts collecting on the next visit.
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid="dropoff-funnel-list">
          {data.rows.map((row) => {
            const isBiggestDrop = data.biggest_drop_index === row.position;
            return (
              <li
                key={row.position}
                data-testid={`dropoff-row-${row.position}`}
                className={`flex items-center gap-3 px-3 py-2 rounded transition ${
                  isBiggestDrop
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-[#F9F6EF]/40 border border-transparent'
                }`}
              >
                <span className="text-[10px] font-mono w-5 text-[#5A6B73] tabular-nums">
                  {row.position + 1}.
                </span>
                <span
                  className={`text-sm flex-1 ${
                    isBiggestDrop ? 'text-red-700 font-medium' : 'text-[#0A1F2E]'
                  }`}
                >
                  {row.label}
                </span>
                {/* Inline mini-bar so the funnel shape is visible at a glance */}
                <div className="hidden sm:block w-32 h-1.5 bg-[#F4ECD8] rounded-full overflow-hidden">
                  <div
                    className={`h-full ${isBiggestDrop ? 'bg-red-400' : 'bg-[#2CA8A0]'}`}
                    style={{ width: `${Math.max(0, Math.min(100, row.pct))}%` }}
                  />
                </div>
                <span
                  className={`text-sm font-mono tabular-nums w-14 text-right ${
                    isBiggestDrop ? 'text-red-700 font-semibold' : 'text-[#0A1F2E]'
                  }`}
                  data-testid={`dropoff-pct-${row.position}`}
                >
                  {row.pct}%
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {data && data.biggest_drop_index !== null && data.biggest_drop_pct > 0 && (
        <p
          className="text-xs text-red-700 mt-3"
          data-testid="dropoff-biggest-drop-note"
        >
          Biggest drop:{' '}
          <span className="font-semibold">
            {data.rows[data.biggest_drop_index - 1]?.label} → {data.rows[data.biggest_drop_index]?.label}
          </span>{' '}
          ({data.biggest_drop_pct} pts)
        </p>
      )}
    </section>
  );
};

// iter 169 - Buyer Health Check card on Admin Overview. Surfaces paying
// clients whose Blueprint is missing/stuck > 1h after purchase, with a
// one-click Generate Blueprint button per row. Shows a green "all healthy"
// state when the at_risk list is empty.
const BuyerHealthCheckCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [dismissingFor, setDismissingFor] = useState(null);
  // iter 169b - "Recover All" bulk workflow state.
  // recoverAllResults: { [email]: 'pending' | 'queued' | 'skipped' | 'error' }
  // When a bulk run is in flight, recoverAllRunning is true and each row
  // shows its individual status as the parallel calls resolve.
  const [recoverAllRunning, setRecoverAllRunning] = useState(false);
  const [recoverAllResults, setRecoverAllResults] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/buyer-health-check`,
        { credentials: 'include' },
      );
      if (r.ok) setData(await r.json());
    } catch (e) {
      console.error('buyer health check failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleGenerate = async (email) => {
    setGeneratingFor(email);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/generate-blueprint`,
        { method: 'POST', credentials: 'include' },
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      const messages = {
        queued: 'Blueprint generation queued. Status flips to "done" in ~2–5 minutes.',
        skipped_already_done: 'Blueprint already generated.',
        skipped_already_generating: 'Generation already in progress - give it a few minutes.',
      };
      notifySuccess(messages[body.action] || `Action: ${body.action}`);
      fetchData();
    } catch (e) {
      notifyError(`Generate failed: ${e.message}`);
    } finally {
      setGeneratingFor(null);
    }
  };

  // iter 178 - Dismiss a comp/promo recipient from the Buyer Health alert.
  // Soft-marks the user with bh_dismissed=true so the red banner only fires
  // for actual paying customers who need rescue. Reversible via the API.
  const handleDismiss = async (row) => {
    if (dismissingFor) return;
    const confirmed = window.confirm(
      `Dismiss ${row.name} (${row.email}) from Buyer Health?\n\n`
      + `Use this for comped / FIRST100FREE / manually-upgraded accounts. `
      + `They will no longer appear in the red alert. Reversible from the CLI.`
    );
    if (!confirmed) return;
    setDismissingFor(row.email);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/buyer-health-check/${row.user_id}/dismiss`,
        { method: 'POST', credentials: 'include' },
      );
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      notifySuccess(`${row.name} dismissed from Buyer Health.`);
      // Optimistic - remove the row locally so the count updates instantly.
      setData((prev) => prev ? {
        ...prev,
        at_risk: (prev.at_risk || []).filter((r) => r.user_id !== row.user_id),
        total_at_risk: Math.max(0, (prev.total_at_risk || 1) - 1),
      } : prev);
    } catch (e) {
      notifyError(`Dismiss failed: ${e.message}`);
    } finally {
      setDismissingFor(null);
    }
  };

  // iter 169b - fire Generate Blueprint for every at-risk row in parallel,
  // tracking each result individually so the UI shows per-row progress as
  // the responses come back. Uses Promise.allSettled so a single failure
  // doesn't abort the whole batch.
  const handleRecoverAll = async () => {
    const rows = data?.at_risk || [];
    if (rows.length === 0) return;
    const apiCost = (rows.length * 0.2).toFixed(2);
    const msg = (
      `Generate Blueprints for ${rows.length} stuck buyer${rows.length === 1 ? '' : 's'}?\n\n`
      + `This will use approximately ${rows.length} Claude API credit${rows.length === 1 ? '' : 's'} `
      + `(~$${apiCost} in LLM costs at current Claude Sonnet pricing).\n\n`
      + `Each Blueprint takes 2–5 minutes to generate. Buyers will be emailed automatically when done.`
    );
    if (!window.confirm(msg)) return;

    // Initialize all rows as "pending" so the UI shows spinners immediately.
    const initial = {};
    rows.forEach((r) => { initial[r.email] = 'pending'; });
    setRecoverAllResults(initial);
    setRecoverAllRunning(true);

    const runOne = async (row) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${encodeURIComponent(row.email)}/generate-blueprint`,
          { method: 'POST', credentials: 'include' },
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          setRecoverAllResults((prev) => ({ ...prev, [row.email]: 'error' }));
          return { email: row.email, ok: false, detail: body.detail || `HTTP ${res.status}` };
        }
        const action = body.action || 'unknown';
        const bucket =
          action === 'queued' ? 'queued'
          : action === 'skipped_already_done' ? 'skipped'
          : action === 'skipped_already_generating' ? 'skipped'
          : 'queued';
        setRecoverAllResults((prev) => ({ ...prev, [row.email]: bucket }));
        return { email: row.email, ok: true, action };
      } catch (e) {
        setRecoverAllResults((prev) => ({ ...prev, [row.email]: 'error' }));
        return { email: row.email, ok: false, detail: e.message };
      }
    };

    const settled = await Promise.allSettled(rows.map(runOne));
    const queuedCount = settled.filter(
      (s) => s.status === 'fulfilled' && s.value.ok && s.value.action === 'queued'
    ).length;
    const skippedCount = settled.filter(
      (s) => s.status === 'fulfilled' && s.value.ok
        && (s.value.action === 'skipped_already_done' || s.value.action === 'skipped_already_generating')
    ).length;
    const errorCount = settled.filter(
      (s) => s.status === 'rejected' || (s.status === 'fulfilled' && !s.value.ok)
    ).length;

    if (errorCount === 0) {
      notifySuccess(
        `Recovered ${queuedCount} blueprint${queuedCount === 1 ? '' : 's'}`
        + (skippedCount ? ` (${skippedCount} already done - skipped)` : '')
        + '. Buyers will be emailed automatically once each Blueprint finishes.'
      );
    } else {
      notifyError(
        `Recover All finished with ${errorCount} error${errorCount === 1 ? '' : 's'}. `
        + `${queuedCount} queued, ${skippedCount} skipped. Check per-row status to retry failures.`
      );
    }
    setRecoverAllRunning(false);
    // Pull a fresh snapshot so any newly-queued rows drop off the at-risk list.
    fetchData();
  };

  const recoverAllProgress = (() => {
    if (!recoverAllRunning) return null;
    const total = Object.keys(recoverAllResults).length;
    const settled = Object.values(recoverAllResults).filter((s) => s !== 'pending').length;
    return { total, settled };
  })();

  if (loading) {
    return (
      <section
        className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
        data-testid="buyer-health-card-loading"
      >
        <p className="text-sm text-[#5A6B73]">Checking buyer health…</p>
      </section>
    );
  }

  const atRisk = data?.at_risk || [];
  const checkedAt = data?.checked_at ? new Date(data.checked_at).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : '';

  if (atRisk.length === 0) {
    return (
      <section
        className="bg-emerald-50 border border-emerald-200 p-5 flex items-center justify-between"
        data-testid="buyer-health-card-healthy"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <p className="font-semibold text-emerald-900">All buyers healthy</p>
            <p className="text-xs text-emerald-800/80">
              No paying client is waiting on a stuck Blueprint. Last check: {checkedAt}
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="text-xs text-emerald-800 hover:text-emerald-900 underline"
          data-testid="buyer-health-refresh-btn"
        >
          Re-check
        </button>
      </section>
    );
  }

  const tierStyles = {
    blueprint_only: 'bg-[#E8A830]/20 text-[#8C5B12] border-[#E8A830]/40',
    essentials:     'bg-[#2CA8A0]/20 text-[#1A5C6E] border-[#2CA8A0]/40',
    guided:         'bg-[#1A5C6E]/15 text-[#0A1F2E] border-[#1A5C6E]/40',
  };

  return (
    <section
      className="bg-red-50 border border-red-200 p-5"
      data-testid="buyer-health-card-at-risk"
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-red-700" />
          </div>
          <div>
            <p className="font-semibold text-red-900" data-testid="buyer-health-headline">
              {atRisk.length} paying client{atRisk.length === 1 ? '' : 's'} waiting on a Blueprint
            </p>
            <p className="text-xs text-red-800/80">
              These buyers paid more than 1 hour ago but their Blueprint is still missing or stuck. Last check: {checkedAt}
            </p>
            {recoverAllProgress && (
              <p className="text-xs text-red-900 font-semibold mt-1" data-testid="buyer-health-progress">
                Recovering {recoverAllProgress.settled} of {recoverAllProgress.total}…
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRecoverAll}
            disabled={recoverAllRunning || atRisk.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-700 text-white text-sm font-semibold hover:bg-red-800 disabled:opacity-50 transition rounded-sm"
            data-testid="buyer-health-recover-all-btn"
            title={`Queue Blueprint generation for all ${atRisk.length} stuck buyers in parallel`}
          >
            <Sparkles className={`w-4 h-4 ${recoverAllRunning ? 'animate-pulse' : ''}`} />
            {recoverAllRunning ? 'Recovering…' : `Recover All (${atRisk.length})`}
          </button>
          <button
            onClick={fetchData}
            className="text-xs text-red-800 hover:text-red-900 underline"
            data-testid="buyer-health-refresh-btn"
          >
            Re-check
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-red-900/70 border-b border-red-200">
              <th className="py-2 pr-4">Client</th>
              <th className="py-2 pr-4">Tier</th>
              <th className="py-2 pr-4">Purchased</th>
              <th className="py-2 pr-4">Blueprint</th>
              <th className="py-2 pr-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {atRisk.map((row) => {
              const tierCls = tierStyles[row.tier] || 'bg-[#F4ECD8] text-[#0A1F2E]/70 border-[#EFE6CF]';
              const purchasedStr = row.purchase_at
                ? new Date(row.purchase_at).toLocaleString('en-US', { timeZone: 'America/Chicago' })
                : '-';
              const isGenerating = generatingFor === row.email;
              const recoverStatus = recoverAllResults[row.email]; // undefined | 'pending' | 'queued' | 'skipped' | 'error'
              return (
                <tr
                  key={row.email}
                  className="border-b border-red-100 last:border-0"
                  data-testid={`buyer-health-row-${row.email}`}
                >
                  <td className="py-3 pr-4">
                    <p className="font-medium text-[#0A1F2E]">{row.name}</p>
                    <p className="text-xs text-[#5A6B73]">{row.email}</p>
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`inline-block text-[11px] px-2 py-0.5 rounded font-medium border ${tierCls}`}>
                      {row.tier}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-xs text-[#0A1F2E]">
                    {purchasedStr}
                    <p className="text-[11px] text-red-700">{row.hours_since_purchase}h ago</p>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="inline-block text-[11px] px-2 py-0.5 rounded bg-red-100 text-red-800 font-medium">
                      {row.blueprint_status}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-right">
                    {recoverStatus ? (
                      <span
                        className={
                          'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm '
                          + (recoverStatus === 'pending' ? 'bg-amber-100 text-amber-800' : '')
                          + (recoverStatus === 'queued' ? 'bg-emerald-100 text-emerald-800' : '')
                          + (recoverStatus === 'skipped' ? 'bg-[#F4ECD8] text-[#5A6B73]' : '')
                          + (recoverStatus === 'error' ? 'bg-red-200 text-red-900' : '')
                        }
                        data-testid={`buyer-health-row-status-${row.email}`}
                      >
                        {recoverStatus === 'pending' && (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        )}
                        {recoverStatus === 'queued' && <CheckCircle className="w-3.5 h-3.5" />}
                        {recoverStatus === 'skipped' && <CheckCircle className="w-3.5 h-3.5" />}
                        {recoverStatus === 'error' && <AlertCircle className="w-3.5 h-3.5" />}
                        {recoverStatus === 'pending' && 'Queueing…'}
                        {recoverStatus === 'queued' && 'Queued'}
                        {recoverStatus === 'skipped' && 'Skipped'}
                        {recoverStatus === 'error' && 'Error - retry'}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => handleGenerate(row.email)}
                          disabled={isGenerating || recoverAllRunning || dismissingFor === row.email}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1A5C6E] text-[#F9F6EF] text-xs font-semibold hover:bg-[#0A1F2E] disabled:opacity-50 transition"
                          data-testid={`buyer-health-generate-btn-${row.email}`}
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${isGenerating ? 'animate-spin' : ''}`} />
                          {isGenerating ? 'Generating…' : 'Generate Blueprint'}
                        </button>
                        <button
                          onClick={() => handleDismiss(row)}
                          disabled={isGenerating || recoverAllRunning || dismissingFor === row.email}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-800 text-xs font-semibold hover:bg-red-100 disabled:opacity-50 transition"
                          data-testid={`buyer-health-dismiss-btn-${row.email}`}
                          title="Comp / friends & family - remove from alert"
                        >
                          {dismissingFor === row.email ? 'Dismissing…' : 'Dismiss'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};



// iter 180 - Top 5 destinations this week. Renders a compact horizontal
// bar chart directly below the Overview headline cards. Empty state:
// "No destination data yet this week" so it never disappears silently.
const TopDestinationsCard = ({ rows }) => {
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const maxN = hasRows ? Math.max(1, ...rows.map((r) => r.count || 0)) : 1;
  return (
    <div
      className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
      data-testid="overview-top-destinations"
    >
      <div className="flex items-center justify-between mb-1">
        <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">
          Top Destinations · Last 7 days
        </p>
        <span className="text-[10px] text-[#5A6B73] font-mono uppercase tracking-wider">
          Unified · all sources
        </span>
      </div>
      <h3 className="font-semibold text-[#0A1F2E] mb-4">Where demand is going</h3>
      {!hasRows ? (
        <p
          className="text-sm text-[#5A6B73] italic"
          data-testid="overview-top-destinations-empty"
        >
          No destination data yet this week.
        </p>
      ) : (
        <ul className="space-y-3" data-testid="overview-top-destinations-list">
          {rows.map((r, idx) => {
            const pct = Math.max(4, Math.round(((r.count || 0) / maxN) * 100));
            return (
              <li
                key={r.city}
                className="grid grid-cols-[24px_minmax(0,1fr)_44px] items-center gap-3"
                data-testid={`overview-top-destinations-row-${idx}`}
              >
                <span className="font-mono text-[10px] tracking-[0.14em] text-[#5A6B73]">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm text-[#0A1F2E] truncate pr-2">
                      {r.city}
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#F4ECD8] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#2CA8A0] rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <span
                  className="text-right text-sm font-semibold text-[#0A1F2E] tabular-nums"
                  data-testid={`overview-top-destinations-count-${idx}`}
                >
                  {r.count}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const OverviewTab = ({ stats }) => (
  <div className="space-y-6">
    <PartnerApplicationsBadge />
    {/* iter 169 - Buyer Health Check: stuck/missing Blueprints for paying
        clients > 1h post-purchase. Top of the Overview so it's the first
        thing an admin sees when something is wrong. */}
    <BuyerHealthCheckCard />
    <FunnelSnapshotCard />
    {/* iter 144g - compact collapsible cards below the funnel. */}
    <PaidConversionCard />
    {/* iter 155 - Quiz Funnel rollup card. */}
    <QuizFunnelCard />
    {/* iter 165 - Calculator V2 dropoff funnel. */}
    <CalculatorDropoffCard />
    <PromoCodeUsageCard />
    <PendingPartnerImagesCard />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[
        { icon: Users, value: stats?.stats?.clients || 0, label: 'Total Clients', deltaKey: 'clients', testId: 'overview-stat-clients' },
        { icon: Building2, value: stats?.stats?.listings || 0, label: 'Total Listings', deltaKey: null, testId: 'overview-stat-listings' },
        { icon: MessageSquare, value: stats?.stats?.leads || 0, label: 'Total Leads', deltaKey: 'leads', testId: 'overview-stat-leads' },
        { icon: CreditCard, value: stats?.stats?.payments || 0, label: 'Paid Orders', deltaKey: 'paid_orders', testId: 'overview-stat-paid-orders' }
      ].map((stat, idx) => {
        const delta = stat.deltaKey ? stats?.stats?.deltas?.[stat.deltaKey] : null;
        const today = delta?.today ?? 0;
        const week = delta?.week ?? 0;
        return (
          <div
            key={idx}
            className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
            data-testid={stat.testId}
          >
            <stat.icon className="w-8 h-8 text-[#2CA8A0] mb-2" />
            <p className="text-3xl font-bold text-[#0A1F2E]" data-testid={`${stat.testId}-value`}>{stat.value}</p>
            <p className="text-sm text-[#5A6B73]">{stat.label}</p>
            {/* iter 178 - call out comped/promo accounts under Paid Orders so
                the headline number represents ONLY real Stripe revenue. */}
            {stat.deltaKey === 'paid_orders' && (stats?.stats?.comped_accounts ?? 0) > 0 && (
              <p
                className="text-[11px] text-[#5A6B73] mt-1"
                data-testid="overview-stat-paid-orders-comped"
                title="Users with a paid role but no real Stripe payment (FIRST100FREE, manual comp, etc.)"
              >
                +{stats.stats.comped_accounts} comped
              </p>
            )}
            {/* iter 178.b - Real revenue MTD. Sums Stripe amount for the
                current calendar month; same source-of-truth as Paid Orders. */}
            {stat.deltaKey === 'paid_orders' && stats?.stats?.revenue_mtd_dollars != null && (
              <p
                className="text-xs font-semibold text-[#1F7A6F] mt-1"
                data-testid="overview-stat-revenue-mtd"
                title="Sum of real Stripe payments this calendar month (America/Chicago)"
              >
                ${stats.stats.revenue_mtd_dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2})}
                {' '}
                <span className="font-normal text-[#5A6B73]">this month</span>
              </p>
            )}
            {stat.deltaKey && (
              <div
                className="mt-2 flex items-center gap-2 text-xs font-medium"
                data-testid={`${stat.testId}-delta`}
              >
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${today > 0 ? 'bg-[#E6F4F1] text-[#1F7A6F]' : 'bg-[#F4ECD8] text-[#5A6B73]'}`}
                  data-testid={`${stat.testId}-delta-today`}
                >
                  {today > 0 ? '▲' : ''} +{today} today
                </span>
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${week > 0 ? 'bg-[#E6F4F1] text-[#1F7A6F]' : 'bg-[#F4ECD8] text-[#5A6B73]'}`}
                  data-testid={`${stat.testId}-delta-week`}
                >
                  +{week} this week
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>

    {/* iter 180 - Top 5 destinations this week. Unified across every lead
        source (matches the /admin/leads table). Sits directly below the
        headline counters so demand concentration is visible at a glance. */}
    <TopDestinationsCard rows={stats?.stats?.top_destinations_week || []} />

    <WelcomeSequenceHealth />
    <EmailPerformanceCard />
    <ToolLeadsCard />
    <PartnerNetworkCard />
    <ConversionPathCard />

    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-6">
        <h3 className="font-semibold text-[#0A1F2E] mb-4">Recent Leads</h3>
        {stats?.recent_leads?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_leads.map((lead, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-[#F4ECD8] last:border-0">
                <div>
                  <p className="text-sm font-medium text-[#0A1F2E]">{lead.name}</p>
                  <p className="text-xs text-[#5A6B73]">{lead.email}</p>
                </div>
                <span className="text-xs px-2 py-1 bg-[#F4ECD8] text-[#1A5C6E] rounded">{lead.inquiry_type || 'General'}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[#5A6B73]">No recent leads</p>}
      </div>

      <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-6">
        <h3 className="font-semibold text-[#0A1F2E] mb-4">Recent Clients</h3>
        {stats?.recent_clients?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_clients.map((client, idx) => (
              <div key={idx} className="flex items-center gap-3 py-2 border-b border-[#F4ECD8] last:border-0">
                {client.picture ? (
                  <img src={client.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#2CA8A0] flex items-center justify-center text-[#F9F6EF] text-sm font-semibold">
                    {client.name?.charAt(0) || '?'}
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-[#0A1F2E]">{client.name}</p>
                  <p className="text-xs text-[#5A6B73]">{client.email}</p>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[#5A6B73]">No clients yet</p>}
      </div>
    </div>

    {/* Recent Uploads - live Cloudinary feed */}
    <RecentUploads />

    {/* News refresh trigger */}
    <NewsRefreshPanel />

    {/* System alerts (canary, broadcast failures, etc.) */}
    <AdminAlertsPanel />

    {/* iter 187.b - Canonical vs raw user count guard */}
    <CanonicalCountTile />

    {/* iter 187.g - Deploy bundle marker (before/after line for combined-iteration deploys) */}
    <DeployBundleTile />

    {/* Weekly Mover Spotlight admin control */}
    <AdminSpotlightPanel />

    {/* News article editor (list + edit + publish toggle) */}
    <AdminNewsEditor />
  </div>
);

// ============== NEWS REFRESH PANEL ==============
const NewsRefreshPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null); // {run_id, elapsed_s}
  const pollRef = React.useRef(null);

  const fetchStatus = async () => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    const r = await fetch(`${apiUrl}/api/admin/news/status`, { credentials: 'include' });
    if (r.ok) {
      const s = await r.json();
      setStatus(s);
      return s;
    }
    return null;
  };

  useEffect(() => {
    fetchStatus().catch(() => {});
    // Resume polling on mount if a run is still in flight (page reload mid-run).
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [result]);

  const pollUntilDone = (runId, startedIso) => {
    // Poll /status every 8s until the running record flips to completed/failed.
    setBusy(true);
    setProgress({ runId, startedIso, elapsed_s: 0 });
    const startMs = Date.now();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await fetchStatus();
        const elapsed = Math.round((Date.now() - startMs) / 1000);
        setProgress((p) => (p ? { ...p, elapsed_s: elapsed } : p));
        const last = s?.last_refresh;
        const inFlight = s?.in_flight;
        // Done when this run_id appears in last_refresh with a terminal status,
        // or when no in_flight remains and last_refresh started at/after ours.
        if (last && last.id === runId && last.status && last.status !== 'running') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (last.status === 'completed') {
            setResult({ ok: true, count: last.count || 0, articles: [] });
          } else {
            setResult({ ok: false, error: last.error || 'Generation failed. Check backend logs.' });
          }
          setProgress(null);
          setBusy(false);
        } else if (!inFlight && elapsed > 20) {
          // Safety net if the log doc got compacted
          clearInterval(pollRef.current);
          pollRef.current = null;
          setResult({ ok: true, count: last?.count || 0, articles: [] });
          setProgress(null);
          setBusy(false);
        } else if (elapsed > 300) {
          // Hard stop after 5 minutes
          clearInterval(pollRef.current);
          pollRef.current = null;
          setResult({ ok: false, error: 'Timed out waiting for generation. Check /admin/news/status.' });
          setProgress(null);
          setBusy(false);
        }
      } catch (_) { /* keep polling */ }
    }, 8000);
  };

  const startRefresh = async (force) => {
    if (busy) return;
    const msg = force
      ? 'Force refresh? Bypasses the 6-hour throttle. ~$0.30 in LLM credits.'
      : 'Trigger Claude to generate 6 fresh news articles? This costs ~$0.30 in LLM credits.';
    if (!window.confirm(msg)) return;
    setBusy(true);
    setResult(null);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const r = await fetch(`${apiUrl}/api/admin/news/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: 6, force }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 202 || data.status === 'queued' || data.status === 'already_running') {
        pollUntilDone(data.run_id, data.started_at);
        return; // pollUntilDone controls busy state
      }
      if (r.ok) {
        // Legacy sync-response path (kept for safety if backend rolls back).
        setResult({ ok: true, count: data.count, articles: data.inserted });
      } else if (r.status === 429) {
        setResult({ ok: false, status: 429, error: data.detail || 'Already refreshed recently. Try again in a few hours, or use Force Refresh.' });
      } else if (r.status === 401) {
        setResult({ ok: false, status: 401, error: 'Admin session expired. Log out and back in.' });
      } else {
        setResult({ ok: false, status: r.status, error: data.detail || `HTTP ${r.status}` });
      }
    } catch (e) {
      setResult({ ok: false, error: String(e.message || e) });
    } finally {
      // Only clear busy if we didn't start polling.
      if (!pollRef.current) setBusy(false);
    }
  };

  const triggerRefresh = () => startRefresh(false);
  const forceRefresh = () => startRefresh(true);

  return (
    <div
      className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
      data-testid="admin-news-refresh"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-semibold text-[#0A1F2E]">Relo News - Refresh</h3>
          <p className="text-xs text-[#5A6B73] mt-1">
            Auto-refresh runs every 30 days. Trigger a manual cycle here.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={triggerRefresh}
            disabled={busy}
            className="font-mono uppercase text-[11px] tracking-[0.12em] font-semibold bg-[#E8A830] hover:brightness-95 disabled:opacity-50 text-[#0A1F2E] px-5 py-2.5 rounded-full transition"
            data-testid="admin-news-refresh-btn"
          >
            {busy ? (progress ? `Generating… ${progress.elapsed_s}s` : 'Generating…') : 'Refresh Now'}
          </button>
          <button
            type="button"
            onClick={forceRefresh}
            disabled={busy}
            className="font-mono uppercase text-[11px] tracking-[0.12em] font-semibold border border-[#0A1F2E]/20 hover:bg-[#0A1F2E] hover:text-[#F9F6EF] disabled:opacity-50 text-[#0A1F2E] px-4 py-2.5 rounded-full transition"
            data-testid="admin-news-force-refresh-btn"
            title="Bypass the 6h throttle"
          >
            Force
          </button>
        </div>
      </div>

      {progress && (
        <div
          className="mb-3 p-3 border border-[#2CA8A0]/30 bg-[#E6F4F1] rounded text-sm text-[#0A1F2E]"
          data-testid="admin-news-progress"
          role="status"
          aria-live="polite"
        >
          <span className="font-mono uppercase text-[10px] tracking-wider mr-2">In progress</span>
          Claude is drafting 6 articles - typically 60–120s. Elapsed: <b>{progress.elapsed_s}s</b>.
        </div>
      )}

      {status && (
        <div className="flex flex-wrap items-center gap-6 text-sm text-[#0A1F2E]/70 mb-3">
          <span><b className="text-[#0A1F2E]">{status.published}</b> published</span>
          <span><b className="text-[#0A1F2E]">{status.drafts}</b> drafts</span>
          <span>
            Last refresh:{' '}
            <b className="text-[#0A1F2E]">
              {status.last_refresh?.ran_at
                ? new Date(status.last_refresh.ran_at).toLocaleString('en-US', { timeZone: 'America/Chicago' })
                : 'never'}
            </b>
          </span>
        </div>
      )}

      {result?.ok && (
        <div className="mt-4 p-3 bg-[#F4ECD8] rounded text-sm text-[#0A1F2E]" data-testid="admin-news-result">
          ✓ Generated <b>{result.count}</b> new articles.
          {result.articles && result.articles.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {result.articles.map((a) => (
                <li key={a.slug}>
                  [{a.category_label}] {a.title}
                </li>
              ))}
            </ul>
          )}
          {(!result.articles || result.articles.length === 0) && (
            <div className="mt-1 text-xs text-[#5A6B73]">Reload the News Editor below to see the new articles.</div>
          )}
        </div>
      )}
      {result?.ok === false && (
        <div
          className={`mt-4 p-3 border rounded text-sm ${
            result.status === 429
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
          data-testid="admin-news-error"
          role="alert"
        >
          <div className="flex items-start gap-2">
            <span className="font-semibold uppercase text-[10px] font-mono tracking-wider mt-0.5">
              {result.status === 429 ? 'Throttled' : 'Error'}
              {result.status ? ` · ${result.status}` : ''}
            </span>
            <span className="flex-1">
              {typeof result.error === 'string' ? result.error : JSON.stringify(result.error)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== iter 160 - TEST USER CLEANUP PANEL ==============
const TestUserCleanupPanel = ({ onRefresh }) => {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/test-cleanup/preview`,
        { credentials: 'include' },
      );
      const d = await r.json();
      setPreview(d);
      setOpen(true);
    } catch {
      setPreview({ count: 0, users: [], error: true });
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const runExecute = async () => {
    if (!preview || preview.count === 0) return;
    const ok = window.confirm(
      `Permanently delete ${preview.count} test user${preview.count === 1 ? '' : 's'}?\n\n` +
      'Pattern match: ' + (preview.patterns || []).join(', ') +
      '\n\nThis cannot be undone.'
    );
    if (!ok) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/test-cleanup/execute`,
        { method: 'POST', credentials: 'include' },
      );
      const d = await r.json();
      alert(`Deleted ${d.deleted} test users.`);
      setPreview(null);
      setOpen(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Delete failed: ' + (e.message || 'unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" data-testid="test-cleanup-panel">
      <button
        onClick={runPreview}
        disabled={loading}
        data-testid="test-cleanup-preview-btn"
        className="text-xs font-mono uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border border-red-200 text-red-700 bg-red-50/40 hover:bg-red-50 disabled:opacity-50"
      >
        {loading ? 'Loading…' : 'Test users · Preview'}
      </button>

      {open && preview && (
        <div
          className="absolute right-0 mt-2 w-[420px] max-w-[90vw] bg-white border border-red-200 rounded-xl shadow-xl p-4 z-30"
          data-testid="test-cleanup-modal"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-[#0A1F2E]">
                Test user dry-run · {preview.count} match{preview.count === 1 ? '' : 'es'}
              </p>
              <p className="text-[11px] text-[#5A6B73] mt-0.5">
                Patterns: {(preview.patterns || []).join(', ')}
              </p>
            </div>
            <button
              onClick={() => { setOpen(false); setPreview(null); }}
              className="text-[#5A6B73] hover:text-[#0A1F2E] text-lg leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {preview.count === 0 ? (
            <p className="text-sm text-[#5A6B73] italic py-3" data-testid="test-cleanup-empty">
              No test users found. Nothing to delete.
            </p>
          ) : (
            <>
              <ul className="max-h-60 overflow-y-auto space-y-1 mb-3 border border-[#F4ECD8] rounded p-2 bg-[#F9F6EF]/40">
                {preview.users.slice(0, 50).map((u) => (
                  <li
                    key={u.id || u.email}
                    className="text-xs text-[#0A1F2E] flex items-center justify-between gap-2"
                    data-testid={`test-cleanup-row-${u.id || u.email}`}
                  >
                    <span className="truncate">{u.email}</span>
                    <span className="text-[10px] text-[#5A6B73] uppercase tracking-wider shrink-0">
                      {u.role || 'none'}
                    </span>
                  </li>
                ))}
                {preview.count > 50 && (
                  <li className="text-[11px] text-[#5A6B73] italic pt-1">
                    … and {preview.count - 50} more
                  </li>
                )}
              </ul>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setOpen(false); setPreview(null); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#EFE6CF] text-[#0A1F2E]"
                >
                  Cancel
                </button>
                <button
                  onClick={runExecute}
                  disabled={loading}
                  data-testid="test-cleanup-execute-btn"
                  className="text-xs px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? 'Deleting…' : `Delete ${preview.count}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============== iter 167 - GHOST USER CLEANUP PANEL ==============
const GhostCleanupPanel = ({ onRefresh }) => {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/ghost-cleanup/preview`,
        { credentials: 'include' },
      );
      const d = await r.json();
      setPreview(d);
      setOpen(true);
    } catch {
      setPreview({ count: 0, users: [], error: true });
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const runExecute = async () => {
    if (!preview || preview.count === 0) return;
    const ok = window.confirm(
      `Archive + permanently delete ${preview.count} ghost account${preview.count === 1 ? '' : 's'}?\n\n` +
      'Each will be copied to users_ghost_archive first.\n\n' +
      'This cannot be undone.'
    );
    if (!ok) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/ghost-cleanup/execute`,
        { method: 'POST', credentials: 'include' },
      );
      const d = await r.json();
      alert(`Archived ${d.archived} and deleted ${d.deleted} ghost accounts.`);
      setPreview(null);
      setOpen(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Cleanup failed: ' + (e.message || 'unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" data-testid="ghost-cleanup-panel">
      <button
        onClick={runPreview}
        disabled={loading}
        data-testid="ghost-cleanup-preview-btn"
        className="text-xs font-mono uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border border-red-300 text-red-800 bg-red-50/60 hover:bg-red-100 disabled:opacity-50"
      >
        {loading ? 'Loading…' : 'Ghost cleanup · Preview'}
      </button>

      {open && preview && (
        <div
          className="absolute right-0 mt-2 w-[460px] max-w-[92vw] bg-white border border-red-300 rounded-xl shadow-xl p-4 z-30"
          data-testid="ghost-cleanup-modal"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-[#0A1F2E]">
                Ghost-account dry-run · {preview.count} match{preview.count === 1 ? '' : 'es'}
              </p>
              <p className="text-[11px] text-[#5A6B73] mt-0.5">
                {preview.filter_summary || 'Auto-created free_trial rows with no last login.'}
              </p>
            </div>
            <button
              onClick={() => { setOpen(false); setPreview(null); }}
              className="text-[#5A6B73] hover:text-[#0A1F2E] text-lg leading-none"
              aria-label="Close"
            >×</button>
          </div>

          {preview.count === 0 ? (
            <p className="text-sm text-[#5A6B73] italic py-3" data-testid="ghost-cleanup-empty">
              No ghost accounts found. Nothing to clean up.
            </p>
          ) : (
            <>
              <ul className="max-h-72 overflow-y-auto space-y-1 mb-3 border border-[#F4ECD8] rounded p-2 bg-[#F9F6EF]/40">
                {preview.users.slice(0, 100).map((u) => (
                  <li
                    key={u.id || u.email}
                    className="text-xs text-[#0A1F2E] flex items-center justify-between gap-2"
                    data-testid={`ghost-cleanup-row-${u.id || u.email}`}
                  >
                    <span className="truncate flex-1">{u.email}</span>
                    <span className="text-[10px] text-[#5A6B73] uppercase tracking-wider shrink-0">
                      {u.signup_method || 'no-pw'}
                    </span>
                    <span className="text-[10px] text-[#5A6B73] shrink-0">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : '-'}
                    </span>
                  </li>
                ))}
                {preview.count > 100 && (
                  <li className="text-[11px] text-[#5A6B73] italic pt-1">
                    … and {preview.count - 100} more
                  </li>
                )}
              </ul>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setOpen(false); setPreview(null); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#EFE6CF] text-[#0A1F2E]"
                >
                  Cancel
                </button>
                <button
                  onClick={runExecute}
                  disabled={loading}
                  data-testid="ghost-cleanup-execute-btn"
                  className="text-xs px-3 py-1.5 rounded-full bg-red-700 text-white hover:bg-red-800 disabled:opacity-50"
                >
                  {loading ? 'Cleaning…' : `Archive + delete ${preview.count}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};


// ============== CLIENTS TAB ==============
const ClientsTab = ({ clients, onSelectClient, onRefresh, onOptimisticRemove, page = 1, setPage = () => {}, meta = { total: 0, total_pages: 0, per_page: 25 } }) => {
  const [busyId, setBusyId] = React.useState(null);

  const apiCall = async (method, path, body) => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
    return data;
  };

  const toggleBlock = async (client) => {
    const willBlock = !client.blocked;
    const reason = willBlock
      ? window.prompt(`Block ${client.email}? Enter reason (shown to the user on login):`, 'terms violation')
      : null;
    if (willBlock && (reason === null || reason.trim() === '')) return;
    if (!willBlock && !window.confirm(`Unblock ${client.email}?`)) return;
    setBusyId(client.id);
    try {
      await apiCall('POST', `/api/admin/members/${client.id}/block`, {
        blocked: willBlock,
        reason: reason || '',
      });
      onRefresh?.();
    } catch (e) {
      notifyError(`Block toggle failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const editTier = async (client) => {
    const newTier = window.prompt(
      `Set new tier for ${client.email}. Options: blueprint_only, essentials, guided, community, connected, free_trial, expired_trial, admin`,
      client.tier || client.subscription_tier || client.role || ''
    );
    if (!newTier || newTier === (client.tier || client.subscription_tier)) return;
    setBusyId(client.id);
    try {
      await apiCall('PUT', `/api/admin/members/${client.id}`, {
        subscription_tier: newTier.trim().toLowerCase(),
        role: newTier.trim().toLowerCase(),
      });
      onRefresh?.();
    } catch (e) {
      notifyError(`Tier edit failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const deleteMember = async (client) => {
    // iter 96: simplify + harden the delete confirmation flow.
    //
    // Previous flow chained 3 modal dialogs (confirm → confirm → prompt)
    // and used strict equality (`conf !== 'yes'`). Users reported that
    // "the confirmation completes but nothing happens" - almost certainly
    // because:
    //   1. The strict check rejected "Yes", "YES", or "yes " (trailing
    //      space) silently with no toast/error.
    //   2. A successful delete had no positive feedback (no toast), so
    //      users couldn't tell whether the action actually fired.
    //   3. Cancelling any of the 3 dialogs returned silently, masking
    //      bugs from the cancel path too.
    //
    // New flow: a single typed confirm (`window.prompt`) with
    // case-insensitive + trimmed comparison; a success toast on 2xx;
    // optimistic removal from the list before the refetch so the row
    // disappears immediately; an error toast on failure.
    const typed = window.prompt(
      `Type DELETE to permanently remove ${client.email}.\n\nThis cascades to all their data and cannot be undone.`,
      '',
    );
    if (typed === null) return; // user cancelled prompt
    if (typed.trim().toUpperCase() !== 'DELETE') {
      notifyError('Deletion cancelled - confirmation phrase did not match.');
      return;
    }

    setBusyId(client.id);
    try {
      await apiCall('DELETE', `/api/admin/members/${client.id}`);
      // Optimistic UX: hide the row immediately so the admin sees the
      // action took effect. The full refetch below catches any cascade
      // counts and ensures we converge with the server.
      onOptimisticRemove?.(client.id);
      notifySuccess(`Deleted ${client.email}.`);
      // Re-fetch to converge counts/badges (selected client cleared too).
      onRefresh?.();
    } catch (e) {
      notifyError(`Delete failed: ${e.message || 'Unknown error'}`);
    } finally {
      setBusyId(null);
    }
  };

  // Status badge helper
  const getBlueprintStatusBadge = (status) => {
    const styles = {
      done: 'bg-green-500/20 text-green-700',
      generating: 'bg-yellow-500/20 text-yellow-700',
      pending: 'bg-blue-500/20 text-blue-700',
      error: 'bg-red-500/20 text-red-700',
      none: 'bg-[#F4ECD8] text-[#5A6B73]',
    };
    const labels = {
      done: 'Ready',
      generating: 'Generating',
      pending: 'Pending',
      error: 'Error',
      none: 'None',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded ${styles[status] || styles.none}`}>
        {labels[status] || status || 'None'}
      </span>
    );
  };

  const formatLastLogin = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    return date.toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
  };

  // iter 160 - Fix 3: tier badge color differentiation. Paid tiers get
  // colored badges; free trial gets a clearly labeled neutral badge so
  // admins can scan the list and tell paying members apart at a glance.
  const tierBadgeFor = (client, testidPrefix = 'tier-badge') => {
    const raw = (client.tier || client.subscription_tier || client.role || '').toLowerCase();
    const label = client.tier_name || client.tier || client.subscription_tier || client.role || '-';
    const styles = {
      blueprint_only: 'bg-[#E8A830]/20 text-[#8C5B12] border border-[#E8A830]/40',
      essentials:     'bg-[#2CA8A0]/20 text-[#1A5C6E] border border-[#2CA8A0]/40',
      guided:         'bg-[#1A5C6E]/15 text-[#0A1F2E] border border-[#1A5C6E]/40',
      community:      'bg-[#7C3AED]/15 text-[#5B21B6] border border-[#7C3AED]/30',
      connected:      'bg-[#7C3AED]/20 text-[#4C1D95] border border-[#7C3AED]/40',
      free_trial:     'bg-[#F4ECD8] text-[#0A1F2E]/70 border border-[#EFE6CF]',
      expired_trial:  'bg-red-50 text-red-700 border border-red-200',
    };
    const cls = styles[raw] || 'bg-[#F4ECD8] text-[#0A1F2E]/70 border border-[#EFE6CF]';
    const displayLabel = raw === 'free_trial' ? 'Free Trial' :
                         raw === 'expired_trial' ? 'Expired Trial' :
                         label;
    return (
      <span
        className={`text-xs px-2 py-0.5 rounded font-medium ${cls}`}
        data-testid={`${testidPrefix}-${client.id}`}
      >
        {displayLabel}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#5A6B73]" data-testid="admin-clients-count">
          {clients.length} registered clients
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <GhostCleanupPanel onRefresh={onRefresh} />
          <TestUserCleanupPanel onRefresh={onRefresh} />
        </div>
      </div>
      {clients.length > 0 ? (
        <>
          {/* Desktop table view */}
          <div className="hidden md:block bg-[#FFFFFF] border border-[#F4ECD8] overflow-x-auto">
            <table className="w-full min-w-[960px]" data-testid="admin-clients-table">
              <thead className="bg-[#F9F6EF]">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Tier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Destination</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Signup</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Trial</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Blueprint</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Last Login</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => {
                  const isBusy = busyId === client.id;
                  return (
                  <tr key={client.id || client.user_id} className={`border-t border-[#F4ECD8] hover:bg-[#FFFFFF] ${client.blocked ? 'bg-red-50/40' : ''}`} data-testid={`admin-client-row-${client.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#2CA8A0] flex items-center justify-center text-[#F9F6EF] text-sm font-semibold">
                          {(client.name || client.first_name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#0A1F2E] flex items-center gap-2">
                            {client.name || `${client.first_name} ${client.last_name}`.trim() || 'Unknown'}
                            {client.blocked && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded uppercase tracking-wide" data-testid={`blocked-badge-${client.id}`}>Blocked</span>
                            )}
                          </p>
                          <p className="text-xs text-[#5A6B73]">{client.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {tierBadgeFor(client, 'tier-badge')}
                      {client.is_internal && (
                        <span
                          className="ml-1 text-[9px] px-1 py-0.5 bg-purple-100 text-purple-800 rounded uppercase tracking-wide"
                          data-testid={`client-internal-badge-${client.id}`}
                          title="Internal / founder test account - excluded from revenue metrics"
                        >
                          Internal
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#0A1F2E] whitespace-nowrap" data-testid={`client-destination-${client.id}`}>
                      {client.destination_city || <span className="text-xs text-[#5A6B73]/60">-</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#1A5C6E]">
                      {client.signup_date ? new Date(client.signup_date).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {client.trial_status ? (
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          client.trial_status === 'active' ? 'bg-green-100 text-green-700' :
                          client.trial_status === 'expired' ? 'bg-red-100 text-red-700' :
                          'bg-[#F4ECD8] text-[#5A6B73]'
                        }`}>{client.trial_status}</span>
                      ) : <span className="text-xs text-[#5A6B73]">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      {getBlueprintStatusBadge(client.blueprint_status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#1A5C6E]">
                      {formatLastLogin(client.last_login)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => onSelectClient(client)} title="View" className="p-1.5 text-[#2CA8A0] hover:bg-[#2CA8A0]/10 rounded" data-testid={`view-client-${client.id}`}>
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => editTier(client)} disabled={isBusy} title="Edit tier" className="p-1.5 text-[#1A5C6E] hover:bg-[#1A5C6E]/10 rounded disabled:opacity-40" data-testid={`edit-tier-${client.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => toggleBlock(client)} disabled={isBusy} title={client.blocked ? 'Unblock' : 'Block'} className={`p-1.5 rounded disabled:opacity-40 ${client.blocked ? 'text-green-600 hover:bg-green-100' : 'text-orange-600 hover:bg-orange-100'}`} data-testid={`toggle-block-${client.id}`}>
                          {client.blocked ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </button>
                        {/* iter 142d - protected admins (francisco/rodrigo) never get a delete button. */}
                        {!isProtectedAdmin(client) && (
                          <button onClick={() => deleteMember(client)} disabled={isBusy} title="Delete" className="p-1.5 text-red-600 hover:bg-red-100 rounded disabled:opacity-40" data-testid={`delete-member-${client.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {clients.map((client) => {
              const isBusy = busyId === client.id;
              return (
              <div key={client.id || client.user_id} className={`bg-[#FFFFFF] border border-[#F4ECD8] p-4 rounded-lg ${client.blocked ? 'border-red-300 bg-red-50/40' : ''}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#2CA8A0] flex items-center justify-center text-[#F9F6EF] font-semibold">
                      {(client.name || client.first_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#0A1F2E] flex items-center gap-2">
                        {client.name || `${client.first_name} ${client.last_name}`.trim() || 'Unknown'}
                        {client.blocked && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded uppercase tracking-wide">Blocked</span>
                        )}
                      </p>
                      <p className="text-xs text-[#5A6B73] break-all">{client.email}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                  <div>
                    <span className="text-[#5A6B73]">Tier:</span>
                    <div className="mt-1">
                      {tierBadgeFor(client, 'mobile-tier-badge')}
                    </div>
                  </div>
                  <div>
                    <span className="text-[#5A6B73]">Signup:</span>
                    <div className="mt-1 text-[#1A5C6E]">{client.signup_date ? new Date(client.signup_date).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : '-'}</div>
                  </div>
                  <div>
                    <span className="text-[#5A6B73]">Trial:</span>
                    <div className="mt-1 text-[#1A5C6E]">{client.trial_status || '-'}</div>
                  </div>
                  <div>
                    <span className="text-[#5A6B73]">Blueprint:</span>
                    <div className="mt-1">{getBlueprintStatusBadge(client.blueprint_status)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1 pt-2 border-t border-[#F4ECD8]">
                  <button onClick={() => onSelectClient(client)} className="p-2 text-[#2CA8A0]" data-testid={`mobile-view-${client.id}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => editTier(client)} disabled={isBusy} className="p-2 text-[#1A5C6E] disabled:opacity-40">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => toggleBlock(client)} disabled={isBusy} className={`p-2 ${client.blocked ? 'text-green-600' : 'text-orange-600'} disabled:opacity-40`}>
                    {client.blocked ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                  </button>
                  {!isProtectedAdmin(client) && (
                    <button onClick={() => deleteMember(client)} disabled={isBusy} className="p-2 text-red-600 disabled:opacity-40" data-testid={`mobile-delete-${client.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-12 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-[#E0D6B8]" />
          <p className="text-[#5A6B73]">No clients registered yet</p>
        </div>
      )}
      {/* iter 175 - server-side pagination footer for Clients */}
      <PaginationFooter
        page={page}
        totalPages={meta.total_pages}
        total={meta.total}
        perPage={meta.per_page}
        onChange={setPage}
        testIdPrefix="clients"
      />
    </div>
  );
};

// ============== CLIENT DETAIL VIEW ==============
// iter 144g - Communications tab for ClientDetailView. Reads from the
// existing `email_logs` collection. Most-recent-first. Collapsible to
// keep the client detail page tidy.
const ClientCommunicationsSection = ({ clientId }) => {
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!clientId) return;
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${apiUrl}/api/admin/clients/${clientId}/email-logs?limit=100`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(typeof body?.detail === 'string' ? body.detail : `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((d) => setRows(d?.logs || []))
      .catch((e) => setErr(e.message || 'Failed'))
      .finally(() => setLoading(false));
  }, [clientId]);

  const fmt = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago'});
    } catch { return iso; }
  };

  return (
    <div className="bg-white border border-[#F4ECD8] rounded-lg" data-testid="client-communications">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
        data-testid="client-communications-toggle"
      >
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">Communications</p>
          <p className="text-sm text-[#5A6B73] mt-0.5">
            {loading ? 'Loading…' : `${rows.length} email${rows.length === 1 ? '' : 's'} sent · most recent first`}
          </p>
        </div>
        <span className="text-[#5A6B73] text-lg">{open ? '–' : '+'}</span>
      </button>

      {open && (
        <div className="px-5 pb-5">
          {loading && <p className="text-sm text-[#5A6B73]">Loading…</p>}
          {err && <p className="text-sm text-[#C0392B]" data-testid="client-communications-error">{err}</p>}
          {!loading && !err && rows.length === 0 && (
            <p className="text-sm text-[#5A6B73] italic" data-testid="client-communications-empty">
              No email log entries for this client yet.
            </p>
          )}
          {!loading && !err && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[#5A6B73]">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono uppercase text-[10px] tracking-wider">Date Sent</th>
                    <th className="text-left px-3 py-2 font-mono uppercase text-[10px] tracking-wider">Type</th>
                    <th className="text-left px-3 py-2 font-mono uppercase text-[10px] tracking-wider">Trigger</th>
                    <th className="text-left px-3 py-2 font-mono uppercase text-[10px] tracking-wider">Subject</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i} className="border-t border-[#F4ECD8]" data-testid={`client-comm-row-${i}`}>
                      <td className="px-3 py-2 text-[#5A6B73] text-xs whitespace-nowrap">{fmt(r.sent_at)}</td>
                      <td className="px-3 py-2 text-[#0A1F2E]">{r.type}</td>
                      <td className="px-3 py-2 text-[#5A6B73] text-xs">{r.trigger}</td>
                      <td className="px-3 py-2 text-[#0A1F2E]">{r.subject || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ClientDetailView = ({ client, onBack, onListingAdded, onDeleted }) => {
  const [clientData, setClientData] = useState(null);
  const [showAddListing, setShowAddListing] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [formData, setFormData] = useState({
    title: '', price: '', location: '', description: '',
    bedrooms: '', bathrooms: '', area: '', images: [],
    source_url: '', agent_name: '', agent_phone: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState(client.tags || []);
  const [newTag, setNewTag] = useState('');
  const [emailData, setEmailData] = useState({ subject: '', html_content: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [retryingBlueprint, setRetryingBlueprint] = useState(false);
  const [generatingBlueprint, setGeneratingBlueprint] = useState(false);
  const [resendingBlueprintEmail, setResendingBlueprintEmail] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // iter 179 (Fix 1) - is_internal toggle. Local state so the button
  // reflects the flag flip without a full re-fetch (which would also drop
  // any pending listing/tag edits).
  const [isInternal, setIsInternal] = useState(!!client.is_internal);
  const [togglingInternal, setTogglingInternal] = useState(false);

  const handleToggleInternal = async () => {
    if (togglingInternal) return;
    const nextVal = !isInternal;
    const msg = nextVal
      ? `Flag ${client.email} as an INTERNAL account? They will be excluded from Paid Orders, revenue, and comped_accounts metrics.`
      : `Unflag ${client.email} as internal? They will start counting toward revenue metrics again.`;
    if (!window.confirm(msg)) return;
    setTogglingInternal(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${client.id}/toggle-internal`,
        { method: 'POST', credentials: 'include' },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      const body = await r.json();
      setIsInternal(!!body.is_internal);
      notifySuccess(
        body.is_internal
          ? `${client.email} flagged internal - revenue metrics refresh in ~60s.`
          : `${client.email} unflagged - counts toward revenue again.`,
      );
    } catch (e) {
      notifyError(`Toggle failed: ${e.message || 'Unknown error'}`);
    } finally {
      setTogglingInternal(false);
    }
  };

  // iter 142d - restore the delete control on the client detail page.
  // Uses the same DELETE /api/admin/members/{id} cascade as the row-list
  // delete (see deleteMember in ClientsTab). Requires the admin to type
  // DELETE to confirm. Hidden entirely for protected admin emails.
  const handleDelete = async () => {
    if (isProtectedAdmin(client)) return;
    const typed = window.prompt(
      `Type DELETE to permanently remove ${client.email}.\n\nThis cascades to all their data and cannot be undone.`,
      '',
    );
    if (typed === null) return;
    if (typed.trim().toUpperCase() !== 'DELETE') {
      notifyError('Deletion cancelled - confirmation phrase did not match.');
      return;
    }
    setDeleting(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/members/${client.id}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      notifySuccess(`Deleted ${client.email}.`);
      onDeleted?.();
    } catch (e) {
      notifyError(`Delete failed: ${e.message || 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const blueprintStatus =
    clientData?.client?.latest_blueprint_status
    ?? clientData?.blueprint_status
    ?? client.blueprint_status;
  // iter 168 - Surface the Blueprint action button for ALL blueprint-tier
  // clients, not just ones with a stuck/errored blueprint row. The same
  // endpoint is idempotent and:
  //   • re-sends the intake email when no intake on file ("intake_required")
  //   • re-queues generation when status is error/generating ("queued")
  //   • no-ops cleanly when the blueprint is already ready
  // Trigger: a paying client (Dick, blueprint_only) reported he never got
  // an intake link. The Retry button was hidden because his blueprint row
  // didn't exist yet (status="none"), so admins had no way to resend it.
  const blueprintTier = (
    client.tier
    || client.subscription_tier
    || clientData?.client?.subscription_tier
    || clientData?.client?.role
    || ''
  ).toLowerCase();
  const isBlueprintTier = ['blueprint_only', 'essentials', 'guided'].includes(blueprintTier);
  const isBlueprintReady = blueprintStatus === 'ready' || blueprintStatus === 'done';
  const showBlueprintAction = isBlueprintTier && !isBlueprintReady;
  // Dynamic label: when there's no intake on file (status missing / "none"
  // / "pending"), the action is really "send them the intake link". When a
  // blueprint row exists with a failure state, the action is "retry
  // generation". Same endpoint either way.
  const needsIntake =
    !blueprintStatus
    || blueprintStatus === 'none'
    || blueprintStatus === 'pending';
  const blueprintActionLabel = needsIntake ? 'Resend Intake Link' : 'Retry Blueprint';
  const blueprintActionTitle = needsIntake
    ? 'Re-send the Blueprint intake form email to this client'
    : `Blueprint is currently "${blueprintStatus}" - re-run generation`;

  const handleRetryBlueprint = async () => {
    if (!client.email) {
      notifyError('Client email missing - cannot retry blueprint.');
      return;
    }
    setRetryingBlueprint(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/debug/retry-blueprint/${encodeURIComponent(client.email)}`,
        { method: 'POST', credentials: 'include' },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.detail || `Retry failed (HTTP ${res.status})`);
      }
      const action = body.action || 'unknown';
      const messages = {
        queued: 'Blueprint regeneration queued. This will take ~2–5 minutes.',
        skipped_already_ready: 'Blueprint is already ready - no retry needed.',
        intake_required: 'No intake on file - intake form email re-sent to the client.',
      };
      notifySuccess(messages[action] || `Retry complete (action: ${action}).`);
      fetchClientData();
    } catch (e) {
      notifyError(`Retry failed: ${e.message}`);
    } finally {
      setRetryingBlueprint(false);
    }
  };

  // iter 168 - manual Blueprint generation + email resend buttons. Driven
  // by Dick's case (paid blueprint_only, intake came in after the auto-
  // fire ran, blueprint never generated and admin had no way to recover).
  const handleGenerateBlueprint = async () => {
    if (!client.email) {
      notifyError('Client email missing - cannot generate blueprint.');
      return;
    }
    setGeneratingBlueprint(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${encodeURIComponent(client.email)}/generate-blueprint`,
        { method: 'POST', credentials: 'include' },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const messages = {
        queued: 'Blueprint generation queued. Status will flip to "done" in ~2–5 minutes; client gets the PDF email automatically.',
        skipped_already_done: 'Blueprint already generated - use Resend Blueprint Email instead.',
        skipped_already_generating: 'Generation is already in progress - give it a few minutes.',
      };
      notifySuccess(messages[body.action] || `Action: ${body.action}`);
      fetchClientData();
    } catch (e) {
      notifyError(`Generate failed: ${e.message}`);
    } finally {
      setGeneratingBlueprint(false);
    }
  };

  const handleResendBlueprintEmail = async () => {
    if (!client.email) {
      notifyError('Client email missing - cannot resend.');
      return;
    }
    setResendingBlueprintEmail(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${encodeURIComponent(client.email)}/resend-blueprint-email`,
        { method: 'POST', credentials: 'include' },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      notifySuccess('Blueprint email re-sent (PDF + portal link).');
    } catch (e) {
      notifyError(`Resend failed: ${e.message}`);
    } finally {
      setResendingBlueprintEmail(false);
    }
  };

  useEffect(() => {
    fetchClientData();
  }, [client.user_id]);

  const fetchClientData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${client.user_id}`, { credentials: 'include' });
      if (response.ok) setClientData(await response.json());
    } catch (error) {
      console.error('Failed to fetch client data:', error);
    }
  };

  const handleScrape = async () => {
    if (!scrapeUrl.trim()) return;
    setScraping(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/scrape-listing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: scrapeUrl })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setFormData({
            ...formData,
            title: result.data.title || '',
            price: result.data.price || '',
            location: result.data.location || '',
            description: result.data.description || '',
            bedrooms: result.data.bedrooms || '',
            bathrooms: result.data.bathrooms || '',
            area: result.data.area || '',
            images: result.data.images || [],
            source_url: result.data.source_url || scrapeUrl
          });
        }
      }
    } catch (error) {
      console.error('Scrape error:', error);
      notifyError('Could not scrape. Add details manually.');
    } finally {
      setScraping(false);
    }
  };

  const handleAddListing = async () => {
    if (!formData.title.trim()) { notifyError('Title is required.'); return; }
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_id: client.user_id,
          ...formData,
          bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
          bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null
        })
      });
      if (response.ok) {
        setShowAddListing(false);
        setFormData({ title: '', price: '', location: '', description: '', bedrooms: '', bathrooms: '', area: '', images: [], source_url: '', agent_name: '', agent_phone: '', notes: '' });
        setScrapeUrl('');
        fetchClientData();
        onListingAdded();
      }
    } catch (error) {
      notifyError('Failed to add listing.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim() || tags.includes(newTag.trim())) return;
    const updatedTags = [...tags, newTag.trim()];
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: client.user_id, tags: updatedTags })
      });
      setTags(updatedTags);
      setNewTag('');
    } catch (error) {
      console.error('Failed to add tag:', error);
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    const updatedTags = tags.filter(t => t !== tagToRemove);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: client.user_id, tags: updatedTags })
      });
      setTags(updatedTags);
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.subject.trim() || !emailData.html_content.trim()) {
      notifyError('Subject and content are required.');
      return;
    }
    setSendingEmail(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          recipient_email: client.email,
          subject: emailData.subject,
          html_content: emailData.html_content
        })
      });
      if (response.ok) {
        notifySuccess('Email sent!');
        setShowEmail(false);
        setEmailData({ subject: '', html_content: '' });
      } else {
        const err = await response.json().catch(() => ({}));
        notifyError(err, 'Failed to send email.');
      }
    } catch (error) {
      notifyError('Failed to send email.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-sm text-[#5A6B73] hover:text-[#2CA8A0]">← Back to Clients</button>

      {/* Client Header */}
      <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {client.picture ? (
              <img src={client.picture} alt="" className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#2CA8A0] flex items-center justify-center text-[#F9F6EF] text-2xl font-semibold">
                {client.name?.charAt(0) || '?'}
              </div>
            )}
            <div>
              <h2 className="text-xl font-semibold text-[#0A1F2E]">{client.name}</h2>
              <p className="text-[#5A6B73]">{client.email}</p>
              {/* iter 179 (Fix 4) - Destination city in the client header. */}
              {client.destination_city && (
                <p className="text-xs text-[#1A5C6E] mt-1" data-testid="client-detail-destination">
                  <span className="font-mono uppercase tracking-wider text-[10px] text-[#5A6B73] mr-1">Destination:</span>
                  <span className="font-semibold">{client.destination_city}</span>
                </p>
              )}
              {/* iter 179 (Fix 1) - Internal-account badge (visible when flagged). */}
              {isInternal && (
                <span
                  className="inline-block text-[10px] px-1.5 py-0.5 mt-1 bg-purple-100 text-purple-800 rounded uppercase tracking-wide"
                  data-testid="client-detail-internal-badge"
                >
                  Internal account
                </span>
              )}
              {/* iter 160 - Fix 3: colored tier badge on the client detail header */}
              <div className="mt-2" data-testid="client-detail-tier-badge">
                {(() => {
                  const raw = (client.tier || client.subscription_tier || client.role || '').toLowerCase();
                  const label = client.tier_name || client.tier || client.subscription_tier || client.role || '-';
                  const styles = {
                    blueprint_only: 'bg-[#E8A830]/20 text-[#8C5B12] border border-[#E8A830]/40',
                    essentials:     'bg-[#2CA8A0]/20 text-[#1A5C6E] border border-[#2CA8A0]/40',
                    guided:         'bg-[#1A5C6E]/15 text-[#0A1F2E] border border-[#1A5C6E]/40',
                    community:      'bg-[#7C3AED]/15 text-[#5B21B6] border border-[#7C3AED]/30',
                    connected:      'bg-[#7C3AED]/20 text-[#4C1D95] border border-[#7C3AED]/40',
                    free_trial:     'bg-[#F4ECD8] text-[#0A1F2E]/70 border border-[#EFE6CF]',
                    expired_trial:  'bg-red-50 text-red-700 border border-red-200',
                  };
                  const cls = styles[raw] || 'bg-[#F4ECD8] text-[#0A1F2E]/70 border border-[#EFE6CF]';
                  const display = raw === 'free_trial' ? 'Free Trial' :
                                  raw === 'expired_trial' ? 'Expired Trial' : label;
                  return (
                    <span className={`inline-block text-xs px-2.5 py-1 rounded font-medium ${cls}`}>
                      {display}
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* iter 179 (Fix 1) - Toggle Internal flag: excludes from revenue. */}
            <button
              onClick={handleToggleInternal}
              disabled={togglingInternal}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border transition disabled:opacity-50 ${
                isInternal
                  ? 'border-purple-500 bg-purple-100 text-purple-900 hover:bg-purple-200'
                  : 'border-[#EFE6CF] text-[#5A6B73] hover:border-purple-400 hover:text-purple-800'
              }`}
              data-testid="client-toggle-internal-btn"
              title={isInternal
                ? 'This user is flagged internal and excluded from revenue metrics. Click to unflag.'
                : 'Flag as internal - excludes from Paid Orders, revenue, and comped metrics.'}
            >
              {togglingInternal
                ? 'Saving…'
                : isInternal ? '✓ Internal' : 'Flag internal'}
            </button>
            {showBlueprintAction && (
              <button
                onClick={handleRetryBlueprint}
                disabled={retryingBlueprint}
                className="flex items-center gap-2 px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] text-sm font-semibold hover:bg-[#1A5C6E] disabled:opacity-50 transition"
                data-testid="retry-blueprint-btn"
                title={blueprintActionTitle}
              >
                <RefreshCw className={`w-4 h-4 ${retryingBlueprint ? 'animate-spin' : ''}`} />
                {retryingBlueprint ? 'Sending…' : blueprintActionLabel}
              </button>
            )}
            {/* iter 168 - Manual Blueprint controls. Visible for all
                blueprint-tier clients so an admin can force-generate or
                re-send the PDF email when the automatic webhook path
                missed the window (e.g. intake submitted after payment). */}
            {isBlueprintTier && (
              <>
                <button
                  onClick={handleGenerateBlueprint}
                  disabled={generatingBlueprint}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1A5C6E] text-[#F9F6EF] text-sm font-semibold hover:bg-[#0A1F2E] disabled:opacity-50 transition"
                  data-testid="generate-blueprint-btn"
                  title="Manually trigger Blueprint AI generation using the client's submitted intake"
                >
                  <RefreshCw className={`w-4 h-4 ${generatingBlueprint ? 'animate-spin' : ''}`} />
                  {generatingBlueprint ? 'Generating…' : 'Generate Blueprint'}
                </button>
                <button
                  onClick={handleResendBlueprintEmail}
                  disabled={resendingBlueprintEmail}
                  className="flex items-center gap-2 px-4 py-2 bg-[#F4ECD8] text-[#0A1F2E] text-sm font-semibold hover:bg-[#E0D6B8] disabled:opacity-50 transition"
                  data-testid="resend-blueprint-email-btn"
                  title="Re-send the Blueprint ready email (PDF + portal link)"
                >
                  <Mail className="w-4 h-4" />
                  {resendingBlueprintEmail ? 'Sending…' : 'Resend Blueprint Email'}
                </button>
              </>
            )}
            <button onClick={() => setShowEmail(true)} className="flex items-center gap-2 px-4 py-2 bg-[#F4ECD8] text-[#0A1F2E] text-sm hover:bg-[#E0D6B8]">
              <Mail className="w-4 h-4" />
              Send Email
            </button>
            {/* iter 142d - Delete button. Hidden for protected admin emails
                (francisco / rodrigo) so they can never delete themselves. */}
            {!isProtectedAdmin(client) && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 text-sm hover:bg-red-100 border border-red-200 disabled:opacity-50 transition"
                data-testid="client-detail-delete-btn"
                title="Permanently delete this client and all their data"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Deleting…' : 'Delete client'}
              </button>
            )}
          </div>
        </div>

        {/* Tags */}
        <div className="mt-4 pt-4 border-t border-[#F4ECD8]">
          <div className="flex items-center gap-2 flex-wrap">
            <Tag className="w-4 h-4 text-[#5A6B73]" />
            {tags.map((tag, idx) => (
              <span key={idx} className="text-xs px-2 py-1 bg-[#2CA8A0]/20 text-[#2CA8A0] rounded flex items-center gap-1">
                {tag}
                <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-400">×</button>
              </span>
            ))}
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
              placeholder="Add tag..."
              className="text-xs px-2 py-1 bg-transparent border border-[#E0D6B8] text-[#0A1F2E] w-24"
            />
            <button onClick={handleAddTag} className="text-xs text-[#2CA8A0]">+ Add</button>
          </div>
        </div>
      </div>

      {/* iter 144g - Communications history (email_logs). Collapsible. */}
      <ClientCommunicationsSection clientId={client.id} />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-4 text-center">
          <p className="text-2xl font-semibold text-[#2CA8A0]">{clientData?.listings?.length || 0}</p>
          <p className="text-xs text-[#5A6B73]">Listings</p>
        </div>
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-4 text-center">
          <p className="text-2xl font-semibold text-[#2CA8A0]">{clientData?.documents?.length || 0}</p>
          <p className="text-xs text-[#5A6B73]">Documents</p>
        </div>
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-4 text-center">
          <p className="text-2xl font-semibold text-[#2CA8A0]">{clientData?.payments?.length || 0}</p>
          <p className="text-xs text-[#5A6B73]">Payments</p>
        </div>
      </div>

      {/* Listings */}
      <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#0A1F2E]">Listings</h3>
          <button onClick={() => setShowAddListing(true)} className="flex items-center gap-2 px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] text-sm font-semibold">
            <Plus className="w-4 h-4" />
            Add Listing
          </button>
        </div>
        {clientData?.listings?.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {clientData.listings.map((listing) => (
              <div key={listing.listing_id} className="bg-[#F9F6EF] border border-[#E0D6B8] overflow-hidden">
                {listing.image && <img src={listing.image} alt="" className="w-full h-32 object-cover" />}
                <div className="p-4">
                  <h4 className="font-medium text-[#0A1F2E]">{listing.title}</h4>
                  <p className="text-sm text-[#5A6B73]">{listing.location}</p>
                  <p className="text-[#2CA8A0] font-semibold mt-2">{listing.price}</p>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[#5A6B73]">No listings assigned</p>}
      </div>

      {/* Add Listing Modal */}
      {showAddListing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#FFFFFF] border border-[#F4ECD8] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#FFFFFF] border-b border-[#F4ECD8] p-4 flex items-center justify-between">
              <h3 className="font-semibold text-[#0A1F2E]">Add Listing for {client.name}</h3>
              <button onClick={() => setShowAddListing(false)} className="text-[#5A6B73] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* URL Scraper */}
              <div className="bg-[#F9F6EF] border border-[#E0D6B8] p-4">
                <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">
                  <Link className="w-4 h-4 inline mr-2" />
                  Paste EasyBroker or Inmuebles24 URL
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={scrapeUrl}
                    onChange={(e) => setScrapeUrl(e.target.value)}
                    placeholder="https://www.easybroker.com/mx/..."
                    className="flex-1 px-3 py-2 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E] text-sm"
                  />
                  <button onClick={handleScrape} disabled={scraping || !scrapeUrl.trim()} className="px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] font-semibold text-sm disabled:opacity-50 flex items-center gap-2">
                    {scraping ? 'Scraping...' : <><Search className="w-4 h-4" /> Extract</>}
                  </button>
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Title *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Price</label>
                  <input type="text" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Location</label>
                  <input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Bedrooms</label>
                  <input type="number" value={formData.bedrooms} onChange={(e) => setFormData({...formData, bedrooms: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Bathrooms</label>
                  <input type="number" step="0.5" value={formData.bathrooms} onChange={(e) => setFormData({...formData, bathrooms: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Area</label>
                  <input type="text" value={formData.area} onChange={(e) => setFormData({...formData, area: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" placeholder="85 m²" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Agent Name</label>
                  <input type="text" value={formData.agent_name} onChange={(e) => setFormData({...formData, agent_name: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="3" className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Notes (internal)</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows="2" className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
              </div>

              {/* Images Preview */}
              {formData.images.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">Images Found ({formData.images.length})</label>
                  <div className="grid grid-cols-5 gap-2">
                    {formData.images.slice(0, 10).map((img, idx) => (
                      <div key={idx} className="aspect-square bg-[#F4ECD8] overflow-hidden">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-[#FFFFFF] border-t border-[#F4ECD8] p-4 flex justify-end gap-3">
              <button onClick={() => setShowAddListing(false)} className="px-4 py-2 border border-[#E0D6B8] text-[#0A1F2E] text-sm">Cancel</button>
              <button onClick={handleAddListing} disabled={saving} className="px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] font-semibold text-sm disabled:opacity-50">
                {saving ? 'Saving...' : 'Add Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {showEmail && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#FFFFFF] border border-[#F4ECD8] w-full max-w-lg">
            <div className="border-b border-[#F4ECD8] p-4 flex items-center justify-between">
              <h3 className="font-semibold text-[#0A1F2E]">Email to {client.name}</h3>
              <button onClick={() => setShowEmail(false)} className="text-[#5A6B73] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">To</label>
                <input type="text" value={client.email} disabled className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#5A6B73] text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Subject *</label>
                <input type="text" value={emailData.subject} onChange={(e) => setEmailData({...emailData, subject: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Content *</label>
                <textarea value={emailData.html_content} onChange={(e) => setEmailData({...emailData, html_content: e.target.value})} rows="6" className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" placeholder="Write your email here... Use {{name}} to personalize." />
              </div>
            </div>
            <div className="border-t border-[#F4ECD8] p-4 flex justify-end gap-3">
              <button onClick={() => setShowEmail(false)} className="px-4 py-2 border border-[#E0D6B8] text-[#0A1F2E] text-sm">Cancel</button>
              <button onClick={handleSendEmail} disabled={sendingEmail} className="px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] font-semibold text-sm disabled:opacity-50 flex items-center gap-2">
                {sendingEmail ? 'Sending...' : <><Send className="w-4 h-4" /> Send</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== LISTINGS TAB ==============
const ListingsTab = ({ listings, onDelete, clients, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [formData, setFormData] = useState({ title: '', price: '', location: '', description: '', bedrooms: '', bathrooms: '', area: '', images: [], source_url: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleScrape = async () => {
    if (!scrapeUrl.trim()) return;
    setScraping(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/scrape-listing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: scrapeUrl })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setFormData({ ...formData, ...result.data });
        }
      }
    } catch (error) {
      notifyError('Scraping failed. Add details manually.');
    } finally {
      setScraping(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedUserId || !formData.title.trim()) {
      notifyError('Select a client and add a title.');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: selectedUserId, ...formData, bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null, bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null })
      });
      if (response.ok) {
        setShowAdd(false);
        setFormData({ title: '', price: '', location: '', description: '', bedrooms: '', bathrooms: '', area: '', images: [], source_url: '', notes: '' });
        setScrapeUrl('');
        setSelectedUserId('');
        onRefresh();
      }
    } catch (error) {
      notifyError('Failed to add listing.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#5A6B73]">{listings.length} total listings</p>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] text-sm font-semibold">
          <Plus className="w-4 h-4" /> Add Listing
        </button>
      </div>

      {listings.length > 0 ? (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F9F6EF]">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Listing</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Price</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listings.map((listing) => (
                <tr key={listing.listing_id} className="border-t border-[#F4ECD8] hover:bg-[#FFFFFF]">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-[#0A1F2E]">{listing.title}</p>
                    <p className="text-xs text-[#5A6B73]">{listing.location}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-[#1A5C6E]">{listing.client_name}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#2CA8A0]">{listing.price || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => onDelete(listing.listing_id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-[#E0D6B8]" />
          <p className="text-[#5A6B73]">No listings yet</p>
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#FFFFFF] border border-[#F4ECD8] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#FFFFFF] border-b border-[#F4ECD8] p-4 flex items-center justify-between">
              <h3 className="font-semibold text-[#0A1F2E]">Add New Listing</h3>
              <button onClick={() => setShowAdd(false)} className="text-[#5A6B73] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Assign to Client *</label>
                <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm">
                  <option value="">Select a client...</option>
                  {clients.map(c => <option key={c.user_id} value={c.user_id}>{c.name} ({c.email})</option>)}
                </select>
              </div>
              <div className="bg-[#F9F6EF] border border-[#E0D6B8] p-4">
                <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">Paste Listing URL</label>
                <div className="flex gap-2">
                  <input type="url" value={scrapeUrl} onChange={(e) => setScrapeUrl(e.target.value)} placeholder="https://www.easybroker.com/mx/..." className="flex-1 px-3 py-2 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E] text-sm" />
                  <button onClick={handleScrape} disabled={scraping} className="px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] font-semibold text-sm disabled:opacity-50">
                    {scraping ? '...' : 'Extract'}
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Title *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Price</label>
                  <input type="text" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Location</label>
                  <input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
                </div>
              </div>
              {formData.images?.length > 0 && (
                <div className="grid grid-cols-5 gap-2">
                  {formData.images.slice(0, 5).map((img, idx) => (
                    <div key={idx} className="aspect-square bg-[#F4ECD8] overflow-hidden">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-[#FFFFFF] border-t border-[#F4ECD8] p-4 flex justify-end gap-3">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 border border-[#E0D6B8] text-[#0A1F2E] text-sm">Cancel</button>
              <button onClick={handleAdd} disabled={saving} className="px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] font-semibold text-sm disabled:opacity-50">
                {saving ? 'Saving...' : 'Add Listing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== LEADS TAB ==============
// iter 166 - Added "Source" column so admin can see at a glance which
// funnel (calculator / move_score / visa_finder / direct) the lead
// came from. Backed by the canonical `source` field on every lead row.
// iter 182.e - Option A reality-mirrored taxonomy: each source key maps
// 1:1 to the tool that captured the lead so admins never send "you took
// our quiz" copy to a Cost Calculator user (or vice versa).
const SOURCE_BADGE_STYLES = {
  calculator_v2:          'bg-[#2CA8A0]/15 text-[#1A5C6E] border border-[#2CA8A0]/30',
  calculator_legacy:      'bg-[#2CA8A0]/10 text-[#1A5C6E] border border-[#2CA8A0]/25',
  move_score:             'bg-[#7C3AED]/15 text-[#5B21B6] border border-[#7C3AED]/30',
  visa_finder:            'bg-[#E8A830]/20 text-[#8C5B12] border border-[#E8A830]/40',
  quiz:                   'bg-[#E85B4E]/15 text-[#8C2E22] border border-[#E85B4E]/30',
  blueprint_intake_open:  'bg-[#0A1F2E]/10 text-[#0A1F2E] border border-[#0A1F2E]/25',
  blueprint_intake_trial: 'bg-[#0A1F2E]/15 text-[#0A1F2E] border border-[#0A1F2E]/35',
  // Legacy fallback for old rows that still carry "calculator" in the DB.
  calculator:             'bg-[#2CA8A0]/15 text-[#1A5C6E] border border-[#2CA8A0]/30',
  direct:                 'bg-[#F4ECD8] text-[#0A1F2E]/70 border border-[#EFE6CF]',
  unknown:                'bg-gray-100 text-gray-600 border border-gray-200',
};
const SOURCE_LABELS = {
  calculator_v2:          'Cost Calculator V2',
  calculator_legacy:      'Calculator legacy v1',
  move_score:             'Move Score',
  visa_finder:            'Visa Finder',
  quiz:                   '/start Quiz',
  blueprint_intake_open:  'Intake - no portal account',
  blueprint_intake_trial: 'Intake - Free Trial users',
  calculator:             'Calculator',   // legacy fallback
  direct:                 'Direct',
  unknown:                'Unknown',
};

// ============== iter 167 - LOST LEAD RECOVERY BUTTON ==============
const LostLeadRecoveryButton = () => {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/lost-lead-recovery/preview`,
        { credentials: 'include' },
      );
      const d = await r.json();
      setPreview(d);
      setOpen(true);
    } catch {
      setPreview({ count: 0, users: [], error: true });
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const runExecute = async () => {
    if (!preview || preview.count === 0) return;
    const ok = window.confirm(
      `Send the recovery email to ${preview.count} archived lost lead${preview.count === 1 ? '' : 's'}?\n\n` +
      'Each address is stamped after send so this cannot double-send.\n\nProceed?'
    );
    if (!ok) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/lost-lead-recovery/execute`,
        { method: 'POST', credentials: 'include' },
      );
      const d = await r.json();
      alert(`Sent: ${d.sent}\nFailed: ${d.failed}\nSkipped: ${d.skipped}\nTotal attempted: ${d.total_attempted}`);
      setPreview(null);
      setOpen(false);
    } catch (e) {
      alert('Recovery blast failed: ' + (e.message || 'unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" data-testid="lost-lead-recovery-panel">
      <button
        onClick={runPreview}
        disabled={loading}
        data-testid="lost-lead-recovery-btn"
        className="text-xs font-mono uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border border-[#E8A830] text-[#8C5B12] bg-[#E8A830]/15 hover:bg-[#E8A830]/30 disabled:opacity-50"
      >
        {loading ? 'Loading…' : 'Send Lost Lead Recovery'}
      </button>

      {open && preview && (
        <div
          className="absolute right-0 mt-2 w-[440px] max-w-[92vw] bg-white border border-[#E8A830] rounded-xl shadow-xl p-4 z-30"
          data-testid="lost-lead-recovery-modal"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-[#0A1F2E]">
                Lost-lead recovery · {preview.count} address{preview.count === 1 ? '' : 'es'} pending
              </p>
              <p className="text-[11px] text-[#5A6B73] mt-0.5">
                Archived ghosts from calculator + visa_finder, not yet emailed.
              </p>
            </div>
            <button
              onClick={() => { setOpen(false); setPreview(null); }}
              className="text-[#5A6B73] hover:text-[#0A1F2E] text-lg leading-none"
              aria-label="Close"
            >×</button>
          </div>

          {preview.count === 0 ? (
            <p className="text-sm text-[#5A6B73] italic py-3" data-testid="lost-lead-recovery-empty">
              No lost leads to recover. Either none archived yet, or every eligible address already got the email.
            </p>
          ) : (
            <>
              <ul className="max-h-60 overflow-y-auto space-y-1 mb-3 border border-[#F4ECD8] rounded p-2 bg-[#F9F6EF]/40">
                {preview.users.slice(0, 80).map((u) => (
                  <li
                    key={u.email}
                    className="text-xs text-[#0A1F2E] flex items-center justify-between gap-2"
                  >
                    <span className="truncate flex-1">{u.email}</span>
                    <span className="text-[10px] text-[#5A6B73] uppercase tracking-wider shrink-0">
                      {u.source}
                    </span>
                  </li>
                ))}
                {preview.count > 80 && (
                  <li className="text-[11px] text-[#5A6B73] italic pt-1">
                    … and {preview.count - 80} more
                  </li>
                )}
              </ul>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setOpen(false); setPreview(null); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#EFE6CF] text-[#0A1F2E]"
                >
                  Cancel
                </button>
                <button
                  onClick={runExecute}
                  disabled={loading}
                  data-testid="lost-lead-recovery-execute-btn"
                  className="text-xs px-3 py-1.5 rounded-full bg-[#E8A830] text-[#0A1F2E] hover:brightness-95 disabled:opacity-50 font-medium"
                >
                  {loading ? 'Sending…' : `Send to ${preview.count}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============== iter 172 - LAST ENGAGEMENT RELATIVE-TIME PILL ==============
// Shared between the Leads list cell and the drawer header. Renders a tiny
// pill like "opened 2h ago" / "clicked 3d ago" / "captured 7d ago", with
// color graded by warmth (clicked = hot, opened = warm, captured = cool).

const _ENGAGEMENT_COLORS = {
  clicked:  'bg-emerald-100 text-emerald-800',  // hottest - actively engaging
  opened:   'bg-[#2CA8A0]/15 text-[#1A5C6E]',   // warm - saw it
  captured: 'bg-[#F4ECD8] text-[#5A6B73]',      // cool - has not re-engaged
};

const _formatRelative = (iso) => {
  if (!iso) return '-';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '-';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

// ============== iter 175 - SHARED PAGINATION CONTROLS ==============
// Server-side pagination footer used by ClientsTab + LeadsTab. 25/page.
const PaginationFooter = ({ page, totalPages, total, perPage, onChange, testIdPrefix }) => {
  if (!totalPages || totalPages <= 1) {
    return (
      <div className="text-xs text-[#5A6B73] py-2" data-testid={`${testIdPrefix}-pagination`}>
        {total} {total === 1 ? 'row' : 'rows'} total
      </div>
    );
  }
  const firstRow = (page - 1) * perPage + 1;
  const lastRow = Math.min(page * perPage, total);
  return (
    <div
      className="flex items-center justify-between flex-wrap gap-2 py-3 px-1"
      data-testid={`${testIdPrefix}-pagination`}
    >
      <p className="text-xs text-[#5A6B73]">
        Showing <span className="font-semibold text-[#0A1F2E]">{firstRow}–{lastRow}</span> of <span className="font-semibold text-[#0A1F2E]">{total}</span>
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(1)}
          disabled={page <= 1}
          className="px-2.5 py-1 text-xs font-semibold border border-[#EFE6CF] bg-white text-[#1A5C6E] hover:border-[#2CA8A0] disabled:opacity-40 disabled:cursor-not-allowed transition rounded-sm"
          data-testid={`${testIdPrefix}-page-first`}
        >
          ‹‹
        </button>
        <button
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="px-2.5 py-1 text-xs font-semibold border border-[#EFE6CF] bg-white text-[#1A5C6E] hover:border-[#2CA8A0] disabled:opacity-40 disabled:cursor-not-allowed transition rounded-sm"
          data-testid={`${testIdPrefix}-page-prev`}
        >
          ‹ Prev
        </button>
        <span className="px-3 py-1 text-xs text-[#0A1F2E]" data-testid={`${testIdPrefix}-page-indicator`}>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className="px-2.5 py-1 text-xs font-semibold border border-[#EFE6CF] bg-white text-[#1A5C6E] hover:border-[#2CA8A0] disabled:opacity-40 disabled:cursor-not-allowed transition rounded-sm"
          data-testid={`${testIdPrefix}-page-next`}
        >
          Next ›
        </button>
        <button
          onClick={() => onChange(totalPages)}
          disabled={page >= totalPages}
          className="px-2.5 py-1 text-xs font-semibold border border-[#EFE6CF] bg-white text-[#1A5C6E] hover:border-[#2CA8A0] disabled:opacity-40 disabled:cursor-not-allowed transition rounded-sm"
          data-testid={`${testIdPrefix}-page-last`}
        >
          ››
        </button>
      </div>
    </div>
  );
};

const LeadEngagementPill = ({ engagement, createdAt, testIdSuffix }) => {
  // Falls back to `captured + createdAt` so leads with no email events
  // still show something meaningful in the column.
  const eng = engagement || (createdAt ? { type: 'captured', at: createdAt } : null);
  if (!eng) {
    return (
      <span className="text-xs text-[#5A6B73]" data-testid={`lead-engagement-${testIdSuffix}`}>
        -
      </span>
    );
  }
  const cls = _ENGAGEMENT_COLORS[eng.type] || _ENGAGEMENT_COLORS.captured;
  return (
    <span
      className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${cls}`}
      data-testid={`lead-engagement-${testIdSuffix}`}
      title={eng.at ? new Date(eng.at).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : ''}
    >
      {eng.type} {_formatRelative(eng.at)}
    </span>
  );
};


// ============== iter 171 - LEAD DETAIL DRAWER ==============
// Right-side slide-in panel surfaced when a row in LeadsTab is clicked.
// Shows full calculator/quiz data, the funnel position, every nurture
// email sent to this lead, and three quick-actions (Send Email, Copy
// Email, change funnel stage). Pure additive - list view is untouched.

const FUNNEL_STAGES = [
  { value: 'captured', label: 'Captured', color: 'bg-[#F4ECD8] text-[#5A6B73]' },
  { value: 'engaged', label: 'Engaged', color: 'bg-[#2CA8A0]/20 text-[#1A5C6E]' },
  { value: 'checkout_clicked', label: 'Checkout clicked', color: 'bg-amber-100 text-amber-900' },
  { value: 'paid', label: 'Paid', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'cold', label: 'Cold', color: 'bg-red-50 text-red-700' },
];

const LeadDrawer = ({ email, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [composing, setComposing] = useState(false);
  const [composeData, setComposeData] = useState({ subject: '', body: '' });
  const [updatingStage, setUpdatingStage] = useState(false);

  const fetchDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${encodeURIComponent(email)}/detail`,
        { credentials: 'include' },
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      setData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetail(); }, [email]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const firstName = data?.lead?.first_name || '';
  const city = data?.lead?.city_1 || data?.lead?.city_name || data?.lead?.city || '';

  const openCompose = () => {
    const intro =
      (firstName ? `Hi ${firstName},\n\n` : 'Hi,\n\n')
      + (city ? `I was looking at your interest in ${city} - ` : '')
      + 'wanted to share a quick note about your relocation plan.\n\n- Francisco';
    setComposeData({
      subject: city ? `Your move to ${city} - quick note` : 'A quick note from Mundalo',
      body: intro,
    });
    setShowCompose(true);
  };

  const handleSendEmail = async () => {
    if (!composeData.subject.trim() || !composeData.body.trim()) {
      notifyError('Subject and body are required.');
      return;
    }
    setComposing(true);
    try {
      const html = composeData.body
        .split('\n\n')
        .map((p) => `<p style="margin: 0 0 16px 0; line-height: 1.6;">${p.replace(/\n/g, '<br/>')}</p>`)
        .join('');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/send-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            recipient_email: email,
            subject: composeData.subject,
            html_content: html,
          }),
        },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      notifySuccess('Email sent.');
      setShowCompose(false);
      fetchDetail();
    } catch (e) {
      notifyError(`Send failed: ${e.message}`);
    } finally {
      setComposing(false);
    }
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      notifySuccess(`Copied ${email}`);
    } catch {
      notifyError('Could not access clipboard.');
    }
  };

  const handleStageChange = async (newStage) => {
    setUpdatingStage(true);
    // iter 171b - optimistic update so the badge + breadcrumb flip
    // immediately. The re-fetch below confirms persistence; if it fails
    // we'll surface the error and the next refetch reverts the badge.
    setData((prev) => prev && ({
      ...prev,
      funnel: { ...prev.funnel, stage: newStage, manually_set: true },
    }));
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${encodeURIComponent(email)}/funnel-stage`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ stage: newStage }),
        },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      notifySuccess(`Moved to ${newStage.replace('_', ' ')}.`);
      fetchDetail();
    } catch (e) {
      notifyError(`Update failed: ${e.message}`);
      // Revert optimistic change by refetching ground truth.
      fetchDetail();
    } finally {
      setUpdatingStage(false);
    }
  };

  const stage = data?.funnel?.stage || 'captured';
  const stageMeta = FUNNEL_STAGES.find((s) => s.value === stage) || FUNNEL_STAGES[0];
  const source = (data?.lead?.source || data?.source_collection || 'unknown').toLowerCase();
  const sourceLabel = SOURCE_LABELS[source] || source;
  const sourceCls = SOURCE_BADGE_STYLES[source] || SOURCE_BADGE_STYLES.unknown;

  return (
    <>
      {/* Backdrop. iter 171b - raised to z-[60] so toasts (Sonner often
          sits at z-[50]) can't sit on top and steal the close click. */}
      <div
        className="fixed inset-0 bg-black/40 z-[60]"
        onClick={onClose}
        data-testid="lead-drawer-backdrop"
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 bottom-0 w-full sm:w-[480px] bg-[#F9F6EF] z-[70] shadow-2xl flex flex-col"
        data-testid="lead-drawer"
        role="dialog"
        aria-label="Lead detail"
      >
        {/* Header */}
        <header className="px-5 py-4 border-b border-[#EFE6CF] flex items-start justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold text-lg text-[#0A1F2E] truncate">
              {data?.lead?.first_name || email.split('@')[0]}
            </h3>
            <p className="text-xs text-[#5A6B73] truncate" data-testid="lead-drawer-email">
              {email}
            </p>
            {/* iter 179 (Fix 4) - Destination city prominent in header. */}
            {data?.destination_city && (
              <p className="text-xs text-[#1A5C6E] mt-1" data-testid="lead-drawer-destination">
                <span className="font-mono uppercase tracking-wider text-[10px] text-[#5A6B73] mr-1">Destination:</span>
                <span className="font-semibold">{data.destination_city}</span>
              </p>
            )}
            {/* iter 172 - warmth signal in the drawer header. Same pill
                component the list cell uses so the relative time always
                matches between the two views. */}
            {data?.last_engagement && (
              <div className="mt-2" data-testid="lead-drawer-engagement">
                <LeadEngagementPill
                  engagement={data.last_engagement}
                  createdAt={data?.funnel?.captured_at}
                  testIdSuffix="drawer"
                />
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#F4ECD8] rounded transition"
            data-testid="lead-drawer-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-[#5A6B73]" />
          </button>
        </header>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && <p className="text-sm text-[#5A6B73]">Loading lead…</p>}
          {error && (
            <p className="text-sm text-red-700" data-testid="lead-drawer-error">
              {error}
            </p>
          )}
          {data && (
            <>
              {/* Source + capture date */}
              <section className="flex items-center gap-2 flex-wrap" data-testid="lead-drawer-meta">
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${sourceCls}`}>
                  {sourceLabel}
                </span>
                <span className="text-xs text-[#5A6B73]">
                  Captured {data.funnel.captured_at ? new Date(data.funnel.captured_at).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : '-'}
                </span>
              </section>

              {/* Calculator household snapshot */}
              <section data-testid="lead-drawer-household">
                <h4 className="text-[11px] uppercase tracking-wide text-[#5A6B73] mb-2">
                  Calculator data
                </h4>
                <dl className="grid grid-cols-2 gap-y-1.5 gap-x-3 text-sm">
                  {city && (
                    <>
                      <dt className="text-[#5A6B73]">City</dt>
                      <dd className="text-[#0A1F2E] font-medium">{city}</dd>
                    </>
                  )}
                  {data.lead.primary_visa && (
                    <>
                      <dt className="text-[#5A6B73]">Visa</dt>
                      <dd className="text-[#0A1F2E] font-medium">{data.lead.primary_visa}</dd>
                    </>
                  )}
                  {data.household.persona && (
                    <>
                      <dt className="text-[#5A6B73]">Household</dt>
                      <dd className="text-[#0A1F2E] font-medium capitalize">
                        {data.household.persona.replace('_', ' ')}
                      </dd>
                    </>
                  )}
                  {data.household.age_range && (
                    <>
                      <dt className="text-[#5A6B73]">Age</dt>
                      <dd className="text-[#0A1F2E]">{data.household.age_range}</dd>
                    </>
                  )}
                  {data.household.income_type && (
                    <>
                      <dt className="text-[#5A6B73]">Income</dt>
                      <dd className="text-[#0A1F2E] capitalize">{data.household.income_type.replace('_', ' ')}</dd>
                    </>
                  )}
                  {data.household.budget_range && (
                    <>
                      <dt className="text-[#5A6B73]">Budget</dt>
                      <dd className="text-[#0A1F2E]">${data.household.budget_range.replace(/_/g, '–')}/mo</dd>
                    </>
                  )}
                </dl>
              </section>

              {/* Funnel position indicator */}
              <section data-testid="lead-drawer-funnel">
                <h4 className="text-[11px] uppercase tracking-wide text-[#5A6B73] mb-2">
                  Funnel position
                </h4>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded ${stageMeta.color}`}>
                  <span className="text-sm font-semibold">{stageMeta.label}</span>
                  {data.funnel.manually_set && (
                    <span className="text-[10px] uppercase opacity-70">manual</span>
                  )}
                </div>
                {/* Visual funnel breadcrumb. iter 171b - expanded to 4
                    nodes including 'engaged' so the breadcrumb reflects
                    every stage the dropdown can set (cold is rendered
                    separately as a "stalled" indicator). */}
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  {['captured', 'engaged', 'checkout_clicked', 'paid'].map((s, i, arr) => {
                    const order = ['captured', 'engaged', 'checkout_clicked', 'paid'];
                    const stageIndex = order.indexOf(stage);
                    const here = order.indexOf(s);
                    const passed = stageIndex >= here && stageIndex !== -1;
                    return (
                      <React.Fragment key={s}>
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded ${passed ? 'bg-[#1A5C6E] text-white' : 'bg-[#F4ECD8] text-[#5A6B73]'}`}
                        >
                          {s === 'checkout_clicked' ? 'Checkout' : s.charAt(0).toUpperCase() + s.slice(1)}
                        </span>
                        {i < arr.length - 1 && <span className="text-[#5A6B73]">→</span>}
                      </React.Fragment>
                    );
                  })}
                  {stage === 'cold' && (
                    <span className="text-[11px] px-2 py-0.5 rounded bg-red-100 text-red-800 ml-2">
                      Cold - stalled
                    </span>
                  )}
                </div>
              </section>

              {/* iter 179 (Fix 3/4) - Tool interaction history: every
                  calculator/quiz/visa/score row this email ever submitted,
                  newest first, with per-interaction city where captured. */}
              {Array.isArray(data.interactions) && data.interactions.length > 0 && (
                <section data-testid="lead-drawer-interactions">
                  <h4 className="text-[11px] uppercase tracking-wide text-[#5A6B73] mb-2">
                    Tool interactions ({data.interactions.length})
                  </h4>
                  <ul className="space-y-2">
                    {data.interactions.map((intx, i) => (
                      <li
                        key={i}
                        className="bg-white border border-[#EFE6CF] px-3 py-2 rounded-sm"
                        data-testid={`lead-drawer-interaction-${i}`}
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] uppercase tracking-wide text-[#1A5C6E] bg-[#F4ECD8] px-1.5 py-0.5 rounded">
                            {intx.source || 'tool'}
                          </span>
                          {intx.city && (
                            <span className="text-[10px] text-[#0A1F2E] bg-[#E6F4F1] px-1.5 py-0.5 rounded">
                              {intx.city}
                            </span>
                          )}
                          <span className="text-[11px] text-[#5A6B73]">
                            {intx.at ? new Date(intx.at).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : '-'}
                          </span>
                        </div>
                        {intx.summary && (
                          <p className="text-xs text-[#5A6B73]">{intx.summary}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Email history */}
              <section data-testid="lead-drawer-emails">
                <h4 className="text-[11px] uppercase tracking-wide text-[#5A6B73] mb-2">
                  Email history ({data.emails.length})
                </h4>
                {data.emails.length === 0 ? (
                  <p className="text-sm text-[#5A6B73]">No emails sent yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.emails.map((em, i) => (
                      <li
                        key={i}
                        className="bg-white border border-[#EFE6CF] px-3 py-2 rounded-sm"
                        data-testid={`lead-drawer-email-row-${i}`}
                      >
                        <p className="text-sm text-[#0A1F2E] font-medium leading-tight">
                          {em.subject}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[11px] text-[#5A6B73]">
                            {em.sent_at ? new Date(em.sent_at).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : '-'}
                          </span>
                          {em.tag && (
                            <span className="text-[10px] uppercase tracking-wide text-[#1A5C6E] bg-[#2CA8A0]/10 px-1.5 py-0.5 rounded">
                              {em.tag}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>

        {/* Sticky footer actions */}
        {data && (
          <footer className="border-t border-[#EFE6CF] bg-[#F9F6EF] px-5 py-3 space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={openCompose}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#2CA8A0] text-[#F9F6EF] text-sm font-semibold hover:bg-[#1A5C6E] transition rounded-sm"
                data-testid="lead-drawer-send-email-btn"
              >
                <Mail className="w-4 h-4" />
                Send Email
              </button>
              <button
                onClick={handleCopyEmail}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#F4ECD8] text-[#0A1F2E] text-sm font-semibold hover:bg-[#E0D6B8] transition rounded-sm"
                data-testid="lead-drawer-copy-email-btn"
              >
                <FileText className="w-4 h-4" />
                Copy Email
              </button>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-[#5A6B73] mb-1">
                Move to funnel stage
              </label>
              <select
                value={stage}
                disabled={updatingStage}
                onChange={(e) => handleStageChange(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-[#EFE6CF] text-sm text-[#0A1F2E] rounded-sm focus:outline-none focus:border-[#2CA8A0] disabled:opacity-50"
                data-testid="lead-drawer-funnel-stage-select"
              >
                {FUNNEL_STAGES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </footer>
        )}
      </aside>

      {/* Compose modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" data-testid="lead-drawer-compose-modal">
          <div className="bg-white w-full max-w-xl rounded-sm shadow-2xl">
            <div className="px-5 py-3 border-b border-[#EFE6CF] flex items-center justify-between">
              <h3 className="font-semibold text-[#0A1F2E]">Send email to {email}</h3>
              <button onClick={() => setShowCompose(false)} className="p-1 hover:bg-[#F4ECD8] rounded">
                <X className="w-5 h-5 text-[#5A6B73]" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs text-[#5A6B73] mb-1">Subject</label>
                <input
                  value={composeData.subject}
                  onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-[#EFE6CF] rounded-sm text-sm focus:outline-none focus:border-[#2CA8A0]"
                  data-testid="lead-drawer-compose-subject"
                />
              </div>
              <div>
                <label className="block text-xs text-[#5A6B73] mb-1">Body</label>
                <textarea
                  rows={10}
                  value={composeData.body}
                  onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                  className="w-full px-3 py-2 border border-[#EFE6CF] rounded-sm text-sm focus:outline-none focus:border-[#2CA8A0] font-sans"
                  data-testid="lead-drawer-compose-body"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#EFE6CF] flex justify-end gap-2">
              <button
                onClick={() => setShowCompose(false)}
                className="px-4 py-2 text-sm text-[#5A6B73] hover:text-[#0A1F2E]"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={composing}
                className="px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] text-sm font-semibold hover:bg-[#1A5C6E] disabled:opacity-50 rounded-sm"
                data-testid="lead-drawer-compose-send-btn"
              >
                {composing ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};



const LeadsTab = ({ leads, page = 1, setPage = () => {}, meta = { total: 0, total_pages: 0, per_page: 25 } }) => {
  // iter 171 - clicking a row opens a right-side drawer with full lead
  // details, funnel position, email history, and quick actions. Additive:
  // the table itself is unchanged - only an onClick handler + cursor.
  const [drawerEmail, setDrawerEmail] = useState(null);
  // iter 172 - sort state. Default 'engagement' puts the warmest (most
  // recently opened/clicked/captured) leads on top, since those convert
  // best with manual outreach. Click the header to flip between sort modes.
  const [sortMode, setSortMode] = useState('engagement'); // 'engagement' | 'captured'
  // iter 173 - filter chips. Only one active at a time. Combines with
  // the warmth sort so the filtered cohort still surfaces warmest-first.
  const [activeFilter, setActiveFilter] = useState('all');

  // Filter predicate per chip. Pure functions of (lead, nowMs).
  const FILTERS = {
    all:        () => true,
    eng_24h:    (l, now) => {
      const at = l.last_engagement?.at;
      if (!at || l.last_engagement.type === 'captured') return false;
      return (now - new Date(at).getTime()) <= 24 * 60 * 60 * 1000;
    },
    eng_7d:     (l, now) => {
      const at = l.last_engagement?.at;
      if (!at || l.last_engagement.type === 'captured') return false;
      return (now - new Date(at).getTime()) <= 7 * 24 * 60 * 60 * 1000;
    },
    cold_14d:   (l, now) => {
      // Captured >14d ago AND no open/click events.
      if (l.last_engagement?.type !== 'captured' && l.last_engagement?.type) return false;
      const at = l.last_engagement?.at || l.created_at;
      if (!at) return false;
      return (now - new Date(at).getTime()) > 14 * 24 * 60 * 60 * 1000;
    },
    // iter 182.e - Option A reality-mirrored taxonomy. Each chip maps to
    // exactly ONE lead-capture tool so we can never confuse Quiz vs
    // Cost Calculator V2 vs Blueprint intake buckets again.
    calculator_v2:          (l) => (l.source || '').toLowerCase() === 'calculator_v2',
    calculator_legacy:      (l) => (l.source || '').toLowerCase() === 'calculator_legacy',
    move_score:             (l) => (l.source || '').toLowerCase() === 'move_score',
    visa:                   (l) => (l.source || '').toLowerCase() === 'visa_finder',
    quiz:                   (l) => (l.source || '').toLowerCase() === 'quiz',
    blueprint_intake_open:  (l) => (l.source || '').toLowerCase() === 'blueprint_intake_open',
    blueprint_intake_trial: (l) => (l.source || '').toLowerCase() === 'blueprint_intake_trial',
  };

  const CHIPS = [
    { id: 'all',                    label: 'All' },
    { id: 'eng_24h',                label: 'Engaged · last 24h' },
    { id: 'eng_7d',                 label: 'Engaged · last 7d' },
    { id: 'cold_14d',               label: 'Cold · 14d+ no opens' },
    { id: 'quiz',                   label: '/start Quiz' },
    { id: 'calculator_v2',          label: 'Cost Calculator V2' },
    { id: 'visa',                   label: 'Visa Finder' },
    { id: 'move_score',             label: 'Move Score' },
    { id: 'calculator_legacy',      label: 'Calculator legacy v1' },
    { id: 'blueprint_intake_open',  label: 'Intake - no portal account' },
    { id: 'blueprint_intake_trial', label: 'Intake - Free Trial users' },
  ];

  // iter 179 (Fix 3) - bridge: when the Overview "New Leads" card clicks
  // an email, it writes the address to sessionStorage and switches this
  // tab active. We pick it up here and open the drawer automatically.
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem('mundalo_pending_lead_drawer');
      if (pending) {
        setDrawerEmail(pending);
        sessionStorage.removeItem('mundalo_pending_lead_drawer');
      }
    } catch { /* sessionStorage may throw in private mode */ }
  }, []);

  const nowMs = Date.now();
  const filterFn = FILTERS[activeFilter] || FILTERS.all;
  const filteredLeads = leads.filter((l) => filterFn(l, nowMs));

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    const aKey = sortMode === 'engagement'
      ? (a.last_engagement?.at || a.created_at || '')
      : (a.created_at || '');
    const bKey = sortMode === 'engagement'
      ? (b.last_engagement?.at || b.created_at || '')
      : (b.created_at || '');
    if (aKey === bKey) return 0;
    return aKey < bKey ? 1 : -1;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#5A6B73]">
          {sortedLeads.length} on this page · {meta.total} total leads
          {' · '}sorted by {sortMode === 'engagement' ? 'warmest first' : 'newest captured'}
        </p>
        <LostLeadRecoveryButton />
      </div>

      {/* iter 173 - filter chip row. Single-select. */}
      <div className="flex flex-wrap gap-2" data-testid="lead-filter-chips">
        {CHIPS.map((c) => {
          const isActive = activeFilter === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setActiveFilter(c.id)}
              className={
                'px-3 py-1.5 text-xs font-semibold rounded-full transition border '
                + (isActive
                  ? 'bg-[#1A5C6E] text-[#F9F6EF] border-[#1A5C6E]'
                  : 'bg-white text-[#1A5C6E] border-[#EFE6CF] hover:border-[#2CA8A0] hover:text-[#0A1F2E]')
              }
              data-testid={`lead-filter-chip-${c.id}${isActive ? '-active' : ''}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {sortedLeads.length > 0 ? (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F9F6EF]">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Source</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Destination</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Message</th>
                <th
                  className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase cursor-pointer select-none hover:text-[#1A5C6E]"
                  onClick={() => setSortMode((m) => m === 'engagement' ? 'captured' : 'engagement')}
                  data-testid="lead-engagement-sort-header"
                  title="Click to toggle: warmest first ↔ newest captured first"
                >
                  Last engagement {sortMode === 'engagement' ? '↓' : ''}
                </th>
                <th
                  className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase cursor-pointer select-none hover:text-[#1A5C6E]"
                  onClick={() => setSortMode((m) => m === 'captured' ? 'engagement' : 'captured')}
                  data-testid="lead-captured-sort-header"
                >
                  Captured {sortMode === 'captured' ? '↓' : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedLeads.map((lead, idx) => {
                const src = (lead.source || 'unknown').toLowerCase();
                const styleCls = SOURCE_BADGE_STYLES[src] || SOURCE_BADGE_STYLES.unknown;
                const label = SOURCE_LABELS[src] || src;
                return (
                  <tr
                    key={`${lead.email}-${idx}`}
                    className="border-t border-[#F4ECD8] hover:bg-[#F9F6EF] cursor-pointer transition"
                    onClick={() => lead.email && setDrawerEmail(lead.email)}
                    data-testid={`lead-row-${idx}`}
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-[#0A1F2E]">{lead.name}</p>
                      <p className="text-xs text-[#5A6B73]">{lead.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded font-medium ${styleCls}`}
                        data-testid={`lead-source-badge-${idx}`}
                      >
                        {label}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`lead-destination-${idx}`}>
                      {lead.destination_city
                        ? <span className="text-sm text-[#0A1F2E]">{lead.destination_city}</span>
                        : <span className="text-xs text-[#5A6B73]/60">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 bg-[#F4ECD8] text-[#1A5C6E] rounded">{lead.inquiry_type || 'General'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-[#1A5C6E] truncate max-w-xs">{lead.message || '-'}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <LeadEngagementPill engagement={lead.last_engagement} createdAt={lead.created_at} testIdSuffix={idx} />
                    </td>
                    <td className="px-4 py-3 text-sm text-[#5A6B73] whitespace-nowrap">
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-12 text-center" data-testid="lead-empty-state">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-[#E0D6B8]" />
          <p className="text-[#5A6B73]">
            {activeFilter === 'all' ? 'No leads yet' : 'No leads match this filter'}
          </p>
        </div>
      )}
      {drawerEmail && (
        <LeadDrawer
          email={drawerEmail}
          onClose={() => setDrawerEmail(null)}
        />
      )}
      {/* iter 175 - server-side pagination footer */}
      <PaginationFooter
        page={page}
        totalPages={meta.total_pages}
        total={meta.total}
        perPage={meta.per_page}
        onChange={setPage}
        testIdPrefix="leads"
      />
    </div>
  );
};

// ============== PERSONALIZED CAMPAIGN MODAL (iter 134) ==============
//
// Send personalized emails to a filtered audience. Filter by tier, signup
// date range, and destination city. Compose subject + body with the
// {{first_name}} token. Preview as one recipient. Send a test to
// francisco@mundalo.com. Confirm-then-send with a hard 500 recipient cap.
//
const TIER_OPTIONS = [
  { value: 'free_trial', label: 'Free trial' },
  { value: 'expired_trial', label: 'Expired trial' },
  { value: 'blueprint_only', label: 'Blueprint only' },
  { value: 'essentials', label: 'Essentials' },
  { value: 'guided', label: 'Guided' },
  { value: 'community', label: 'Community' },
  { value: 'connected', label: 'Connected' },
  { value: 'admin', label: 'Admin' },
];

const PersonalizedCampaignModal = ({ onClose, onSent }) => {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('Hi {{first_name}},\n\n');
  const [tiers, setTiers] = useState([]);
  const [signupAfter, setSignupAfter] = useState('');
  const [signupBefore, setSignupBefore] = useState('');
  const [destinationCity, setDestinationCity] = useState('');

  // iter 182.c - Audience source. "portal" targets tier'd users; "leads"
  // targets the unified tool leads (quiz / calculator / visa / score) who
  // don't have portal accounts yet. Merge tags + segment inspector work
  // identically for both sources.
  const [audienceSource, setAudienceSource] = useState('portal'); // 'portal' | 'leads'
  const [leadSources, setLeadSources] = useState([]);             // subset of ['quiz','calculator','visa','score']

  const toggleLeadSource = (v) => {
    setLeadSources((prev) => (prev.includes(v) ? prev.filter((s) => s !== v) : [...prev, v]));
    setAudience(null);
  };


  const [audience, setAudience] = useState(null); // {count, sample, over_limit, limit}
  const [previewing, setPreviewing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // iter 182 - Merge-tag registry pulled from the backend so the composer
  // stays authoritative even if new tokens are added server-side later.
  const [mergeTags, setMergeTags] = useState([]);
  const [sampleCtx, setSampleCtx] = useState({
    first_name: 'Francisco', last_name: 'Ochoa',
    email: 'francisco@mundalo.com',
    destination_city: 'Mexico City', visa_path: 'Temporary Resident',
    from_city: 'Dallas', from_country: 'United States',
    current_city: 'Dallas', move_timeline: '0-3 months', persona: 'Founder',
  });
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const [activeField, setActiveField] = useState('body'); // "subject" | "body"

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  // iter 182 - Load supported merge tags once on mount so the composer's
  // chip row + preview substitution use the same registry as the backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/admin/crm/campaigns/merge-tags`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        if (Array.isArray(data?.tags)) setMergeTags(data.tags);
        if (data?.sample_context) setSampleCtx((prev) => ({ ...prev, ...data.sample_context }));
      } catch (_e) { /* fallback to hardcoded defaults */ }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  // Insert a token at the caret of the currently-focused input/textarea.
  // Falls back to appending when there is no live caret.
  const insertMergeTag = (token) => {
    const useBody = activeField === 'body';
    const el = useBody ? bodyRef.current : subjectRef.current;
    const setter = useBody ? setBody : setSubject;
    const current = useBody ? body : subject;
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd ?? start;
      const next = current.slice(0, start) + token + current.slice(end);
      setter(next);
      requestAnimationFrame(() => {
        try {
          el.focus();
          const caret = start + token.length;
          el.setSelectionRange(caret, caret);
        } catch (_e) { /* older browsers */ }
      });
    } else {
      setter(current + token);
    }
  };


  const toggleTier = (v) => {
    setTiers((prev) => (prev.includes(v) ? prev.filter((t) => t !== v) : [...prev, v]));
    setAudience(null); // invalidate preview when filters change
  };

  const refreshAudience = async () => {
    setPreviewing(true);
    try {
      const r = await fetch(`${apiBase}/api/admin/crm/campaigns/audience-preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // iter 182.b + 182.c - Send the current subject + body so the
        // backend returns per-sample rendered previews (segment inspector).
        // `source` picks portal vs leads; `lead_sources` narrows the leads
        // audience to specific tool origins.
        body: JSON.stringify({
          filters: {
            source: audienceSource,
            lead_sources: audienceSource === 'leads' ? leadSources : [],
            tiers: audienceSource === 'portal' ? tiers : [],
            signup_after: signupAfter || null,
            signup_before: signupBefore || null,
            destination_city: destinationCity.trim() || null,
          },
          subject: subject || null,
          body: body || null,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setAudience(data);
    } catch (e) {
      notifyError(`Audience preview failed: ${e.message}`);
    } finally {
      setPreviewing(false);
    }
  };

  // iter 182 - Multi-token in-app preview. Every registered merge tag is
  // substituted with the shared sample context so what Francisco sees in
  // the modal is exactly what a recipient with that context would receive.
  // iter 182.f - tokens inside a URL query (immediately following `=` or
  // `&`, with `://` or `href=` in the preceding ~300 chars) get URL-
  // encoded so `?email={{email}}` renders as `?email=francisco%40...`.
  const applyMergeTags = (text) => {
    if (!text) return text;
    const needsUrlEncoding = (fullText, matchStart) => {
      const windowStart = Math.max(0, matchStart - 300);
      const window = fullText.slice(windowStart, matchStart);
      if (!window || (window[window.length - 1] !== '=' && window[window.length - 1] !== '&')) return false;
      return window.includes('://') || window.toLowerCase().includes('href=');
    };
    let out = text;
    Object.entries(sampleCtx).forEach(([key, val]) => {
      const pat = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
      const raw = String(val ?? '');
      const parts = [];
      let lastEnd = 0;
      let m;
      while ((m = pat.exec(out)) !== null) {
        parts.push(out.slice(lastEnd, m.index));
        parts.push(needsUrlEncoding(out, m.index) ? encodeURIComponent(raw) : raw);
        lastEnd = m.index + m[0].length;
      }
      parts.push(out.slice(lastEnd));
      out = parts.join('');
    });
    return out;
  };
  const renderedSubject = applyMergeTags(subject);
  const renderedBody = applyMergeTags(body)
    .split('\n')
    .map((line) => (line.trim() ? `<p>${line}</p>` : '<p>&nbsp;</p>'))
    .join('');

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) {
      notifyError('Add a subject and body before sending a test.');
      return;
    }
    setTesting(true);
    try {
      const r = await fetch(`${apiBase}/api/admin/crm/campaigns/send-test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject,
          body: renderedBody, // wrap plain-text → <p> here so the test mirrors the real send
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      notifySuccess('Test email sent to francisco@mundalo.com.');
    } catch (e) {
      notifyError(`Test send failed: ${e.message}`);
    } finally {
      setTesting(false);
    }
  };

  const sendCampaign = async () => {
    setSending(true);
    try {
      const r = await fetch(`${apiBase}/api/admin/crm/campaigns/send-personalized`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name || `Campaign ${new Date().toISOString().slice(0, 10)}`,
          subject,
          body: body
            .split('\n')
            .map((line) => (line.trim() ? `<p>${line}</p>` : '<p>&nbsp;</p>'))
            .join(''),
          filters: {
            source: audienceSource,
            lead_sources: audienceSource === 'leads' ? leadSources : [],
            tiers: audienceSource === 'portal' ? tiers : [],
            signup_after: signupAfter || null,
            signup_before: signupBefore || null,
            destination_city: destinationCity.trim() || null,
          },
          confirm: true,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      notifySuccess(`Sent ${data.sent}/${data.recipient_count}. Failed: ${data.failed}.`);
      onSent?.();
      onClose();
    } catch (e) {
      notifyError(`Campaign send failed: ${e.message}`);
    } finally {
      setSending(false);
      setShowConfirm(false);
    }
  };

  const canPreviewSend = audience && audience.count > 0 && !audience.over_limit && subject.trim() && body.trim();

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="personalized-campaign-modal">
      <div className="bg-[#F9F6EF] border border-[#E0D6B8] w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded">
        <div className="sticky top-0 bg-[#F9F6EF] border-b border-[#E0D6B8] px-5 py-4 flex items-center justify-between z-10">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">
              Send personalized email
            </p>
            <h3 className="font-display italic text-2xl text-[#0A1F2E]">New personalized campaign</h3>
          </div>
          <button onClick={onClose} className="text-[#5A6B73] hover:text-[#0A1F2E]" data-testid="close-personalized-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* STEP 1 - AUDIENCE */}
          <section>
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-2">Step 1 · Audience</p>

            {/* iter 182.c - Audience source. Portal = tier'd users;
                Leads = unified quiz/calculator/visa/score leads (warm
                pre-signup outreach). Test / internal / ambassador
                accounts are ALWAYS excluded from both. */}
            <label className="block text-xs font-medium text-[#0A1F2E] mb-1">Audience source</label>
            <div className="flex gap-2 mb-4" data-testid="audience-source-toggle">
              {[
                { value: 'portal', label: 'Portal users (by tier)' },
                { value: 'leads',  label: 'Leads (by source)' },
              ].map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { setAudienceSource(o.value); setAudience(null); }}
                  data-testid={`audience-source-${o.value}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    audienceSource === o.value
                      ? 'bg-[#0A1F2E] text-white border-[#0A1F2E]'
                      : 'bg-white text-[#0A1F2E] border-[#E0D6B8] hover:border-[#0A1F2E]'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[#5A6B73] italic mb-4" data-testid="audience-exclusion-note">
              Test accounts (@mundalotest.com, @mundalo-qa.com), internal admins, and ambassadors are always excluded.
            </p>

            {audienceSource === 'portal' && (
              <>
                <label className="block text-xs font-medium text-[#0A1F2E] mb-1">Tiers (leave empty for all)</label>
                <div className="flex flex-wrap gap-2 mb-4" data-testid="tier-chips">
                  {TIER_OPTIONS.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => toggleTier(t.value)}
                      data-testid={`tier-chip-${t.value}`}
                      className={`text-xs px-3 py-1 rounded-full border transition ${
                        tiers.includes(t.value)
                          ? 'bg-[#2CA8A0] text-[#F9F6EF] border-[#2CA8A0]'
                          : 'bg-white text-[#0A1F2E] border-[#E0D6B8] hover:border-[#2CA8A0]'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {audienceSource === 'leads' && (
              <>
                <label className="block text-xs font-medium text-[#0A1F2E] mb-1">Lead source (leave empty for tool leads only)</label>
                <p className="text-[10px] text-[#5A6B73] mb-2">
                  iter 182.e - Option A reality-mirrored taxonomy. Each chip
                  hits the exact tool collection that captured the lead so
                  copy stays truthful. Blueprint intake chips exclude paid
                  clients automatically.
                </p>
                <div className="flex flex-wrap gap-2 mb-4" data-testid="lead-source-chips">
                  {[
                    { value: 'quiz',                   label: '/start Quiz' },
                    { value: 'calculator_v2',          label: 'Cost Calculator V2' },
                    { value: 'visa',                   label: 'Visa Finder' },
                    { value: 'score',                  label: 'Move Score' },
                    { value: 'calculator_legacy',      label: 'Calculator legacy v1' },
                    { value: 'blueprint_intake_open',  label: 'Intake - no portal account' },
                    { value: 'blueprint_intake_trial', label: 'Intake - Free Trial users' },
                  ].map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => toggleLeadSource(o.value)}
                      data-testid={`lead-source-chip-${o.value}`}
                      className={`text-xs px-3 py-1 rounded-full border transition ${
                        leadSources.includes(o.value)
                          ? 'bg-[#2CA8A0] text-[#F9F6EF] border-[#2CA8A0]'
                          : 'bg-white text-[#0A1F2E] border-[#E0D6B8] hover:border-[#2CA8A0]'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-[#0A1F2E] mb-1">Signup after</label>
                <input
                  type="date"
                  value={signupAfter}
                  onChange={(e) => { setSignupAfter(e.target.value); setAudience(null); }}
                  className="w-full px-3 py-2 border border-[#E0D6B8] bg-white text-[#0A1F2E] text-sm rounded"
                  data-testid="signup-after"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#0A1F2E] mb-1">Signup before</label>
                <input
                  type="date"
                  value={signupBefore}
                  onChange={(e) => { setSignupBefore(e.target.value); setAudience(null); }}
                  className="w-full px-3 py-2 border border-[#E0D6B8] bg-white text-[#0A1F2E] text-sm rounded"
                  data-testid="signup-before"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#0A1F2E] mb-1">Destination city</label>
                <input
                  type="text"
                  value={destinationCity}
                  onChange={(e) => { setDestinationCity(e.target.value); setAudience(null); }}
                  placeholder="e.g. Mexico City"
                  className="w-full px-3 py-2 border border-[#E0D6B8] bg-white text-[#0A1F2E] text-sm rounded"
                  data-testid="destination-city"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={refreshAudience}
                disabled={previewing}
                data-testid="refresh-audience"
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#1A5C6E] text-[#1A5C6E] text-xs font-mono uppercase tracking-[0.12em] rounded hover:bg-[#1A5C6E] hover:text-white disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${previewing ? 'animate-spin' : ''}`} />
                {previewing ? 'Counting…' : 'Preview count'}
              </button>
              {audience && (
                <p className="text-sm text-[#0A1F2E]" data-testid="audience-count">
                  <span className="font-semibold">{audience.count}</span> recipients match
                  {audience.over_limit && (
                    <span className="ml-2 text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded uppercase tracking-wide">
                      Over {audience.limit} limit - narrow filters
                    </span>
                  )}
                </p>
              )}
            </div>

            {audience && audience.sample && audience.sample.length > 0 && (
              <div className="mt-3 bg-white border border-[#F4ECD8] rounded p-3 text-xs" data-testid="audience-sample">
                <p className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#5A6B73] mb-2">First {audience.sample.length} matches</p>
                <ul className="space-y-1 text-[#0A1F2E]">
                  {audience.sample.map((s, i) => (
                    <li key={i}>
                      <span className="font-medium">{s.first_name || '-'}</span>{' '}
                      <span className="text-[#5A6B73]">· {s.email}</span>
                      {s.subscription_tier && <span className="text-[#5A6B73]"> · {s.subscription_tier}</span>}
                      {s.destination_city && <span className="text-[#5A6B73]"> · {s.destination_city}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* iter 182.b - Segment inspector. When the composer has a
                subject and/or body, the audience-preview response now
                includes `rendered_samples` - each sample recipient paired
                with their personalized rendered subject + body + a list of
                tokens that fell back to a default value. Lets admins catch
                thin data (e.g. 'your city' for someone with no destination
                captured) BEFORE hitting Send. */}
            {audience && Array.isArray(audience.rendered_samples) && audience.rendered_samples.length > 0 && (
              <div
                className="mt-3 bg-white border border-[#F4ECD8] rounded p-3 text-xs"
                data-testid="segment-inspector"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#1A5C6E]">
                    Segment inspector · rendered for each sample
                  </p>
                  <span className="text-[9px] text-[#5A6B73] font-mono uppercase tracking-wider">
                    {audience.rendered_samples.length} of {audience.count ?? '?'}
                  </span>
                </div>
                <ul className="space-y-2">
                  {audience.rendered_samples.map((rs, i) => {
                    const fbCount = Array.isArray(rs.used_fallbacks) ? rs.used_fallbacks.length : 0;
                    return (
                      <li
                        key={rs.email || i}
                        className="border border-[#EFE6CF] rounded p-2 bg-[#FDFBF3]"
                        data-testid={`segment-inspector-row-${i}`}
                      >
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="text-[10px] text-[#5A6B73] font-mono truncate pr-1">
                            {rs.email || '-'}
                          </span>
                          {fbCount > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-[#7A4B00] bg-[#FDF6E6] border border-[#E8A830]/40 rounded px-1.5 py-0.5"
                              title={`Missing: ${rs.used_fallbacks.join(', ')}`}
                              data-testid={`segment-inspector-fallback-${i}`}
                            >
                              {fbCount} fallback{fbCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                        {rs.rendered_subject && (
                          <p
                            className="text-[#0A1F2E] font-semibold text-[12px] leading-snug"
                            data-testid={`segment-inspector-subject-${i}`}
                          >
                            {rs.rendered_subject}
                          </p>
                        )}
                        {rs.rendered_body && (
                          <p
                            className="text-[#5A6B73] text-[11px] leading-snug mt-1 line-clamp-3 whitespace-pre-wrap"
                            data-testid={`segment-inspector-body-${i}`}
                          >
                            {String(rs.rendered_body).replace(/<[^>]+>/g, ' ').slice(0, 220)}
                            {String(rs.rendered_body).length > 220 ? '…' : ''}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-2 text-[9px] text-[#5A6B73] italic">
                  Fallback pill flags any merge tag that would resolve to its default (e.g. destination_city → &quot;your city&quot;) for that recipient.
                </p>
              </div>
            )}
          </section>

          {/* STEP 2 - COMPOSE */}
          <section>
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-2">Step 2 · Compose</p>

            <div className="mb-3">
              <label className="block text-xs font-medium text-[#0A1F2E] mb-1">Campaign name (internal)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. June check-in to free-trial users"
                className="w-full px-3 py-2 border border-[#E0D6B8] bg-white text-[#0A1F2E] text-sm rounded"
                data-testid="campaign-name"
              />
            </div>

            <div className="mb-3">
              <label className="block text-xs font-medium text-[#0A1F2E] mb-1">
                Subject <span className="text-[#5A6B73] font-normal">· merge tags allowed</span>
              </label>
              <input
                ref={subjectRef}
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                onFocus={() => setActiveField('subject')}
                placeholder="e.g. {{first_name}}, your Mexico checklist is live"
                className="w-full px-3 py-2 border border-[#E0D6B8] bg-white text-[#0A1F2E] text-sm rounded"
                data-testid="campaign-subject"
              />
            </div>

            {/* iter 182 - Click-to-insert merge tag chips. Inserts at the
                current caret of whichever field (subject / body) was last
                focused. The label shows the fallback so admins understand
                what recipients missing that field will see. */}
            <div className="mb-3" data-testid="merge-tag-chips">
              <p className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#5A6B73] mb-1.5">
                Merge tags · click to insert
              </p>
              <div className="flex flex-wrap gap-1.5">
                {mergeTags.map((t) => (
                  <button
                    key={t.token}
                    type="button"
                    onClick={() => insertMergeTag(t.token)}
                    title={t.fallback ? `Fallback: "${t.fallback}"` : 'Fallback: (empty)'}
                    className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#1A5C6E]/40 bg-white text-[#1A5C6E] text-[11px] font-mono rounded hover:bg-[#1A5C6E]/10"
                    data-testid={`merge-tag-${t.token.replace(/[{}\s]/g, '')}`}
                  >
                    {t.token}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-medium text-[#0A1F2E] mb-1">
                Body <span className="text-[#5A6B73] font-normal">· line breaks become paragraphs · merge tags supported</span>
              </label>
              <textarea
                ref={bodyRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onFocus={() => setActiveField('body')}
                rows={10}
                className="w-full px-3 py-2 border border-[#E0D6B8] bg-white text-[#0A1F2E] text-sm rounded font-mono"
                data-testid="campaign-body"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowPreview((p) => !p)}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#1A5C6E] text-[#1A5C6E] text-xs font-mono uppercase tracking-[0.12em] rounded hover:bg-[#1A5C6E] hover:text-white"
                data-testid="toggle-preview"
              >
                <Eye className="w-3.5 h-3.5" />
                {showPreview ? 'Hide preview' : 'Preview as recipient'}
              </button>
              <button
                type="button"
                onClick={sendTest}
                disabled={testing || !subject.trim() || !body.trim()}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E8A830] text-[#0A1F2E] bg-[#E8A830]/20 text-xs font-mono uppercase tracking-[0.12em] rounded hover:bg-[#E8A830]/40 disabled:opacity-50"
                data-testid="send-test"
              >
                <Mail className="w-3.5 h-3.5" />
                {testing ? 'Sending…' : 'Send test to francisco@mundalo.com'}
              </button>
            </div>

            {showPreview && (
              <div className="mt-3 bg-white border border-[#F4ECD8] rounded p-4" data-testid="campaign-preview">
                <p className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#5A6B73] mb-1">From</p>
                <p className="text-sm text-[#0A1F2E] mb-3">Francisco - Mundalo &lt;hello@mundalo.com&gt;</p>
                <p className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#5A6B73] mb-1">Subject</p>
                <p className="text-sm text-[#0A1F2E] mb-3 font-semibold">{renderedSubject || '(no subject)'}</p>
                <p className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#5A6B73] mb-2">Body (as Francisco would see it)</p>
                <div
                  className="text-sm text-[#0A1F2E] leading-relaxed [&_p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: renderedBody || '<p>(empty)</p>' }}
                />
              </div>
            )}
          </section>

          {/* STEP 3 - SEND */}
          <section>
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-2">Step 3 · Send</p>
            <button
              type="button"
              onClick={() => setShowConfirm(true)}
              disabled={!canPreviewSend || sending}
              data-testid="open-confirm-send"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A1F2E] text-[#F9F6EF] font-mono uppercase text-xs tracking-[0.14em] rounded hover:bg-[#1A5C6E] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              Send to {audience?.count ?? '-'} recipients
            </button>
            {!audience && (
              <p className="text-xs text-[#5A6B73] mt-2">Click "Preview count" first to load the audience.</p>
            )}
            {audience && audience.count === 0 && (
              <p className="text-xs text-red-700 mt-2">No recipients match your filters.</p>
            )}
          </section>
        </div>
      </div>

      {/* Confirmation overlay */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" data-testid="confirm-send-overlay">
          <div className="bg-[#F9F6EF] border border-[#E0D6B8] rounded max-w-md w-full p-6">
            <h4 className="font-display italic text-2xl text-[#0A1F2E] mb-3">Confirm send</h4>
            <p className="text-sm text-[#0A1F2E] mb-2">
              You're about to send <span className="font-semibold">{audience?.count}</span> personalized emails from <span className="font-mono">Francisco - Mundalo &lt;hello@mundalo.com&gt;</span>.
            </p>
            <p className="text-sm text-[#5A6B73] mb-5">This cannot be undone.</p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={sending}
                className="px-4 py-2 border border-[#E0D6B8] text-[#0A1F2E] text-sm rounded disabled:opacity-50"
                data-testid="cancel-confirm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={sendCampaign}
                disabled={sending}
                className="px-4 py-2 bg-[#0A1F2E] text-[#F9F6EF] text-sm font-mono uppercase tracking-[0.14em] rounded hover:bg-[#1A5C6E] disabled:opacity-50"
                data-testid="confirm-send"
              >
                {sending ? 'Sending…' : `Send ${audience?.count} now`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ============== CAMPAIGNS TAB ==============
const CampaignsTab = ({ campaigns, clients, onRefresh, templates }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [showPersonalized, setShowPersonalized] = useState(false);
  const [formData, setFormData] = useState({ name: '', subject: '', html_content: '', recipient_type: 'all', recipient_tags: [], recipient_ids: [] });
  const [sending, setSending] = useState(false);
  const [allTags, setAllTags] = useState([]);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/tags`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAllTags(data.tags);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleSend = async () => {
    if (!formData.name || !formData.subject || !formData.html_content) {
      notifyError('Fill all required fields.');
      return;
    }
    setSending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        const result = await response.json();
        notifySuccess(`Campaign sent! ${result.sent_count} emails delivered, ${result.failed_count} failed.`);
        setShowCreate(false);
        setFormData({ name: '', subject: '', html_content: '', recipient_type: 'all', recipient_tags: [], recipient_ids: [] });
        onRefresh();
      } else {
        const err = await response.json().catch(() => ({}));
        notifyError(err, 'Failed to send campaign.');
      }
    } catch (error) {
      notifyError('Failed to send campaign.');
    } finally {
      setSending(false);
    }
  };

  const loadTemplate = (template) => {
    setFormData({ ...formData, subject: template.subject, html_content: template.html_content });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#5A6B73]">{campaigns.length} campaigns sent</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPersonalized(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#0A1F2E] text-[#F9F6EF] text-sm font-semibold rounded hover:bg-[#1A5C6E]"
            data-testid="open-personalized-modal"
          >
            <Mail className="w-4 h-4" /> Send Personalized Email
          </button>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] text-sm font-semibold">
            <Send className="w-4 h-4" /> New Campaign
          </button>
        </div>
      </div>

      {campaigns.length > 0 ? (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F9F6EF]">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Campaign</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Sent</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase">Date</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.campaign_id} className="border-t border-[#F4ECD8]">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-[#0A1F2E]">{c.name}</p>
                    <p className="text-xs text-[#5A6B73]">{c.subject}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#2CA8A0]">{c.sent_count}/{c.recipient_count}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded ${c.status === 'completed' ? 'bg-green-900/30 text-green-400' : 'bg-yellow-900/30 text-yellow-400'}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#5A6B73]">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-12 text-center">
          <Send className="w-12 h-12 mx-auto mb-4 text-[#E0D6B8]" />
          <p className="text-[#5A6B73]">No campaigns yet</p>
          <p className="text-sm text-[#5A6B73] mt-1">Create your first email campaign</p>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#FFFFFF] border border-[#F4ECD8] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#FFFFFF] border-b border-[#F4ECD8] p-4 flex items-center justify-between">
              <h3 className="font-semibold text-[#0A1F2E]">Create Email Campaign</h3>
              <button onClick={() => setShowCreate(false)} className="text-[#5A6B73] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Campaign Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" placeholder="e.g., January Newsletter" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Recipients</label>
                <select value={formData.recipient_type} onChange={(e) => setFormData({...formData, recipient_type: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm">
                  <option value="all">All Clients ({clients.length})</option>
                  <option value="tagged">By Tag</option>
                  <option value="selected">Select Specific</option>
                </select>
              </div>
              {formData.recipient_type === 'tagged' && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Select Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {allTags.map(tag => (
                      <button key={tag} onClick={() => {
                        const tags = formData.recipient_tags.includes(tag)
                          ? formData.recipient_tags.filter(t => t !== tag)
                          : [...formData.recipient_tags, tag];
                        setFormData({...formData, recipient_tags: tags});
                      }} className={`text-xs px-3 py-1 rounded ${formData.recipient_tags.includes(tag) ? 'bg-[#2CA8A0] text-[#F9F6EF]' : 'bg-[#F4ECD8] text-[#1A5C6E]'}`}>
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {templates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Load Template</label>
                  <select onChange={(e) => { if (e.target.value) loadTemplate(templates.find(t => t.template_id === e.target.value)); }} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm">
                    <option value="">Select a template...</option>
                    {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Subject *</label>
                <input type="text" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Email Content * <span className="text-[#5A6B73]">(Use {"{{name}}"} to personalize)</span></label>
                <textarea value={formData.html_content} onChange={(e) => setFormData({...formData, html_content: e.target.value})} rows="10" className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm font-mono" placeholder="<h1>Hello {{name}}!</h1>&#10;&#10;<p>Your email content here...</p>" />
              </div>
            </div>
            <div className="sticky bottom-0 bg-[#FFFFFF] border-t border-[#F4ECD8] p-4 flex justify-end gap-3">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-[#E0D6B8] text-[#0A1F2E] text-sm">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] font-semibold text-sm disabled:opacity-50 flex items-center gap-2">
                {sending ? 'Sending...' : <><Send className="w-4 h-4" /> Send Campaign</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPersonalized && (
        <PersonalizedCampaignModal
          onClose={() => setShowPersonalized(false)}
          onSent={onRefresh}
        />
      )}
    </div>
  );
};

// ============== TEMPLATES TAB ==============
const TemplatesTab = ({ templates, onRefresh }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState({ name: '', subject: '', html_content: '', category: 'general' });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!formData.name || !formData.subject || !formData.html_content) {
      notifyError('Fill all fields.');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowCreate(false);
        setFormData({ name: '', subject: '', html_content: '', category: 'general' });
        onRefresh();
      }
    } catch (error) {
      notifyError('Failed to create template.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      onRefresh();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#5A6B73]">{templates.length} email templates</p>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] text-sm font-semibold">
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>

      {templates.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {templates.map((t) => (
            <div key={t.template_id} className="bg-[#FFFFFF] border border-[#F4ECD8] p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-[#0A1F2E]">{t.name}</h4>
                  <p className="text-sm text-[#5A6B73]">{t.subject}</p>
                  <span className="text-xs px-2 py-0.5 bg-[#F4ECD8] text-[#1A5C6E] rounded mt-2 inline-block">{t.category}</span>
                </div>
                <button onClick={() => handleDelete(t.template_id)} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-12 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-[#E0D6B8]" />
          <p className="text-[#5A6B73]">No templates yet</p>
          <p className="text-sm text-[#5A6B73] mt-1">Create reusable email templates</p>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#FFFFFF] border border-[#F4ECD8] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="border-b border-[#F4ECD8] p-4 flex items-center justify-between">
              <h3 className="font-semibold text-[#0A1F2E]">Create Email Template</h3>
              <button onClick={() => setShowCreate(false)} className="text-[#5A6B73] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Template Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Category</label>
                <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm">
                  <option value="general">General</option>
                  <option value="welcome">Welcome</option>
                  <option value="listing">Listing</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="follow-up">Follow Up</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Subject *</label>
                <input type="text" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0A1F2E]">Content *</label>
                <textarea value={formData.html_content} onChange={(e) => setFormData({...formData, html_content: e.target.value})} rows="8" className="w-full px-3 py-2 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E] text-sm font-mono" />
              </div>
            </div>
            <div className="border-t border-[#F4ECD8] p-4 flex justify-end gap-3">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-[#E0D6B8] text-[#0A1F2E] text-sm">Cancel</button>
              <button onClick={handleCreate} disabled={saving} className="px-4 py-2 bg-[#2CA8A0] text-[#F9F6EF] font-semibold text-sm disabled:opacity-50">
                {saving ? 'Saving...' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== ADVISORY INTAKES TAB ==============
const AdvisoryIntakesTab = ({ intakes, stats, onExport, onRefetch }) => {
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [excludeTest, setExcludeTest] = useState(false);

  const handleToggleTest = () => {
    const next = !excludeTest;
    setExcludeTest(next);
    if (onRefetch) onRefetch(next);
  };

  const tierColors = {
    digital_blueprint: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    guided_intensive: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    private_advisory: 'bg-[#2CA8A0]/10 text-[#2CA8A0] border-[#2CA8A0]/30',
    strategy_blueprint: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    execution_oversight: 'bg-red-500/10 text-red-400 border-red-500/30',
    enterprise: 'bg-white/10 text-white border-white/30'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit', timeZone: 'America/Chicago'});
  };

  return (
    <div className="space-y-4">
      {/* Header Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-[#5A6B73]">{stats?.total || 0} total intakes</p>
          <button
            onClick={handleToggleTest}
            className={`px-3 py-1 text-xs border transition ${
              excludeTest
                ? 'border-[#2CA8A0] text-[#2CA8A0] bg-[#2CA8A0]/10'
                : 'border-[#333] text-[#5A6B73] hover:text-white'
            }`}
            data-testid="toggle-test-filter"
          >
            {excludeTest ? 'Showing Real Only' : 'Hide Test Entries'}
          </button>
          {stats?.byTier && (
            <div className="flex gap-2">
              {Object.entries(stats.byTier).map(([tier, count]) => (
                <span key={tier} className={`px-2 py-1 text-xs border ${tierColors[tier] || 'bg-[#333] text-white'}`}>
                  {tier.replace(/_/g, ' ')}: {count}
                </span>
              ))}
            </div>
          )}
        </div>
        <button 
          onClick={onExport}
          className="flex items-center gap-2 px-4 py-2 border border-[#E0D6B8] text-[#0A1F2E] text-sm hover:bg-[#F4ECD8] transition"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Intakes List */}
      {intakes.length > 0 ? (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] divide-y divide-[#F4ECD8]">
          {intakes.map((intake) => (
            <div 
              key={intake.id} 
              className="p-4 hover:bg-[#FFFFFF] transition cursor-pointer"
              onClick={() => setSelectedIntake(selectedIntake?.id === intake.id ? null : intake)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="font-medium text-[#0A1F2E]">{intake.full_name}</p>
                    <p className="text-sm text-[#5A6B73]">{intake.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className={`text-xs px-2 py-1 border ${tierColors[intake.recommended_tier] || 'bg-[#333]'}`}>
                      {intake.recommended_tier_name}
                    </p>
                    <p className="text-xs text-[#5A6B73] mt-1">Score: {intake.complexity_score}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#5A6B73]">{formatDate(intake.created_at)}</p>
                    <p className={`text-xs mt-1 ${intake.converted ? 'text-green-400' : 'text-[#5A6B73]'}`}>
                      {intake.converted ? 'Converted' : 'Pending'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Expanded Details */}
              {selectedIntake?.id === intake.id && (
                <div className="mt-4 pt-4 border-t border-[#F4ECD8] grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-[#5A6B73]">Location</p>
                    <p className="text-[#0A1F2E]">{intake.current_city_country}</p>
                  </div>
                  <div>
                    <p className="text-[#5A6B73]">Primary Goal</p>
                    <p className="text-[#0A1F2E] capitalize">{intake.primary_goal?.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-[#5A6B73]">Timeline</p>
                    <p className="text-[#0A1F2E] capitalize">{intake.timeline?.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-[#5A6B73]">Support Level</p>
                    <p className="text-[#0A1F2E] capitalize">{intake.support_level?.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-[#5A6B73]">Budget</p>
                    <p className="text-[#0A1F2E]">{intake.budget_range?.replace(/_/g, ' to $').replace('under', 'Under $').replace('over', 'Over $')}</p>
                  </div>
                  <div>
                    <p className="text-[#5A6B73]">City Focus</p>
                    <p className="text-[#0A1F2E]">{intake.city_focus}</p>
                  </div>
                  <div>
                    <p className="text-[#5A6B73]">Citizenship</p>
                    <p className="text-[#0A1F2E]">{intake.citizenship}</p>
                  </div>
                  <div>
                    <p className="text-[#5A6B73]">Household</p>
                    <p className="text-[#0A1F2E] capitalize">{intake.household?.replace(/_/g, ' ')}</p>
                  </div>
                  {intake.biggest_concern && (
                    <div className="col-span-2 md:col-span-4">
                      <p className="text-[#5A6B73]">Biggest Concern</p>
                      <p className="text-[#0A1F2E]">{intake.biggest_concern}</p>
                    </div>
                  )}
                  {intake.utm_source && (
                    <div className="col-span-2 md:col-span-4">
                      <p className="text-[#5A6B73]">Source</p>
                      <p className="text-[#0A1F2E]">
                        {[intake.utm_source, intake.utm_medium, intake.utm_campaign].filter(Boolean).join(' / ')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-12 text-center">
          <ClipboardList className="w-12 h-12 mx-auto mb-4 text-[#E0D6B8]" />
          <p className="text-[#5A6B73]">No advisory intakes yet</p>
          <p className="text-sm text-[#5A6B73] mt-1">Intakes from /advisory-intake will appear here</p>
        </div>
      )}
    </div>
  );
};

// ============== TOOL LEADS TAB (iter 140) ==============
//
// Surfaces the three lead collections captured by the interactive SEO tools
// (Move Score, Cost Calculator, Visa Finder). Filter chips switch between
// collections; each section ships a "Download CSV" button hitting the
// authenticated admin export endpoint.
//
const TOOL_LEAD_TYPES = [
  { id: 'score', label: 'Move Score', tag: 'score_lead' },
  { id: 'calculator', label: 'Cost Calculator', tag: 'calculator_lead' },
  { id: 'visa', label: 'Visa Finder', tag: 'visa_lead' },
  { id: 'quiz', label: 'Start Quiz', tag: 'quiz_lead' },
  // iter 144w - new Timeleft-style cost calculator at /mexico-cost-of-living-calculator.
  { id: 'calculator_v2', label: 'Cost Calculator V2', tag: 'calculator_lead_v2' },
];

const _fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago'});
  } catch {
    return iso;
  }
};

const ToolLeadsTab = () => {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [activeType, setActiveType] = useState('score');
  const [leads, setLeads] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(
          `${apiBase}/api/tools/admin/tool-leads/${activeType}`,
          { credentials: 'include' }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) {
          setLeads(data.leads || []);
          setCount(data.count || 0);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load leads');
          setLeads([]);
          setCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeType, apiBase]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await fetch(
        `${apiBase}/api/tools/admin/tool-leads/${activeType}/export.csv`,
        { credentials: 'include' }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `tool_leads_${activeType}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      notifySuccess(`Exported ${count} ${activeType} leads`);
    } catch (e) {
      notifyError(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  const activeMeta = TOOL_LEAD_TYPES.find((t) => t.id === activeType);

  // Per-type column definitions. Email + tag + date are always shown.
  const renderRow = (lead, idx) => {
    const dateCell = (
      <td className="px-4 py-3 text-sm text-[#5A6B73]" data-testid={`tool-lead-${activeType}-date-${idx}`}>
        {_fmtDate(lead.created_at)}
      </td>
    );
    const emailCell = (
      <td className="px-4 py-3" data-testid={`tool-lead-${activeType}-email-${idx}`}>
        <p className="text-sm font-medium text-[#0A1F2E] break-all">{lead.email || '-'}</p>
      </td>
    );
    const tagCell = (
      <td className="px-4 py-3">
        <span className="text-xs px-2 py-1 bg-[#F4ECD8] text-[#1A5C6E] rounded font-mono">
          {lead.tag || activeMeta.tag}
        </span>
      </td>
    );

    if (activeType === 'score') {
      return (
        <tr key={lead.lead_id || idx} className="border-t border-[#F4ECD8] hover:bg-[#F9F6EF]">
          {emailCell}
          {tagCell}
          <td className="px-4 py-3 text-sm text-[#0A1F2E]">
            <span className="font-mono font-semibold">{lead.score ?? '-'}</span>
            <span className="text-[#5A6B73]">/100</span>
          </td>
          <td className="px-4 py-3 text-sm text-[#1A5C6E]">{lead.city || '-'}</td>
          {dateCell}
        </tr>
      );
    }
    if (activeType === 'calculator') {
      return (
        <tr key={lead.lead_id || idx} className="border-t border-[#F4ECD8] hover:bg-[#F9F6EF]">
          {emailCell}
          {tagCell}
          <td className="px-4 py-3 text-sm text-[#1A5C6E]">{lead.city || '-'}</td>
          <td className="px-4 py-3 text-sm text-[#0A1F2E] font-mono">
            {lead.monthly_total != null ? `$${Number(lead.monthly_total).toLocaleString('en-US', { timeZone: 'America/Chicago' })}/mo` : '-'}
          </td>
          {dateCell}
        </tr>
      );
    }
    if (activeType === 'quiz') {
      return (
        <tr key={lead.lead_id || idx} className="border-t border-[#F4ECD8] hover:bg-[#F9F6EF]">
          {emailCell}
          {tagCell}
          <td className="px-4 py-3 text-sm text-[#1A5C6E]">{lead.first_name || '-'}</td>
          <td className="px-4 py-3 text-sm text-[#1A5C6E]">{lead.city || '-'}</td>
          <td className="px-4 py-3 text-sm text-[#5A6B73] font-mono">{lead.utm_source || '-'}</td>
          {dateCell}
        </tr>
      );
    }
    // iter 144w - Cost Calculator V2 row.
    if (activeType === 'calculator_v2') {
      return (
        <tr key={lead.lead_id || idx} className="border-t border-[#F4ECD8] hover:bg-[#F9F6EF]">
          {emailCell}
          {tagCell}
          <td className="px-4 py-3 text-sm text-[#1A5C6E]">{lead.first_name || '-'}</td>
          <td className="px-4 py-3 text-sm text-[#1A5C6E] capitalize">{lead.persona || '-'}</td>
          <td className="px-4 py-3 text-sm text-[#0A1F2E]">{lead.age_range || '-'}</td>
          <td className="px-4 py-3 text-sm text-[#1A5C6E]">{lead.city || '-'}</td>
          <td className="px-4 py-3 text-sm text-[#5A6B73] font-mono">{lead.utm_source || '-'}</td>
          {dateCell}
        </tr>
      );
    }
    // visa
    return (
      <tr key={lead.lead_id || idx} className="border-t border-[#F4ECD8] hover:bg-[#F9F6EF]">
        {emailCell}
        {tagCell}
        <td className="px-4 py-3 text-sm text-[#1A5C6E]">{lead.primary_visa || '-'}</td>
        {dateCell}
      </tr>
    );
  };

  const headers = activeType === 'score'
    ? ['Email', 'Tag', 'Score', 'City', 'Captured']
    : activeType === 'calculator'
    ? ['Email', 'Tag', 'City', 'Monthly Budget', 'Captured']
    : activeType === 'quiz'
    ? ['Email', 'Tag', 'First Name', 'City', 'UTM Source', 'Captured']
    : activeType === 'calculator_v2'
    ? ['Email', 'Tag', 'First Name', 'Persona', 'Age', 'City', 'UTM Source', 'Captured']
    : ['Email', 'Tag', 'Primary Visa', 'Captured'];

  return (
    <div className="space-y-5" data-testid="tool-leads-tab">
      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        {TOOL_LEAD_TYPES.map((t) => {
          const active = t.id === activeType;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveType(t.id)}
              data-testid={`tool-leads-chip-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition border ${
                active
                  ? 'bg-[#0A1F2E] text-[#F9F6EF] border-[#0A1F2E]'
                  : 'bg-[#FFFFFF] text-[#1A5C6E] border-[#F4ECD8] hover:border-[#2CA8A0]'
              }`}
            >
              {t.label}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-3">
          <p className="text-sm text-[#5A6B73]" data-testid="tool-leads-count">
            {loading ? 'Loading…' : `${count} ${activeMeta.label.toLowerCase()} leads`}
          </p>
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting || count === 0}
            data-testid={`tool-leads-export-${activeType}`}
            className="inline-flex items-center gap-2 px-3 py-2 bg-[#2CA8A0] text-white text-xs font-semibold uppercase tracking-wider rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#1A5C6E]"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-[#FFFFFF] border border-red-200 p-4 text-sm text-red-700 rounded">
          {error}
        </div>
      )}

      {leads.length > 0 ? (
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F9F6EF]">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#5A6B73] uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody data-testid={`tool-leads-table-${activeType}`}>
              {leads.map(renderRow)}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && !error && (
          <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-12 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-[#E0D6B8]" />
            <p className="text-[#5A6B73]">No {activeMeta.label.toLowerCase()} leads yet</p>
            <p className="text-sm text-[#5A6B73] mt-1">
              Captures from the interactive tools will appear here.
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default AdminDashboard;
